import { Component } from 'preact';
import { getBinding as gameKeybindingGetBinding } from './utils/game/keybindings';
import { dismissTop } from './utils/escStack';
import { createGamepadFocusBridge } from './utils/input/gamepadFocusBridge';
import { controllerMode } from './utils/input/controllerMode';
import { autoReconnectHidGamepads } from './utils/input/hidGamepadBridge';
import { startSdlGamepadClient, stopSdlGamepadClient } from './utils/input/sdlGamepadClient';
import { isControllerEnabled } from './utils/input/controllerFeature';
import VirtualKeyboard from './components/ui/VirtualKeyboard';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import './app.css';
import { toast } from 'sonner';
import { k, t } from './localization/t';
import { overrideLanguage } from './localization/preferences';
import * as lfg from './utils/arena/lfgStore';
import LookingForMatchDialog from './components/LookingForMatchDialog';
import MatchFoundOverlay from './components/MatchFoundOverlay';
import { ringBell } from './utils/notificationsStore';
import { Toaster } from './components/ui/sonner';

import { deleteSavedDeckById, listSavedDecks, loadSavedDeckById, saveSavedDeck, saveDeckShowcase, renameSavedDeck, setDeckFavorite, setDeckTags, deleteDeckTag } from './utils/deckStorageApi';
import { shareDeck, unshareDeck } from './utils/arena/deckLibraryApi';
import { createSavedDeckCardIndex, restoreSavedDeckCards, normalizeSavedDeckText } from './utils/savedDeckRestore';
import { applyThemePreference, getStoredThemePreference } from './utils/themePreference';
import { validateUsername } from './utils/usernameValidation';
import { getLocalApiOrigin } from './utils/localApi';
import { isWeb, webClientVersion, isPhoneLayout } from './utils/platform';
import { getUpdateChannel } from './utils/updateChannel';
import MobileHub from './components/mobile/MobileHub';
import { getViewportWidth } from './utils/workspaceLayout';
import { loadSorceryCardsWithSource } from './utils/sorcery/cardsApi';
import { setSorceryCards } from './utils/sorcery/cardDb';
import { collectAllExpectedSlugs } from './utils/sorcery/normalizeCards';
import GameBoard from './components/GameBoard';
import ArenaHub from './components/ArenaHub';
import { hasCap, hasStaffAccess, onRolePreviewChange } from './utils/staffAccess';
import { start as startGlobalChat, subscribe as subscribeGlobalChat, postReplayAndConfirm as postReplayToTavern } from './utils/globalChatStore';
import ArenaStarterPicker from './components/ArenaStarterPicker';
import ArenaFirstPlaymatPicker from './components/ArenaFirstPlaymatPicker';
import ProgressionScreen from './components/ProgressionScreen';
import ArenaStore from './components/ArenaStore';
import AuctionHouse from './components/AuctionHouse';
import ArenaPackOpening from './components/ArenaPackOpening';
import AchievementClaimOverlay from './components/AchievementClaimOverlay';
import AchievementUnlockToast from './components/AchievementUnlockToast';
import { loadArenaProfile, saveArenaProfile, grantCards, detectAchievements, claimAchievement, claimProgressionDeck } from './utils/arena/profileApi';
import { seedSkillState, getSkillState, getMaxQuestSlots } from './utils/arena/skills';
import { purchasePacks, openPendingPack, resolvePendingPacks, resolvePack } from './utils/arena/packsApi';
import { ALPHA_PRECONS } from './utils/arena/alphaPrecons';
import PreconCollectedModal from './components/PreconCollectedModal';
import { playMusic, stopMusic, isMusicSuppressed, getCurrentTrack } from './utils/arena/musicManager';
import { getEffectiveMusicVolume, notificationSoundEnabled } from './utils/arena/soundSettings';
import { getPlaymatUrl, prewarmPlaymatPreviews } from './components/PlaymatPicker';
import { playUI, UI, preloadUISounds } from './utils/arena/uiSounds';
import { createDefaultProfile, CURRENCY, isArenaDebugMode, levelFromXp } from './utils/arena/profileDefaults';
import { checkAchievements, getAchievement } from './utils/arena/achievements';
import { pushAchievementToast } from './utils/arena/achievementToasts';
import {
  resolveStarterDeck,
  PROGRESSION_DECKS,
  PROGRESSION_DECK_NAMES,
} from './utils/arena/starterDecks';
import { findStalePreconDecks } from './utils/arena/preconUpgrade';
import { createDefaultSeasonProgress } from './utils/arena/seasonPass';
import { loadCurrentSeason, claimSeasonTier as apiClaimSeasonTier } from './utils/arena/seasonApi';
import ArenaMatchmaking from './components/ArenaMatchmaking';
import ArenaDeckSelect from './components/ArenaDeckSelect';
import ArenaUsernamePrompt from './components/ArenaUsernamePrompt';
import ArenaDiscordPrompt from './components/ArenaDiscordPrompt';
import { wasDiscordPromptShown, markDiscordPromptShown } from './utils/discordPromptState';
import GameMenu from './components/GameMenu';
import LoginScreen from './components/LoginScreen';
import { clearQueueState, leaveQueue } from './utils/arena/matchmakingApi';
import { api, on, send as wsSend, onSessionExpired, onConnectionState } from './utils/serverClient';
import { drainPendingReports } from './utils/arena/pendingReports';
import { resolveDraftPool } from './utils/arena/draftPool';
import { drainPendingLogs } from './utils/game/replay/logUpload';
import { reportMatchOutcome } from './utils/arena/matchReporting';
import { resolveStatsCacheWrite, resolveCompletedInstanceResult, hasArbitrationReason } from './utils/arena/matchStatsCache';
import { canPlayDeck, buildCardCatalog, applyAvatarZoneRouting } from './utils/sorcery/deckRules';
import { computeFeaturedCards } from './utils/sorcery/deckFeaturedCards';
import { buildOwnedMap } from './utils/arena/collectionUtils';
import { ART_GROUP_BASE } from './utils/sorcery/artGroups';
import { getStoredToken, validateToken, clearStoredToken, getMyInviteCode, isNetworkError } from './utils/authApi';
import { checkRenderParity, getReportedParityViolations } from './components/gameBoard/controllers/renderParity';
// FriendsSidebar retired. Friends are accessed via SocialOverlay
import FriendProfileOverlay from './components/FriendProfileOverlay';
import { applyMatchMetaPatch } from './utils/arena/matchHistory';
import SpectatorBanner from './components/SpectatorBanner';
import RuneSpinner from './components/RuneSpinner';
import LoadingIndicator from './components/LoadingIndicator';
import FirstRunDownload from './components/FirstRunDownload';
import TokenUpdateGate from './components/TokenUpdateGate';
import MatchAssetGate from './components/MatchAssetGate';
import { isAuthMatchEnabled } from './utils/game/net/authMatchFlag';
import { getCosmeticsCached, setCurrentUserId as setCosmeticsUserId, awaitProtectedRegistry } from './utils/arena/cosmeticsCache';
import { DICE_SKINS } from './utils/game/dice3d/diceSkins.data';
import { bakeShelfPreview } from './utils/game/dice3d/diceSetRig';
import { setDicePalette } from './utils/game/dice3d/dieAssets';
import TradeWindow from './components/TradeWindow';
import { FriendRequestToast } from './components/FriendRequestToast';
import { HeraldAvatarSeal } from './components/ui/HeraldSeal';
import { startPresence, stopPresence, updateActivity, refreshFriendList } from './utils/presenceManager';
import {
  startNotifications, stopNotifications, registerNotificationOpenHandler, onNewNotification,
  subscribe as subscribeNotifications, getState as getNotificationsState,
} from './utils/notificationsStore';
import { describeNotification, notificationHasTarget } from './utils/notificationCopy';
import { fetchLibraryDeck } from './utils/arena/deckLibraryApi';
import { startJoinPolling, stopJoinPolling } from './utils/discordPresence';
import * as friendsApi from './utils/friendsApi';
import { sendChatMessage } from './utils/arena/chatApi';
import { createUpdateManager } from './utils/updateManager';
import { onVersionCheck, startVersionPolling } from './utils/versionCheck';
import UpdateModal from './components/UpdateModal';
import MaintenanceBanner from './components/MaintenanceBanner';
import ConnectionLostOverlay from './components/ConnectionLostOverlay';
import SessionSupersededOverlay from './components/SessionSupersededOverlay';
import { initMaintenanceState } from './utils/maintenanceState';
import SettingsScreen from './components/SettingsScreen';
import SkillTreeScreen from './components/SkillTreeScreen';
import HallOfFameScreen from './components/HallOfFameScreen';
import SeasonRankingScreen from './components/SeasonRankingScreen';
import SeasonChampionCelebrationOverlay from './components/SeasonChampionCelebrationOverlay';
import DeckGallery from './components/DeckGallery';
import DeckEditor from './components/DeckEditor';
import Mailbox from './components/Mailbox';
import ArcaneTrials from './components/ArcaneTrials';
import DraftBrowser from './components/DraftBrowser';
import PlayScreen from './components/play/PlayScreen';
import SessionBrowser from './components/SessionBrowser';
import ModePickerScreen from './components/play/ModePickerScreen';
import ModeSetupDialog from './components/play/ModeSetupDialog';
import CreateDraftModal from './components/CreateDraftModal';
import ReadyCheckModal from './components/play/ReadyCheckModal';
import ConfirmModal from './components/ConfirmModal';
import ArenaMatchResult from './components/ArenaMatchResult';
import {
  createInstance as apiCreateInstance,
  joinInstance as apiJoinInstance,
  listInstances as apiListInstances,
  getInstance as apiGetInstance,
  markReady as apiMarkReady,
  subscribeInstance as apiSubscribeInstance,
  abandonInstance as apiAbandonInstance,
  pauseInstance as apiPauseInstance,
  completeInstance as apiCompleteInstance,
  requestResume as apiRequestResume,
  claimWin as apiClaimWin,
  inviteToInstance as apiInviteToInstance,
  setInstanceDeck as apiSetInstanceDeck,
  leaveInstance as apiLeaveInstance,
  devQuickMatch as apiDevQuickMatch,
  onDevQuickMatch,
} from './utils/arena/instancesApi';
import { deriveSessionView } from './utils/arena/sessionView';
import { releaseStuckMatch } from './utils/arena/stuckMatchRelease';
import { resolveDiscardLeave } from './utils/arena/discardLeave';
import { cleanupOnMatchEntry } from './utils/arena/matchEntryCleanup';
import { migrateLocalInstanceState } from './utils/arena/localInstanceStateMigration';
import { migrateSoloSavesToInstances } from './utils/game/migrateSoloSaves';
import { registerInstanceQueueHandlers } from './utils/arena/instanceQueueBus';
import { registerConfirmHandler } from './utils/confirmBus';
import FriendInvitePicker from './components/play/FriendInvitePicker';
import SealedPackOpener from './components/SealedPackOpener';
import { createDraftEvent, inviteToDraft, subscribeToDraftEvents } from './utils/arena/draftApi';
import DraftPicker from './components/DraftPicker';
import DraftDeckBuilder from './components/DraftDeckBuilder';
import DraftTournament from './components/DraftTournament';
import DraftResults from './components/DraftResults';
import { replayMetaFromView } from './components/ReplayShareCard';
import { replaySeatIdentities } from './utils/arena/replaySeats';
import ChatPanel from './components/ChatPanel';
import MiniProfile from './components/MiniProfile';
import MomentsPanel from './components/MomentsPanel';
import * as momentsManager from './utils/moments/momentsManager';
import IntroSplash from './components/IntroSplash';
import ReplayOverlay from './components/replay/ReplayOverlay';
import {
  fetchReplay, fetchReplayComments, postReplayComment, deleteReplayComment,
  toggleReplayCommentLike, updateMatchMeta, createReplayLink, resolveReplayLink,
} from './utils/game/replay/replayApi';
import { ReplayDriver } from './utils/game/replay/ReplayDriver';

// Session-scope fields every match/spectate/replay mount must start clean:
// a leaked instance or draft id gives the mount a real save scope, and the
// spectated/replayed state gets stamped into a live save blob (see
// handleDraftSpectate's history on this failure).
const CLEARED_MATCH_CONTEXT = {
  currentDraftMatchId: null,
  currentInstanceId: null,
  currentInstanceIsHost: false,
  currentInstanceVisibility: null,
  currentInstanceMode: null,
  isResumeJoin: false,
};
import { isDevBuild } from './utils/devMode';
import FoilZoneEditor from './components/FoilZoneEditor';
import MatchResultGallery from './components/dev/MatchResultGallery';

// Legacy-poll notification types that carry their own cue (or none): match
// invites and matchmaking play the summons in their branches below, a match
// starting from an invite the player already heard is silent, and mail has
// its own chime. Everything else gets the soft ping.
// Instance mode keys reach the toasts below as raw enum values, so the
// user-facing name is looked up here rather than interpolated straight.
const MODE_LABELS = {
  match: k('Match'),
  sandbox: k('Sandbox'),
  draft: k('Draft'),
  sealed: k('Sealed'),
};
const modeLabel = (mode) => t(MODE_LABELS[mode] || mode);

const OWN_CUE_NOTIFICATION_TYPES = new Set(['match-invite', 'invite-accepted', 'matchmaking-matched', 'new-mail']);

// Feature toggle for the Escape pause menu. Off for now. Escape still
// dismisses modals/popovers/dropdowns via the central Esc-dismiss stack
// (src/utils/escStack.js); only the fallback pause menu is suppressed. Flip
// to true to bring the menu back.
const PAUSE_MENU_ENABLED = false;

// Key stored per tab session. A window reload replays the intro,
// but navigation/state changes within the same session don't.
// On web a reload is an everyday action (it's also how updates arrive),
// so the flag lives in localStorage there and the intro plays once ever.
const INTRO_SEEN_KEY = 'valkenhall.introSplashSeen';
const introSeenStore = () => (isWeb() ? localStorage : sessionStorage);
const hasSeenIntroThisSession = () => {
  try {
    return introSeenStore().getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const PAGE_TRANSITION_PROPS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25, ease: 'easeInOut' },
};

// Draft/sealed lobby rows are closed by their draft event ending, and the
// draft:cancelled push already tells the player. The "Match abandoned"
// modal (both players disconnected) is for constructed matches only.
function isDraftInstance(instance) {
  return instance?.mode === 'draft' || instance?.mode === 'sealed';
}

function isEditableTarget(target) {
  if (!target || typeof target !== 'object') {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  const tagName = target.tagName?.toLowerCase();
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
}

function resolvePreferredPrintingStatic(card) {
  if (!card) {
    return null;
  }

  if (card.printings?.length === 1) {
    return card.printings[0];
  }

  return card.printings?.[card.printings.length - 1] || card.printings?.[0] || null;
}

export default class App extends Component {
  constructor() {
    super();

    this.state = {
      introComplete: hasSeenIntroThisSession(),
      authChecking: true,
      authFadeOut: false,
      needsAssetDownload: false,
      // Populated alongside needsAssetDownload. The list of card slugs
      // missing from PERSISTENT_IMAGES_DIR that FirstRunDownload should
      // fetch. Empty when no gate is active.
      missingAssetSlugs: [],
      // Set to an array of { slug, version } when the server cosmetics
      // catalog declares token-art versions newer than what's on disk.
      // The TokenUpdateGate blocks gameplay until each one has been
      // refetched into the persistent cache.
      pendingTokenUpdates: null,
      isQuitting: false,
      loggedIn: false,
      isGameBoardOpen: false,
      // Opponent deck surfaced from GameBoard the moment it learns the
      // opposing card list (roomInfo on arena, deck:spawn over P2P on
      // sandbox multiplayer). MatchAssetGate watches this prop and
      // runs its phase-2 preload. Once that finishes, matchAssetsReady
      // flips true and GameBoard releases its queued spawn. Reset to
      // null when the user exits a match so a follow-up match doesn't
      // inherit stale phase-2 input.
      opponentDeckForPreload: null,
      // Mirrors the gate's phase-2 completion. Passed through to
      // GameBoard as `assetsReady`. Held false from match-mount until
      // the gate finishes the opponent download (or set immediately
      // for solo / spectator paths that skip phase 2).
      matchAssetsReady: false,
      arenaProfile: null,
      arenaLoading: true,
      arenaView: 'hub',
      // Whether the unified Progression screen is currently open.
      // Replaces the legacy levelUpQueue / pickerOpen / pickerMode
      // trio with a single boolean. The screen handles its own
      // multi-level focus walk-forward internally and reads server-
      // authoritative claim state on every open, so no client-side
      // queue is needed. Set true by:
      //   - post-match handler when XP crosses a level boundary
      //   - login replay when any unclaimed claim exists
      //   - L1 first-run flow (no starterDeck yet)
      //   - hub level-pill click (browse mode)
      progressionScreenOpen: false,
      // Whether the deck picker is currently on screen. Opens when
      // the Progression screen's deck cell is clicked; closes after
      // the player confirms a deck and handleProgressionDeckChosen
      // resolves. The Progression screen stays mounted behind it so
      // the focus state survives the picker round-trip.
      pickerOpen: false,
      // The latest progression snapshot pushed back to
      // ProgressionScreen after a successful deck claim. Bumping
      // this reference triggers the screen's componentDidUpdate
      // prop-diff which advances focus to the next unclaimed level.
      progressionRefreshTag: null,
      // When the user enters a game mode from the Play screen, their
      // natural "back" destination is the mode picker, not the main
      // hub. We stash 'play' here whenever a mode is launched from
      // PlayScreen and consume it from deck-select / matchmaking /
      // draft-browser back paths. Reset to 'hub' on any landing on
      // the hub itself so downstream navigation starts clean.
      arenaBackTarget: 'hub',
      arenaOpenedPack: null,
      arenaLastSetKey: null,
      arenaPendingPacks: [],
      arenaEditDeck: null,
      isArenaMatch: false,
      isRankedMatch: false,
      // Online matchmade Sandbox match. GameBoard stays fully online
      // but we suppress LP badges, reward UI, and rank progression.
      isSandboxMatch: false,
      // Set when the local-sandbox single-player mode is active
      // (launched from the Casual tile). GameBoard renders dual-hand
      // UI and every online entry-point is hidden. Distinct from
      // `isArenaMatch && connectionStatus === 'offline'`, which is
      // the "arena match that hasn't gone online yet" state.
      isCasualSandbox: false,
      arenaMatchmaking: false,
      arenaMatchmakingOpponent: null,
      // 'ranked' for the public ranked queue, 'sandbox' for the
      // ownership-bypassing / no-rewards queue, 'friend' for invite-
      // driven matches. Drives the copy on the matchmaking screen.
      arenaMatchmakingMode: null,
      // Set when the user clicks a play-screen tile and we advance
      // into the deck picker, so `confirmDeckAndQueue` knows which
      // queue to join. Null means ranked (legacy default).
      pendingQueueMode: null,
      // Deck format the user picked alongside pendingQueueMode (e.g.
      // 'constructed' or 'peasant'). Used to filter the deck picker
      // and to stamp the format on instance creation.
      pendingQueueFormat: null,
      // Starting HP for the active match (10 for Quick Match, 20
      // for Standard). Set when an instance is created or joined,
      // read by GameBoard to seed the life trackers. 20 is the
      // implicit default if no instance metadata carries the field.
      currentStartingHp: 20,
      // Session-wide automation mode of the current instance. Set on create
      // and on every join/launch, read by GameBoard to decide whether the
      // client resolves anything on the players' behalf. Defaults to on so
      // an instance predating the field behaves exactly as it always did.
      currentAutomations: true,
      arenaMatchId: null,
      arenaSelectedDeckId: null,
      sessionMode: null,
      sessionId: null,
      roomCode: null,
      replaySession: null,
      sorceryCards: null,
      sorceryCardsLoadSource: '',
      savedDecks: [],
      loading: true,
      loadingMessage: 'Loading...',
      loadingDetail: '',
      isSavedDecksLoading: true,
      isSavingDeck: false,
      savedDecksError: '',
      editingDeckData: null,
      themePreference: getStoredThemePreference(),
      viewportWidth: getViewportWidth(),
      friendListData: null,
      friendsSidebarOpen: false,
      // Minimal modal invoked by the Play screen's Friend Match tile.
      // Independent from friendsSidebarOpen so the two can't both be
      // visible at once, and so closing the picker doesn't unrelatedly
      // close the main sidebar.
      friendInvitePickerOpen: false,
      // Remembered mode of the draft event the user is currently sitting
      // in ('standard' | 'sealed'). Drives mode-specific branches in the
      // draft lifecycle handlers (sealed skips the picking phase and
      // shows the pack-opening ceremony instead).
      draftEventMode: null,
      // Booster set key of the draft/sealed event the user is in.
      // used by the sealed pack-opening ceremony to theme the
      // booster artwork (gothic / arthurian / beta). Populated from
      // the draft:building payload; null outside a draft event.
      draftEventSetKey: null,
      viewingFriendProfile: null,
      // Profile id the deck editor should return to on Back. Set when
      // opening a library deck via FriendProfileOverlay's onOpenLibraryDeck;
      // cleared by handleBackToGallery after routing back to the profile.
      deckEditorReturnToProfile: null,
      // True when the currently-open profile was reached from the Hall
      // of Fame's top-player button. Persists across profile→deck-editor
      // and profile→skill-tree detours (their existing return-to-profile
      // logic re-opens the same profile, with this flag still set);
      // consumed only when the profile itself is closed, which then
      // re-opens the Hall of Fame.
      profileReturnToHallOfFame: false,
      // Public profile of another player whose skill tree we're
      // viewing in read-only mode. Null means we're either looking at
      // the self skill tree (via skillTreeOpen) or not in a tree view
      // at all. `skillTreeReturnToProfile` carries the id to re-open
      // when the visitor closes the tree.
      viewingSkillTreeProfile: null,
      skillTreeReturnToProfile: null,
      pendingInviteRoomCode: null,
      isInviteMatch: false,
      isInviteHost: false,
      isSpectating: false,
      spectateRoomCode: null,
      tradeActive: false,
      tradePartnerName: null,
      tradeRoomCode: null,
      gameMenuOpen: false,
      updateStatus: null,
      // Set by the version-compat gate (REST preflight or WS rejection)
      // when the server says this client is too old. Drives a forced
      // UpdateModal that overrides the normal READY_TO_INSTALL flow.
      versionRejection: null,
      currentSeason: null,
      settingsOpen: false,
      skillTreeOpen: false,
      hallOfFameOpen: false,
      seasonRankingOpen: false,
      // Celebration overlay payload. Set when the server pushes a
      // `season:rollover:champion` event for the current player. Null
      // when no overlay is showing. Shape: { seasonId, rank, coins,
      // voucher, points }.
      seasonChampionCelebration: null,
      // The achievement currently being claimed via the celebration
      // overlay. Set to an achievement id when the player clicks an
      // unclaimed row in the Hub panel; the overlay reads its
      // metadata from the ACHIEVEMENTS catalog and drives the
      // server-side keystone payout via handleClaimAchievement.
      claimingAchievementId: null,
      mailboxOpen: false,
      globalChatMentions: 0,
      focusGlobalMessageId: null,
      // Bumped to land the social hub on its Notifications tab.
      mailboxFocusNotifications: null,
      // Per-tab unread for the Mailbox (Social / Auction / News badges).
      // Social bucket folds `draft-invite` mail + total unread chat into
      // the friend mail count so the Social tab badge matches everything
      // a player can see inside that tab. Driven by the server's
      // /mail/unread-count response.
      mailboxUnreadByTab: { friend: 0, auction: 0, news: 0 },
      // Matches waiting on the player + unread activity, from notificationsStore.
      notificationsCount: 0,
      mailboxSelectedMailId: null,
      mailboxView: null,
      mailboxComposeRecipientId: null,
      // Draft state
      draftEventId: null,
      draftPhase: null,
      draftedCards: null,
      draftFinalStandings: null,
      draftPrizes: null,
      draftQueueOpen: false,
      // Modal payload shown when an active draft/sealed event got
      // cancelled server-side (opponent disconnect, creator cancel,
      // etc). Null when no cancel is being surfaced. Cleared by the
      // modal's "Back to Hub" button.
      draftCancelledModal: null,

      // Modal payload shown when a live match was abandoned server-side
      // (both players disconnected and neither returned within the grace
      // window). Terminal and non-resumable. No winner, no rewards. Null
      // when nothing is being surfaced. Cleared by the "Back to Hub" button.
      abandonedMatchModal: null,

      // Instance-first play flow state. When set, these override
      // the old PlayScreen/ArenaMatchmaking routing under
      // arenaView='play'. See 2026-04-24 unified instance/save
      // system design.
      pickerVisibility: null,            // 'solo' | 'friends' | 'public' | null
      setupForMode: null,                // { visibility, modeKey } | null
      pendingInstanceLaunch: null,       // { instance, isHost, deckId? } | null
      activeReadyCheck: null,            // { instance } | null
      _reconcileSeenAt: {},   // instanceId -> last applied last_activity_at (ms)
      // Themed confirm dialog. { title, message, confirmLabel,
      // cancelLabel, danger, onConfirm, onCancel } | null. Replaces
      // native confirm(). OS popups ignored the zoom scale,
      // looked jarring against the medieval theme, and couldn't be
      // styled.
      pendingConfirm: null,
      // Per-round draft match ready-check. { eventId, matchId,
      // opponentName } while the modal is open; routed to
      // GameBoard on match_start.
      draftMatchReadyCheck: null,
      joiningPublicInstance: null,       // { instance } | null (pre-deck-pick)
      // Pre-create state: user picked visibility+mode+setup but
      // hasn't yet chosen their deck. Deck-select receives this and
      // on confirm POSTs /instances with creatorDeckId attached.
      pendingInstanceCreate: null,       // { visibility, modeKey, cfg } | null
      // After a Friends instance is created, the picker stays open
      // so the host can invite friends. Instance id is carried
      // here so each FriendInvitePicker pick dispatches an
      // instance-invite chat message referencing it.
      pendingInstanceForInvite: null,    // { instance } | null
      // Hub-initiated create: 'Host a table' / 'Challenge' return to the hub
      // instead of the Play screen once the lobby exists.
      hubQuickHost: false,
      challengeTarget: null,             // { id, name } | null
      // After accepting a chat instance-invite, the acceptor passes
      // through deck-select then joins the instance via
      // apiJoinInstance. Instance id carries here across the
      // deck-pick sub-flow.
      joiningInstanceId: null,           // string | null
      // When a host clicks Resume on a paused multiplayer
      // instance, the local blob id is stashed here so
      // _launchInstanceHost consumes it (sessionMode='load')
      // when the subsequent phaseTransition→active event fires.
      // Map<instanceId, sessionId>.
      _pendingResumeBlobs: null,

      // Marks the next GameBoard mount as a resume-join for a guest
      // (non-host). GameBoard skips fresh-match deck spawn + opening
      // draw and requests state:sync from the host so the board
      // reflects the saved state rather than resetting.
      isResumeJoin: false,

      // --- Draft / sealed instance save-blob context ---
      //
      // Set when the user creates a draft instance via
      // SessionBrowser OR resumes one via SaveRow. Checkpoint
      // save hooks (handleDraftStarted / handleBuildingPhase /
      // handleTournamentStart / handleDraftComplete) consult
      // these to persist snapshots of the server-authoritative
      // draft state into the host's local blob. Cleared on
      // draft exit / cancellation / completion.
      currentDraftInstanceId: null,
      currentDraftInstanceVisibility: null,
      currentDraftInstanceIsHost: false,
      // Active draft ROUND match id. Scopes per-round-match
      // autosaves so each Swiss round has its own resumable blob.
      currentDraftMatchId: null,
      // Drives the legacy CreateDraftModal render from the
      // SessionBrowser visibility → draft/sealed flow. Matches
      // setupForMode's shape for Match / Sandbox, but targets a
      // richer modal that handles set-unlock gates, pod size,
      // and private-draft invites.
      draftCreateSetup: null, // { visibility, modeKey } | null
    };
    // Per-instance WS subscription teardown fns, keyed by instance id.
    this._instanceSubs = new Map();

    // In-flight purchase reconciliation. Maps pack id -> append timestamp
    // for packs we've just appended via buyArenaPack. The hydration in
    // componentDidUpdate consults this set so a server profile push that
    // races a purchase response doesn't wipe the locally-appended packs.
    // Entries clear when the server snapshot finally includes the id, or
    // after RECENT_PURCHASE_TTL_MS as a safety net.
    this._recentPurchaseIds = new Map();

    this.arenaQueuePollTimer = null;
    this.savedDeckCardIndex = null;
    this.savedDeckCardIndexSource = null;
    this.gameBoardRef = null;
    this.themeMediaQuery = null;
    this.updateManager = null;
  }

  // Dev-only E2E hooks. `window.__e2e.startSandbox()` (or `?e2e=1`) boots
  // straight into an empty solo sandbox board so a Playwright harness can
  // set up + play cards via the GameBoard's window.__test bridge. Never
  // installed in packaged builds (isDevBuild gate).
  _installE2EHooks = () => {
    if (!isDevBuild() || typeof window === 'undefined') return;
    window.__e2e = {
      startSandbox: (deckName) => this._startE2ESandbox(deckName),
      // The GUI suite drives the app in English regardless of the saved
      // language; in memory only, so a run never changes the user's choice.
      setLanguage: overrideLanguage,
      // Notification triggers for the GUI suite / visual checks. The
      // toast APIs are module-scoped, so page.evaluate can't reach
      // them without these.
      toast,
      addToast: this.addToast,
      pushAchievementToast,
    };
    // Read-only GUI-test hook (dev builds only). The Playwright GUI flow
    // suite (tests/gui/) asserts app/board/mirror state through this instead
    // of pixel checks, and reads the render-parity checker's verdict as the
    // graphical-correctness proof. Everything returned must be JSON-cloneable.
    window.__valkenhallTest = {
      app: () => ({
        loggedIn: !!this.state.loggedIn,
        authChecking: !!this.state.authChecking,
        arenaView: this.state.arenaView || null,
        isGameBoardOpen: !!this.state.isGameBoardOpen,
        isCasualSandbox: !!this.state.isCasualSandbox,
        currentInstanceId: this.state.currentInstanceId || null,
        roomCode: this.state.roomCode || null,
        profileId: this.state.arenaProfile?.id || null,
        deckCount: (this.state.savedDecks || []).length,
        lfgMatch: this.state.lfgMatch
          ? { matchId: this.state.lfgMatch.matchId || null, phase: this.state.lfgMatch.phase, opponentAccepted: !!this.state.lfgMatch.opponentAccepted }
          : null,
      }),
      openProfile: (id) => this.handleViewFriendProfile(id),
      // Bake a registered dice skin's shelf still, as a data URL; the GUI
      // bake spec writes it beside the skin's tiles. Dev builds only.
      diceSkinIds: () => Object.keys(DICE_SKINS),
      // The tray's palette pick, for the GUI dice-palette spec.
      setDicePalette: (id) => setDicePalette(id),
      bakeDiceShelf: async (id) => {
        const blob = await bakeShelfPreview(DICE_SKINS[id]);
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      },
      board: () => {
        const b = this.gameBoardRef;
        if (!b || !b.state) return null;
        const m = b._authMirror?.getState?.() || null;
        const cards = m?.cards || {};
        const avatars = [];
        try {
          for (const [id, mesh] of (b.meshes || [])) {
            const c = mesh?.userData?.cardInstance;
            if (!c || c.type !== 'Avatar') continue;
            const at = (typeof b._cellAtWorld === 'function' && mesh.position)
              ? b._cellAtWorld(mesh.position.x, mesh.position.z) : null;
            avatars.push({
              id,
              name: c.name || null,
              rotated: !!c.rotated,
              meshCell: at ? { col: at.col, row: at.row } : null,
              mirrorCell: cards[id]?.cell ? { col: cards[id].cell.col, row: cards[id].cell.row } : null,
              world: mesh.position ? { x: mesh.position.x, z: mesh.position.z } : null,
            });
          }
        } catch (_) { /* keep the hook read-only and non-throwing */ }
        return {
          connectionStatus: b.state.connectionStatus || null,
          connectionFailed: !!b.state.connectionFailed,
          loadingMessage: b.state.loadingMessage || null,
          matchPhase: b.state.matchPhase || null,
          mirrorPhase: m?.phase ? JSON.parse(JSON.stringify(m.phase)) : null,
          activePlayerNum: b.state.activePlayerNum ?? null,
          isHost: !!b.state.isHost,
          showFirstPlayerDice: !!b.state.showFirstPlayerDice,
          mulliganPhase: b.state.mulliganPhase || null,
          mulliganPhase2: b.state.mulliganPhase2 || null,
          handCount: (b.state.handCards || []).length,
          handCount2: (b.state.handCards2 || []).length,
          hand: (b.state.handCards || []).map((c) => c.name),
          hand2: (b.state.handCards2 || []).map((c) => c.name),
          boardCards: Object.entries(cards)
            .filter(([, c]) => c?.zone === 'play')
            .map(([id, c]) => {
              // meshCell: the cell the RENDERED mesh actually resolves to (via
              // the renderer's own _cellAtWorld). A GUI assertion compares it
              // against the authoritative `cell`. Any divergence is the
              // play-bounce class the cell-authoritative migration removes.
              const mesh = b.meshes?.get?.(id);
              const at = (mesh?.position && typeof b._cellAtWorld === 'function')
                ? b._cellAtWorld(mesh.position.x, mesh.position.z) : null;
              return {
                id,
                cardId: c.cardId ?? null,
                owner: c.owner || null,
                controller: c.controller || null,
                rotated: typeof c.rotated === 'boolean' ? c.rotated : null,
                tapped: typeof c.tapped === 'boolean' ? c.tapped : null,
                counters: typeof c.counters === 'number' ? c.counters : null,
                cell: c.cell ? { col: c.cell.col, row: c.cell.row } : null,
                meshCell: at ? { col: at.col, row: at.row } : null,
                subsurface: c.subsurface ?? null,
              };
            }),
          avatars,
          // Automation-surface readers (prompt / unit-hand GUI tests).
          mirrorPendingChoice: m?.pendingChoice
            ? {
                promptId: m.pendingChoice.promptId,
                seat: m.pendingChoice.seat,
                kind: m.pendingChoice.kind || 'target',
                candidates: [...(m.pendingChoice.candidates || [])],
                cells: [...(m.pendingChoice.cells || [])],
                choices: (m.pendingChoice.choices || []).map((c) => ({ label: c.label, value: c.value })),
                zone: m.pendingChoice.zone || null,
              }
            : null,
          pendingServerChoice: b.state.pendingServerChoice
            ? {
                promptId: b.state.pendingServerChoice.promptId,
                seat: b.state.pendingServerChoice.seat,
                kind: b.state.pendingServerChoice.kind,
              }
            : null,
          pileDialogOpen: !!b.state.pendingPileSelect,
          discardGlow: !!b._pendingDiscardCost,
          castingSpells: (b.state.castingSpells || []).map((c) => c.cardName),
          mirrorUnitHands: Object.fromEntries(Object.entries(m?.unitHands || {}).map(
            ([ownerId, e]) => [ownerId, { controller: e?.controller || null, spells: (e?.spells || []).map((s) => s.id) }],
          )),
          renderedUnitHands: Object.fromEntries(Object.entries(b.state.gameState?.unitHands || {}).map(
            ([ownerId, e]) => [ownerId, { controller: e?.controller || null, spells: (e?.spells || []).map((s) => s.id) }],
          )),
          mirrorHands: {
            p1: [...(m?.hands?.p1 || [])],
            p2: [...(m?.hands?.p2 || [])],
          },
          dice: Object.values(m?.dice || {}).map((d) => ({ id: d.id, seat: d.seat, dieType: d.dieType, value: d.value, x: d.x, z: d.z })),
          renderedDice: [...(b.diceMeshes?.keys?.() || [])],
          boardFx: (m?.boardFx || []).map((f) => ({
            id: f.id ?? null,
            kind: f.kind,
            seat: f.seat || null,
            steps: (f.steps || []).map((s) => ({ instanceId: s.instanceId, classification: s.classification || null })),
          })),
        };
      },
      parity: () => {
        const b = this.gameBoardRef;
        const m = b?._authMirror?.getState?.() || null;
        let now = [];
        try {
          now = (b && m) ? checkRenderParity(b, m) : [];
        } catch (err) {
          now = [`parity-check-error: ${err?.message || err}`];
        }
        return { now, reported: getReportedParityViolations() };
      },
    };
    try {
      if (new URLSearchParams(window.location.search).get('e2e') === '1') this._startE2ESandbox();
    } catch (_) { /* no-op */ }
  };

  _startE2ESandbox = (deckName) => {
    if (!isDevBuild()) return;
    // Sandbox is a REAL server solo match now (one pilot, both seats). Boot
    // it through the same instance-create flow the Casual tile uses, with the
    // named saved deck (default: the first) in both slots. Requires a
    // logged-in dev profile + local dev server (the normal dev-loop state).
    const decks = this.getArenaSavedDecks() || [];
    const deck = deckName ? decks.find((d) => d.name === deckName) : decks[0];
    if (!this.state.arenaProfile?.serverToken || !deck) {
      toast.error('E2E sandbox needs a logged-in profile with at least one deck.');
      return;
    }
    this.setState({
      introComplete: true, // skip the studio splash + legal disclaimer
      pendingInstanceCreate: {
        // Automated: the GUI suites (tests/gui/automations.gui.js above all)
        // exist to exercise the automation engine, so the E2E sandbox opts in
        // rather than taking the manual default.
        visibility: 'solo', modeKey: 'sandbox', cfg: { automations: true }, creatorDeckId: deck.id,
      },
    }, () => {
      this.confirmDeckAndQueue(deck.id);
    });
  };

  componentDidMount() {
    // play.valkenhall.com/replay/<token>: a public replay link. Opened once
    // the catalog is loaded and the auth check has settled, logged in or not.
    this._pendingReplayLink = window.location.pathname.match(/^\/replay\/([\w-]+)$/)?.[1] || null;
    if (this._pendingReplayLink) this.setState({ replayLinkPending: true });
    document.addEventListener('keydown', this.handleDocumentKeyDown);
    document.addEventListener('keyup', this.handleDocumentKeyUp);
    document.addEventListener('contextmenu', this.handleGlobalContextMenu);
    window.addEventListener('resize', this.handleWindowResize);

    // The Skill Tree button in AppHeader dispatches `vh:open-skill-tree`
    // when no `onOpenSkillTree` prop is wired (i.e. on every screen
    // except the Hub). Catching it here means we don't have to thread
    // the open callback through every AppHeader caller.
    this._handleOpenSkillTreeEvent = () => this.handleOpenSkillTree();
    window.addEventListener('vh:open-skill-tree', this._handleOpenSkillTreeEvent);

    // Dev role preview (AppHeader switcher). The previewed role lives
    // in staffAccess.js rather than on the profile, so nothing here
    // re-renders on its own. Force one pass so the hub button, the
    // panel title and every capability-gated control re-resolve.
    this._unsubscribeRolePreview = onRolePreviewChange(() => this.forceUpdate());

    // Draft cancel/leave both refund the entry currency (keystone or
    // gold) server-side and fire a WS profile:update. But the user
    // typically navigates away the moment cancel succeeds, so the WS
    // push can land after the UI has moved on. draftApi broadcasts a
    // synchronous balance from the cancel/leave responses; we merge
    // it into arenaProfile here so the header counter ticks back up
    // immediately. The WS push remains a belt-and-suspenders sync.
    this._handleDraftRefundEvent = (e) => {
      const { keystones, coins } = e.detail || {};
      this.setState((s) => {
        if (!s.arenaProfile) return null;
        const next = { ...s.arenaProfile };
        if (keystones != null) next.keystones = keystones;
        if (coins != null) next.coins = coins;
        return { arenaProfile: next };
      });
    };
    window.addEventListener('valkenhall:draft-refund', this._handleDraftRefundEvent);

    // Re-derive pending instances whenever the window regains focus or
    // on a slow heartbeat. Covers a missed WS broadcast (sleep,
    // background-throttle, network blip) without trusting any single event.
    this._onFocusReconcile = () => { this._reconcilePending(); };
    window.addEventListener('focus', this._onFocusReconcile);
    this._readyHeartbeat = setInterval(() => { this._reconcilePending(); }, 4000);

    // Drain the match-report outbox every time the WS opens. Covers
    // both first-launch (carrying a report from a previous session
    // that died mid-claim) and mid-session reconnects (a transient
    // disconnect during the post-match window).
    this._unsubReportsDrain = onConnectionState((state) => {
      if (state !== 'open') return;
      drainPendingReports(reportMatchOutcome).catch((err) => {
        console.warn('[app] pending-report drain failed:', err?.message || err);
      });
      // Drain any match logs that failed to upload while offline.
      drainPendingLogs().catch((err) => {
        console.warn('[app] pending-log drain failed:', err?.message || err);
      });
    });

    // Register the singleton bus handlers so InstanceQueueIndicator
    // (rendered inside AppHeader on every screen) can invoke them
    // without prop threading through each AppHeader caller.
    registerConfirmHandler((opts) => this._askConfirm(opts));
    registerInstanceQueueHandlers({
      onOpenInstance: (inst) => {
        // Gathering-phase instances just need the user to see the
        // full save list. Don't fire markReady here (that's what
        // the SaveRow Resume button does for paused/active).
        if (inst?.phase === 'gathering') {
          this.setState({ arenaView: 'play' });
          return;
        }
        this.handleInstanceAction(inst, 'open');
      },
      onAcceptInstanceInvite: (senderId, msg) => this.handleAcceptMatchInviteFromChat(senderId, msg),
      // Draft-invite chat messages live alongside match invites
      // in the queue dropdown now. Accepting one routes through
      // the same legacy path chat Accept does: openDraftLobby
      // which takes care of joining the event and mounting the
      // standalone draft lobby.
      onAcceptDraftInvite: (eventId, _msg, opts) => {
        if (eventId) this.openDraftLobby(eventId, opts);
      },
      // Accepting a draft-MATCH invite (opponent is ready in your round)
      // goes straight into the match, not the lobby/dashboard.
      onAcceptDraftMatchInvite: (eventId, matchId) => {
        if (eventId && matchId) this.handleAcceptDraftMatchInvite(eventId, matchId);
      },
    });
    registerNotificationOpenHandler((n) => this.openNotificationTarget(n));
    // Feeds the social hub badge. mail.received rows are left out: the
    // inbox's own unread count covers those letters.
    const countNotifications = (st) => this.setState({
      notificationsCount: st.matchCount + st.activity.filter((a) => !a.readAt && !a.resolvedAt && a.kind !== 'mail.received').length,
    });
    this._unsubNotificationsStore = subscribeNotifications(countNotifications);
    countNotifications(getNotificationsState());
    // Live toast for a freshly arrived notification. Friend requests keep
    // their own Accept/Decline toast and chat has its own panel + sound.
    this._unsubNewNotification = onNewNotification((n) => {
      if (n.kind === 'friend.request' || n.kind === 'chat.message') return;
      const { title, body } = describeNotification(n);
      this.addToast({
        title,
        message: body || undefined,
        notification: n,
        actions: notificationHasTarget(n) && !this.notificationLinkBlocker()
          ? [{ label: t('Open'), key: 'open-notification', primary: true }] : undefined,
      });
    });

    // Global draft/sealed lifecycle listener. Individual screens
    // (DraftLobby / DraftPicker / SealedPackOpener / DraftDeckBuilder)
    // all subscribe for their own workflows, but we need ONE top-
    // level listener that always fires regardless of which screen
    // the user is currently on. Disconnect + cancelled events have
    // to reach the user wherever they happen to be sitting.
    this.unsubGlobalDraft = subscribeToDraftEvents({
      player_disconnected: (data) => this.handleDraftOpponentDisconnected(data),
      player_reconnected: (data) => this.handleDraftOpponentReconnected(data),
      player_left: (data) => this.handleDraftPlayerLeft(data),
      cancelled: (data) => this.handleDraftEventCancelled(data),
      // Per-pick checkpoint. App-level (not DraftPicker-level) so
      // the blob stays in sync even if the user has the pack
      // overlay minimised or the picker hasn't mounted yet
      // (pre-animation). Runs only for the host of a
      // currentDraftInstance; guests skip inside the helper.
      pick_confirmed: (data) => this._handleDraftPickConfirmed(data),
      auto_picked: (data) => this._handleDraftPickConfirmed(data),
      // Match-result checkpoint. The POST /match-result endpoint
      // broadcasts draft:round_result with updated pairings once
      // the server has recorded the winner. Snapshot the new
      // pairing state so View Results on the save row stays
      // accurate mid-tournament.
      round_result: (data) => this._handleDraftRoundResult(data),
      // Per-round-match ready-check + start. Fired when either
      // player clicks Join Match on a pairing. Opens the modal
      // on both seats; match_start carries the (fresh) roomId
      // once both have confirmed.
      match_ready_check: (data) => this._handleDraftMatchReadyCheck(data),
      match_start: (data) => this._handleDraftMatchStart(data),
      match_ready_left: (data) => this._handleDraftMatchReadyLeft(data),
    });

    // Controller subsystem is gated behind a feature flag (default
    // off). When disabled, nothing starts and no input code runs.
    // Re-enable via Settings → Controls → "Controller support".
    if (isControllerEnabled()) {
      this._controllerBridge = createGamepadFocusBridge();
      this._controllerBridge.start();
      this._controllerModeObserverOff = controllerMode.installTextInputObserver();
      startSdlGamepadClient();
      autoReconnectHidGamepads().catch(() => {});
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.themeMediaQuery.addEventListener('change', this.handleThemeMediaQueryChange);
    }
    applyThemePreference(this.state.themePreference);

    // Decks are loaded after login via postLoginInit. Skipped on mount
    // because the auth token may not be available yet (401 from server).

    this.loadCardsWithRetry = async (retries = 3, delay = 2000) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          return await loadSorceryCardsWithSource();
        } catch (error) {
          console.warn(`Card load attempt ${attempt}/${retries} failed:`, error.message);
          if (attempt < retries) {
            this.setState({ loadingDetail: `Retrying... (${attempt}/${retries})` });
            await new Promise((r) => setTimeout(r, delay));
          } else {
            console.error('Failed to load Sorcery cards after retries:', error);
            return { cards: null, source: '' };
          }
        }
      }
      return { cards: null, source: '' };
    };

    // Load sorcery cards
    this.setState({
      loading: true,
      loadingMessage: 'Loading Card Database',
      loadingDetail: 'Fetching card data...',
    });

    this.loadCardsWithRetry()
      .then((sorceryResult) => {
        setSorceryCards(sorceryResult.cards);
        this.setState({
          sorceryCards: sorceryResult.cards,
          sorceryCardsLoadSource: sorceryResult.source,
          loading: false,
          loadingMessage: '',
          loadingDetail: '',
        }, () => { this.initSeason(); this._installE2EHooks(); this._maybeOpenReplayLink(); });
      });

    // Check for existing auth token
    this.checkAuth();

    // Server-revoked tokens (admin ban, DB anomaly, manual revocation):
    // the next authenticated REST call returns 401, which serverClient
    // fans out here. Already-logged-out apps ignore it so a stray 401
    // on the login screen doesn't fire a redundant logout.
    this._sessionExpiredUnsub = onSessionExpired(() => {
      if (!this.state.loggedIn) return;
      toast.error(t('Your session expired. Please sign in again.'));
      this.handleLogout();
    });

    if (isWeb()) {
      // No updater in the browser: new versions arrive by page reload.
      // Feed a synthetic UP_TO_DATE status so everything that reads
      // updateStatus (hub version chip, report dialog, Settings) keeps
      // working off the baked build version.
      this.setState({ updateStatus: { state: 'UP_TO_DATE', currentVersion: webClientVersion() } });
    } else {
      this.updateManager = createUpdateManager((status) => {
        const prev = this.state.updateStatus;
        this.setState({ updateStatus: status });

        if (status.state === 'READY_TO_INSTALL' && prev?.state !== 'READY_TO_INSTALL') {
          toast.info(t('Update {version} ready. Restart from Settings to install.', { version: status.newVersion || '' }).trim());
        }
      });

      this.updateManager.init();
    }

    // Version-compat gate. Preflight runs on boot and every 5min; the
    // WS layer also pushes rejections into this same channel. On the
    // first rejection we kick off an immediate updater check so the
    // download starts in parallel with the modal appearing.
    this._versionCheckUnsub = onVersionCheck((result) => {
      if (!result || result.ok) {
        if (this.state.versionRejection) this.setState({ versionRejection: null });
        return;
      }
      this.setState({ versionRejection: result });
      this.updateManager?.check?.();
    });
    startVersionPolling();
  }

  checkAuth = async () => {
    const token = await getStoredToken();
    if (!token) {
      this.setState({ authChecking: false }, this._maybeOpenReplayLink);
      return;
    }
    try {
      const result = await validateToken(token);
      if (result.valid) {
        const fullProfile = await loadArenaProfile(token).catch(() => null);
        const serverProfile = fullProfile || result.profile;
        this.setState({
          authChecking: false,
          loggedIn: true,
          serverUnreachable: false,
          arenaProfile: this.profileFromServer(serverProfile, token),
          arenaLoading: false,
        }, () => { this.postLoginInit(); this._maybeOpenReplayLink(); });
        return;
      }
      // Server explicitly said the token is bad. Log the user out
      // cleanly and fall through to the login screen.
      await clearStoredToken().catch(() => {});
      this.setState({ authChecking: false }, this._maybeOpenReplayLink);
    } catch (err) {
      if (isNetworkError(err)) {
        // Server is unreachable. KEEP the stored token so the user
        // isn't kicked out over a transient outage; show the login
        // screen with a connection-lost banner and retry on a loop
        // until the server answers (401 → logout, 200 → fall through
        // into the logged-in state above).
        this.setState({ authChecking: false, serverUnreachable: true });
        if (!this._authRetryTimer) {
          this._authRetryTimer = setTimeout(() => {
            this._authRetryTimer = null;
            this.checkAuth();
          }, 8000);
        }
        return;
      }
      // Any other error. Fail closed and show the login screen.
      this.setState({ authChecking: false });
    }
  };

  componentWillUnmount() {
    try { this._unsubNewNotification?.(); } catch {}
    try { this._unsubNotificationsStore?.(); } catch {}
    this._unsubGlobalChat?.();
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    document.removeEventListener('keyup', this.handleDocumentKeyUp);
    document.removeEventListener('contextmenu', this.handleGlobalContextMenu);
    window.removeEventListener('resize', this.handleWindowResize);
    if (this._handleOpenSkillTreeEvent) {
      window.removeEventListener('vh:open-skill-tree', this._handleOpenSkillTreeEvent);
      this._handleOpenSkillTreeEvent = null;
    }
    if (this._handleDraftRefundEvent) {
      window.removeEventListener('valkenhall:draft-refund', this._handleDraftRefundEvent);
      this._handleDraftRefundEvent = null;
    }
    this.themeMediaQuery?.removeEventListener('change', this.handleThemeMediaQueryChange);
    if (this._controllerBridge) { this._controllerBridge.stop(); this._controllerBridge = null; }
    if (this._controllerModeObserverOff) { this._controllerModeObserverOff(); this._controllerModeObserverOff = null; }
    if (isControllerEnabled()) stopSdlGamepadClient();
    this._unsubscribeRolePreview?.();
    this.unsubGlobalDraft?.();
    this._sessionExpiredUnsub?.();
    this._unsubReportsDrain?.();
    window.removeEventListener('focus', this._onFocusReconcile);
    clearInterval(this._readyHeartbeat);
  }

  // Global draft event handlers. Fire regardless of which draft
  // screen (if any) the user is on right now.

  handleDraftOpponentDisconnected = (data) => {
    if (!data?.eventId || data.eventId !== this.state.draftEventId) return;
    if (data.playerId === this.state.arenaProfile?.id) return;
    const name = data.playerName || t('Opponent');
    const seconds = Math.ceil((data.gracePeriodMs || 120000) / 1000);
    toast.warning(t('{name} disconnected. Waiting up to {seconds}s for them to reconnect.', { name, seconds }), {
      id: `draft-disc-${data.playerId}`,
      duration: 8000,
    });
  };

  // A participant left the tournament (Leave button or quit). Inform the
  // others wherever they're sitting (deck builder, dashboard, …) so they
  // aren't left building/waiting alone with no idea what happened.
  handleDraftPlayerLeft = (data) => {
    if (!data?.eventId || data.eventId !== this.state.draftEventId) return;
    if (data.playerId === this.state.arenaProfile?.id) return;
    const name = data.playerName || t('A player');
    toast.warning(t('{name} left the tournament.', { name }), {
      id: `draft-left-${data.playerId}`,
      duration: 8000,
    });
  };

  handleDraftOpponentReconnected = (data) => {
    if (!data?.eventId || data.eventId !== this.state.draftEventId) return;
    if (data.playerId === this.state.arenaProfile?.id) return;
    const name = data.playerName || t('Opponent');
    toast.success(t('{name} reconnected', { name }), { id: `draft-disc-${data.playerId}`, duration: 4000 });
  };

  handleDraftEventCancelled = (data) => {
    if (!data?.eventId || data.eventId !== this.state.draftEventId) return;
    // Only pop the refund modal for events the user was actually in.
    // Show the modal + route back to the hub; the coin refund + card
    // cleanup was already applied server-side by abortDraft.
    this.setState({
      draftCancelledModal: {
        eventId: data.eventId,
        reason: data.reason || 'cancelled',
        wasSealed: this.state.draftEventMode === 'sealed',
      },
      // Clear active-draft state so re-entering the hub is clean.
      draftEventId: null,
      draftPhase: null,
      draftedCards: null,
      draftEventMode: null,
      draftEventSetKey: null,
      arenaView: 'hub',
    });
    getStoredToken().then((token) => {
      if (token) loadArenaProfile(token).then((p) => {
        if (p) this.setState({ arenaProfile: this.profileFromServer(p, token) });
      }).catch(() => {});
    });
  };

  componentDidUpdate(_prevProps, prevState) {
    // Keep the global instance WS subscription set in sync with the
    // currently-logged-in player. On first profile load, on sign-in
    // after a sign-out, and on any id change, re-subscribe so the
    // ReadyCheckModal overlay fires reliably.
    const prevId = prevState.arenaProfile?.id;
    const nextId = this.state.arenaProfile?.id;
    if (nextId && nextId !== prevId) {
      // One-shot migration of any pre-instance local solo saves so
      // they appear in the unified SessionBrowser list. Gated by a
      // localStorage flag. Runs at most once per device.
      migrateSoloSavesToInstances().finally(() => this._ensureInstanceSubs());
      // DEV quick match (local server): the server pushes both accounts the
      // fresh active instance id; reconcile launches the board on each side
      // with no invite/ready ceremony. Registered once per login.
      if (isDevBuild()) {
        this._offDevQuickMatch?.();
        this._offDevQuickMatch = onDevQuickMatch(({ instanceId }) => {
          this._ensureInstanceSubs();
          this.reconcileInstanceById(instanceId);
        });
      }
      // Looking-for-match pairing lands the same way: a ready instance
      // the server already flipped to active.
      this._offLfgMatched?.();
      this._offLfgMatched = lfg.onMatchEvent((type, data) => this.handleLfgEvent(type, data));
      this._offLfgState?.();
      this._offLfgState = lfg.subscribe((st) => this.setState({ lfgState: st }));
      // One-shot replay of the retired localStorage dismissed/pending
      // sets onto real server state (abandon/leave). See the module
      // docstring for why these sets no longer exist.
      migrateLocalInstanceState(nextId).catch(() => {});
      // Warm the protected-playmat assets the moment we have a userId.
      // i.e. at login, BEFORE onboarding. The first-run flow then walks
      // the user through username + deck selection (several seconds),
      // which is exactly the window needed to prime the protected-mat
      // registry and pre-fetch the four Collector Arthouse preview
      // thumbnails. By the time the playmat picker renders they're warm
      // in the local server's cache, so all six mats paint instantly.
      // the bundled native two and the fetched protected four alike.
      // ArenaHub also warms on mount (returning users); the underlying
      // prewarm is module-guarded, so the two paths share one fetch.
      this.warmOnboardingPlaymatAssets(nextId);
    }

    // Hydrate pending packs whenever the server hands us a fresh
    // _pendingPacksRaw (initial login, post-purchase refetch, profile WS
    // push, any other profileFromServer path). The server list is the
    // canonical "what exists" set, but it can race against a local
    // buyArenaPack append. A WS profile push fired by an adjacent
    // server event (coins update, etc.) may carry a snapshot taken
    // before the purchase commit, which would wipe the just-appended
    // packs if we blindly replaced.
    //
    // Reconciliation rules:
    //   1. Start from the server's resolved list (authoritative).
    //   2. Carry forward any local entry whose id is in
    //      _recentPurchaseIds. Those just came back from a successful
    //      buy and the server snapshot may not have caught up yet.
    //   3. Drop any other local entry not in the server list. That's
    //      a pack the server says no longer exists (e.g., opened on
    //      another device, or finalized).
    //   4. Filter out arenaOpenedPack. Its row sits server-side until
    //      finalize completes; we don't want it rejoining the queue
    //      during the opening animation.
    //   5. Confirmed and expired entries get cleared from
    //      _recentPurchaseIds so the set stays bounded.
    const raw = this.state.arenaProfile?._pendingPacksRaw;
    if (raw && Array.isArray(this.state.sorceryCards) && this.state.sorceryCards.length > 0) {
      const resolved = resolvePendingPacks(raw, this.state.sorceryCards);
      const openedId = this.state.arenaOpenedPack?.id;
      const resolvedIds = new Set(resolved.map((p) => p.id));
      const now = Date.now();
      for (const [id, ts] of this._recentPurchaseIds) {
        if (resolvedIds.has(id) || now - ts > App.RECENT_PURCHASE_TTL_MS) {
          this._recentPurchaseIds.delete(id);
        }
      }
      this.setState((state) => {
        const { _pendingPacksRaw, ...rest } = state.arenaProfile || {};
        const existing = state.arenaPendingPacks || [];
        const byId = new Map();
        for (const p of resolved) byId.set(p.id, p);
        for (const p of existing) {
          if (!byId.has(p.id) && this._recentPurchaseIds.has(p.id)) byId.set(p.id, p);
        }
        let merged = [...byId.values()];
        if (openedId) merged = merged.filter((p) => p.id !== openedId);
        return {
          arenaProfile: rest,
          arenaPendingPacks: merged,
        };
      });
    }

    if (!this.state.arenaLoading) {
      const view = this.state.arenaView;
      const prevView = prevState.arenaView;
      const justLoaded = prevState.arenaLoading && !this.state.arenaLoading;

      if ((view === 'hub' && (prevView !== 'hub' || justLoaded))) {
        playMusic('arena-hub', { fadeInDuration: 3000 });
        // Auto-open the Progression screen if the player has anything
        // unclaimed. Runs on EVERY hub entry (from match-result, from
        // collection, from settings, etc.). Uses the server-derived
        // hasAnyPendingClaim flag baked into the profile snapshot, so
        // no round-trip. The signature guard below prevents annoying
        // re-prompts: if the player closed the screen without claiming
        // (state unchanged), next hub entry skips. If the state did
        // change (a claim landed, a level was crossed), it re-opens
        // so the player can keep walking the gauge.
        this._maybeAutoOpenProgression();
      }
      if ((view === 'deck-gallery' || view === 'deck-editor') && prevView !== 'deck-gallery' && prevView !== 'deck-editor') {
        playMusic('arena-deckbuilder', { fadeInDuration: 3000 });
      }
    }

    // Play match music when the game session actually starts
    if (this.state.sessionMode && !prevState.sessionMode && this.state.isGameBoardOpen) {
      playMusic('arena-match', { fadeInDuration: 3000 });
    }
    // Fade back to hub music when game session ends
    if (!this.state.sessionMode && prevState.sessionMode && !this.state.isGameBoardOpen) {
      playMusic('arena-hub', { fadeInDuration: 3000 });
    }

    // A match that starts and ends on the hub never changes arenaView, so
    // the board closing has to push the screen's label itself or the
    // player keeps reading "In a match" in the Hall and the friend list.
    const boardClosed = prevState.isGameBoardOpen && !this.state.isGameBoardOpen;
    if (prevState.arenaView !== this.state.arenaView || boardClosed) {
      const activityMap = {
        hub: 'hub',
        store: 'store',
        'deck-select': 'deck-select',
        'deck-gallery': 'deckbuilder',
        'deck-editor': 'deckbuilder',
        matchmaking: 'matchmaking',
        'pack-opening': 'pack-opening',
        'auction-house': 'auction-house',
        'draft': 'draft',
        'draft-lobby': 'draft',
        'draft-picking': 'draft',
        'draft-building': 'draft',
        'draft-tournament': 'draft',
        'draft-results': 'draft',
      };
      updateActivity(activityMap[this.state.arenaView] || 'hub');
    }

    if (!prevState.isGameBoardOpen && this.state.isGameBoardOpen && this.state.sessionMode) {
      updateActivity('in-match', {
        ranked: this.state.isRankedMatch,
        opponentName: this.state.arenaMatchmakingOpponent?.name,
        roomCode: this.state.roomCode,
      });
    }
  }

  // Prime the protected-playmat registry + pre-fetch the preview
  // thumbnails as soon as a userId exists (login), so the first-run
  // playmat picker. Which renders before ArenaHub ever mounts. Finds
  // every mat already warm. Once-guarded per id; the registry prime and
  // the prewarm are both module-level idempotent, so this is safe to
  // call alongside ArenaHub's own warm-on-mount. Mirrors
  // ArenaHub.initPlaymatAssets.
  async warmOnboardingPlaymatAssets(id) {
    if (!id || this._playmatWarmedForId === id) return;
    this._playmatWarmedForId = id;
    setCosmeticsUserId(id);
    try { await getCosmeticsCached(); } catch (_) {}
    try { await awaitProtectedRegistry(); } catch (_) {}
    try { await prewarmPlaymatPreviews(); } catch (_) {}
  }

  handleWindowResize = () => {
    const viewportWidth = getViewportWidth();

    this.setState((state) => (state.viewportWidth === viewportWidth ? null : { viewportWidth }));
  };

  refreshSavedDecks = async () => {
    this.setState({ isSavedDecksLoading: true, savedDecksError: '' });

    try {
      const savedDecks = await listSavedDecks('sorcery');
      this.setState({ savedDecks, isSavedDecksLoading: false });
      this.upgradeStalePreconDecks(savedDecks);
    } catch (error) {
      console.error('Failed to load saved decks:', error);
      this.setState({
        savedDecks: [],
        isSavedDecksLoading: false,
        savedDecksError: error?.message || 'Failed to load saved decks',
      });
    }
  };

  // Re-save any precon deck row still holding the pre-upgrade 36/16 list.
  // The server migration grants the added cards; the row itself can only be
  // rewritten here, where the card catalog lives. Runs after every deck list
  // load and no-ops once every row is current. Failures are logged and
  // dropped: the next load retries, and a player whose row stays stale sees
  // the size gate's reason rather than a broken screen.
  upgradeStalePreconDecks = async (savedDecks) => {
    const stale = findStalePreconDecks(savedDecks, this.state.sorceryCards);
    if (!stale.length) return;

    for (const { deck, cards } of stale) {
      try {
        await saveSavedDeck({
          ...deck,
          cards,
          featuredCards: computeFeaturedCards(cards, this.state.sorceryCards, 3),
        }, 'sorcery');
      } catch (e) {
        console.warn(`Failed to upgrade precon deck ${deck.id}:`, e);
        return;
      }
    }

    const refreshed = await listSavedDecks('sorcery').catch(() => null);
    if (refreshed) this.setState({ savedDecks: refreshed });
  };

  profileFromServer = (serverProfile, token) => {
    // Mirror the server's skill blob into the local cache so
    // SkillTreeScreen's sync getSkillState() reads stay in lockstep
    // with whatever the server returned (incl. profile:update WS
    // pushes that re-run profileFromServer).
    if (serverProfile?.skills) seedSkillState(serverProfile.skills);
    return {
      id: serverProfile.id,
      email: serverProfile.email,
      name: serverProfile.name || null,
      coins: serverProfile.coins || 0,
      xp: serverProfile.xp || 0,
      arcanaShards: serverProfile.arcanaShards || 0,
      keystones: serverProfile.keystones || 0,
      wins: serverProfile.wins || 0,
      losses: serverProfile.losses || 0,
      starterDeck: serverProfile.starterDeck || null,
      // One-time free starter-playmat pick (onboarding step after the
      // starter deck). Gates the ArenaFirstPlaymatPicker screen.
      starterPlaymatClaimed: serverProfile.starterPlaymatClaimed || false,
      // Progression state. Owned precon decks and the pending-pick
      // count. The server computes pendingDeckGrants from xp +
      // starter_decks_owned on every GET /profile/me, so we just
      // forward it. Dropping it here would hide the progression
      // picker after any profile:update WS event.
      starterDecksOwned: serverProfile.starterDecksOwned || [],
      pendingDeckGrants: serverProfile.pendingDeckGrants || 0,
      // Server-authoritative Progression-screen snapshot. Drives the
      // ProgressionScreen render and the hub-pill gold-dot indicator
      // (via hasAnyPendingClaim). Updated on every profile load and
      // WS profile:update push, plus after each claim returns its
      // updated snapshot inline. Falls back to whatever was already
      // on the profile so we don't lose state during a transient
      // partial-payload window.
      progression: serverProfile.progression || this.state.arenaProfile?.progression || null,
      lastClaimedGoldLevel: serverProfile.lastClaimedGoldLevel ?? this.state.arenaProfile?.lastClaimedGoldLevel ?? 1,
      grantedSkillPoints: serverProfile.grantedSkillPoints ?? this.state.arenaProfile?.grantedSkillPoints ?? 0,
      // Personal invite code + redemption state. The server now
      // embeds both in the profile payload; we keep a state-fallback
      // for the inviteCode during deploy windows where the server
      // might not yet be shipping the field. inviteCodeRedeemed flips
      // true the moment a friend signs up with the code (server
      // pushes a profile:update on redeem), so the hub's Invite
      // button can grey itself out without a manual refresh.
      inviteCode: serverProfile.inviteCode ?? this.state.arenaProfile?.inviteCode ?? null,
      inviteCodeRedeemed: serverProfile.inviteCodeRedeemed ?? this.state.arenaProfile?.inviteCodeRedeemed ?? false,
      // Full list of codes the player owns ({code, redeemed}). The
      // server keeps each account topped up to three unused codes;
      // we render the entire list so the popover can show every
      // unused code as a separate copy-able row.
      inviteCodes: Array.isArray(serverProfile.inviteCodes)
        ? serverProfile.inviteCodes
        : this.state.arenaProfile?.inviteCodes ?? [],
      profileAvatar: serverProfile.profileAvatar || null,
      discordHandle: serverProfile.discordHandle || null,
      // Title / badge identity. PlayerTitle resolves the displayed
      // title from these three together: the sentinels ('__qa__' /
      // '__founder__') only render when the matching role flag is
      // set, so dropping any one of them here silently demotes the
      // player to their highest-wins crown title everywhere the hub
      // and the in-game HUD read arenaProfile.
      displayedCrownAvatarId: serverProfile.displayedCrownAvatarId ?? null,
      isFounder: !!serverProfile.isFounder,
      isQaTester: !!serverProfile.isQaTester,
      // Scoped admin-panel access. Server-resolved and passed straight
      // through. Utils/staffAccess.js is the only reader and holds no
      // allowlist of its own. No in-memory fallback on purpose: a
      // server that doesn't send these is a server that hasn't shipped
      // roles yet, and the correct answer there is "no panel".
      staffRole: serverProfile.staffRole || null,
      capabilities: Array.isArray(serverProfile.capabilities) ? serverProfile.capabilities : [],
      // Owner-only map of every role's capabilities, used by the dev
      // role-preview switcher. Its presence is what marks the account
      // as allowed to preview; regular staff get null.
      capabilityMatrix: serverProfile.capabilityMatrix || null,
      serverToken: token,
      serverRegistered: true,
      rank: serverProfile.rank || { tier: 'apprentice', division: 4, lp: 0 },
      collection: serverProfile.collection || [],
      matchHistory: serverProfile.matchHistory || [],
      // Pack-opens counter + per-set breakdown. Server is authoritative:
      // packs_opened / packs_opened_by_set are bumped atomically in
      // models/packs.js#openPack inside the same transaction as the
      // grant. The in-memory fallback covers the deploy gap when an
      // older client briefly talks to a server that's already shipping
      // the field but hadn't been before, and vice versa. Once both
      // sides are on this version it never fires.
      packsOpened: serverProfile.packsOpened ?? this.state.arenaProfile?.packsOpened ?? 0,
      packsOpenedBySet: serverProfile.packsOpenedBySet ?? this.state.arenaProfile?.packsOpenedBySet ?? {},
      achievements: serverProfile.achievements || [],
      // Earned-but-not-yet-claimed achievement IDs. Drives the
      // pulsing CLAIM affordance in the panel and the badge counter
      // on the social entry. Falls back to an empty array so the UI
      // can read it unconditionally during server response gaps.
      unclaimedAchievements: serverProfile.unclaimedAchievements || [],
      // Backfill missing fields so consumers (ArcaneTrials in
      // particular) can read claimedTiers / seasonXp / etc. directly.
      // A brand-new account's row defaults to season_progress = '{}'
      // in Postgres; the server forwards it verbatim, and without
      // this backfill the Arcane Trials view crashes on the first
      // access to progress.claimedTiers and Preact unmounts the
      // whole tree (the "blank grey page" symptom).
      seasonProgress: serverProfile.seasonProgress && typeof serverProfile.seasonProgress === 'object'
        ? { ...createDefaultSeasonProgress(serverProfile.seasonProgress.seasonId || null), ...serverProfile.seasonProgress }
        : null,
      // Discord-style personality + availability. The server resolves
      // expired presence to 'online'/null in formatPlayerProfile, so we
      // can pass these through verbatim. Dropping them here would make
      // every profile:update WS event silently revert the user's DND /
      // Idle / Invisible choice on the very next activity transition.
      bio: serverProfile.bio ?? null,
      pronouns: serverProfile.pronouns ?? null,
      customStatus: serverProfile.customStatus ?? null,
      presenceStatus: serverProfile.presenceStatus ?? null,
      presenceExpiresAt: serverProfile.presenceExpiresAt ?? null,
      // Chosen profile background (a Site card id). The hub's
      // profile-background toggle and the own-profile overlay both
      // resolve their backdrop from this via resolveProfileBackdrop;
      // dropping it here would silently fall back to the avatar art.
      backgroundCardId: serverProfile.backgroundCardId ?? null,
      // Skill-tree state. Server is source of truth, the local
      // cache (vh-skills-v1) is seeded by seedSkillState() below so
      // sync renders in SkillTreeScreen line up with the authoritative
      // blob. Skill-gated mechanics like Auction House listing limits
      // and fees read from the server-side `skills` JSONB column.
      skills: serverProfile.skills || { pointsSpent: [], hunterElement: null },
      // Pending packs arrive in wire format from the server. They're
      // resolved against sorceryCards in hydratePendingPacks() once both
      // the profile and the card database have loaded.
      _pendingPacksRaw: serverProfile.pendingPacks || [],
    };
  };

  handleLogin = async (result) => {
    // Verify returns a minimal profile; load the full one from the server
    const fullProfile = await loadArenaProfile(result.token).catch(() => null);
    const serverProfile = fullProfile || result.profile;
    const profile = this.profileFromServer(serverProfile, result.token);
    this.setState({
      loggedIn: true,
      authChecking: false,
      arenaProfile: profile,
      arenaLoading: false,
    }, () => { this.postLoginInit(); this._maybeOpenReplayLink(); });
  };

  // Pull a fresh /me/season into state without touching the rest of
  // the profile. Used by event-driven paths (auction sales, etc.)
  // that advance quest progress server-side. Refetching is the
  // only way to refresh `currentSeason.activeQuests` since the
  // active-quests view is server-computed from per-player progress.
  // When the cycle has rolled over since the cached progress was
  // captured (seasonXp / claimedTiers belong to a prior seasonId), the
  // tier track would render the new season as already fully claimed
  // with a maxed XP bar. Resetting the cached progress here keeps the
  // visual state in sync until the next match writes fresh server
  // progress back into arenaProfile.
  refreshCurrentSeason = async () => {
    try {
      const result = await loadCurrentSeason();
      if (!result?.season) return;
      const season = result.season;
      this.setState((s) => {
        const cached = s.arenaProfile?.seasonProgress;
        const stale = !cached || cached.seasonId !== season.seasonId;
        if (!stale) return { currentSeason: season };
        return {
          currentSeason: season,
          arenaProfile: {
            ...s.arenaProfile,
            seasonProgress: createDefaultSeasonProgress(season.seasonId),
          },
        };
      });
    } catch (e) {
      console.error('Failed to refresh current season:', e);
    }
  };

  initSeason = async () => {
    if (!this.state.arenaProfile) return;
    let season;
    try {
      const result = await loadCurrentSeason();
      // Server attaches `activeQuests` (3 / 6 templates + per-player
      // progress + completed flag) directly onto the season payload.
      // no client-side pool shuffling or initialisation needed.
      season = result.season;
    } catch (e) {
      console.error('Failed to load current season:', e);
      return;
    }
    if (!season) return;

    let progress = this.state.arenaProfile.seasonProgress;
    if (!progress || progress.seasonId !== season.seasonId) {
      progress = createDefaultSeasonProgress(season.seasonId);
    }
    const updatedProfile = { ...this.state.arenaProfile, seasonProgress: progress };
    this.setState({ currentSeason: season, arenaProfile: updatedProfile });
  };

  postLoginInit = () => {
    preloadUISounds();

    // Login replay is now handled by _maybeAutoOpenProgression in
    // componentDidUpdate, which fires on every hub entry (including
    // the first render after arenaLoading flips false). It uses a
    // signature guard so token-refresh / session re-init cycles
    // don't re-pop the screen for the same claim state.

    // Give the server a few seconds to send session:active (which confirms
    // the session is live and cancels this timer). If it doesn't arrive,
    // the session is dead and we clear the stale localStorage entry.
    clearTimeout(this._sessionCleanupTimer);
    this._sessionCleanupTimer = setTimeout(() => {
      try { localStorage.removeItem('valkenhall-active-session'); } catch {}
    }, 5000);
    this.initSeason();
    this.refreshSavedDecks();
    // Fetch the player's personal invite code. Stored on the profile
    // object so it flows to AppHeader through every screen's existing
    // profile prop without additional plumbing.
    const token = this.state.arenaProfile?.serverToken;
    if (token) {
      getMyInviteCode(token).then((code) => {
        if (code && this.state.arenaProfile) {
          this.setState((s) => ({
            arenaProfile: { ...s.arenaProfile, inviteCode: code },
          }));
        }
      }).catch(() => {});
    }
    playMusic('arena-hub', { fadeInDuration: 3000 });
    // Set initial Discord Rich Presence. ComponentDidUpdate only fires
    // on view *changes*, so the first hub load needs an explicit call.
    updateActivity('hub');
    // Poll for Discord "Join Game" clicks. When a friend clicks join
    // on our profile, launch spectator mode with the received room code.
    startJoinPolling((roomCode) => {
      if (this.state.isGameBoardOpen || this.state.isSpectating) return;
      this.handleSpectateAllowed(roomCode);
    });
    // Bind the maintenance WS listeners before presence brings the
    // socket up. Otherwise the first maintenance:notice fan-out can
    // race past us and the banner won't render until the next emit.
    initMaintenanceState();
    startGlobalChat();
    this._unsubGlobalChat = subscribeGlobalChat((s) => {
      if (s.mentions !== this.state.globalChatMentions) this.setState({ globalChatMentions: s.mentions });
    });
    startPresence('hub', {
      onFriendListUpdate: (data) => this.setState({ friendListData: data }),
      onNewNotifications: this.handleNewNotifications,
      onMailCountUpdate: (counts) => {
        const byType = counts?.byType || { friend: 0, auction: 0, news: 0 };
        const chatUnread = counts?.chatUnread || 0;
        // Chat unread lives in the Messages tab (it's the only tab that
        // surfaces chats), so fold it into the friend bucket.
        const mailboxUnreadByTab = {
          friend: (byType.friend || 0) + chatUnread,
          auction: byType.auction || 0,
          news: byType.news || 0,
        };
        this.setState({ mailboxUnreadByTab });
      },
      onChatMessage: (msg) => this.setState({ lastChatMessage: msg }),
      onChatClaimed: (data) => this.setState({ lastChatClaimed: data }),
      onSessionActive: this.handleSessionActive,
      onDraftActive: this.handleDraftActive,
      onProfileUpdate: this.handleServerProfileUpdate,
      onSeasonRefreshNeeded: this.refreshCurrentSeason,
    });
    startNotifications(this.state.arenaProfile?.id);
    // Hydrate the Moments feed from IndexedDB so the panel paints
    // with whatever was cached locally the last time this client
    // received posts. Incoming moment:received events from the
    // presence manager will overlay anything that arrived since.
    momentsManager.init().catch((err) => console.error('[moments] init:', err));
    // Subscribe to draft:complete at the app level so it's caught even
    // when the tournament screen is unmounted (e.g. during a match).
    if (this._draftCompleteUnsub) this._draftCompleteUnsub();
    this._draftCompleteUnsub = on('draft:complete', (data) => {
      if (this.state.draftEventId) {
        this.handleDraftComplete(data);
      }
    });

    // Hub-level match result. GameBoard owns the in-match tally; this only
    // catches the case where a settlement arrives while no board is open.
    // a returning dropper whose match settled (a win-by-default loss) while
    // they were offline, re-delivered by the server on reconnect. Show the
    // same reward tally on the hub so the result isn't lost.
    if (this._matchSettledUnsub) this._matchSettledUnsub();
    this._matchSettledUnsub = on('match:settled', (data) => {
      if (this.state.isGameBoardOpen) return;
      const envelope = data?.envelope;
      if (!envelope) return;
      this.setState({ hubMatchResult: { envelope } });
    });

    // Season rollover celebration. Fires once at rollover for top-10
    // finishers; the WS payload carries everything the overlay needs
    // ({ seasonId, rank, coins, voucher, points }). We don't try to
    // queue multiple in flight. The chance a player is mid-match
    // when two seasons resolve at once is effectively zero, and even
    // if it happens the rewards still land in the mailbox.
    if (this._seasonChampionUnsub) this._seasonChampionUnsub();
    this._seasonChampionUnsub = on('season:rollover:champion', (data) => {
      if (!data || typeof data.rank !== 'number') return;
      this.setState({ seasonChampionCelebration: data });
    });

    this.checkAssetDownload();
    this.checkTokenUpdates();
    this.deliverPatchNews();
  };

  deliverPatchNews = async () => {
    try {
      // state.updateStatus covers both platforms: the updater feeds it on
      // desktop, and web seeds a synthetic UP_TO_DATE with the baked build
      // version (there is no updateManager in the browser, so reading the
      // manager directly silently skipped patch news for every web player).
      const version = this.state.updateStatus?.currentVersion
        ?? this.updateManager?.getLastStatus?.()?.currentVersion;
      if (!version) return;
      await api.post('/profile/me/news', { version });
    } catch {}
  };

  // Trigger FirstRunDownload whenever ANY catalog slug is missing from
  // PERSISTENT_IMAGES_DIR. The card catalog is effectively static (one
  // new set per year) so the runtime invariant we want is: once the gate
  // finishes, no card image fetch ever leaves the device again. The
  // previous 90%-threshold heuristic let up to ~70 slugs lazy-fetch
  // from Vercel Blob during gameplay, which is multi-second slow for
  // players far from us-east-1.
  checkAssetDownload = async () => {
    // Web builds stream card art from the CDN on demand; there is no
    // local disk cache to populate, so the gate never mounts.
    if (isWeb()) return;
    try {
      const api = getLocalApiOrigin();
      const cardsRes = await fetch(`${api}/api/sorcery/cards`);
      const cards = await cardsRes.json();
      const expected = collectAllExpectedSlugs(cards);
      if (expected.length === 0) return;

      const missingRes = await fetch(`${api}/api/assets/check-missing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slugs: expected }),
      });
      const { missing } = await missingRes.json();
      if (Array.isArray(missing) && missing.length > 0) {
        this.setState({ needsAssetDownload: true, missingAssetSlugs: missing });
      }
    } catch (err) {
      console.warn('[assets] check-missing failed:', err?.message || err);
    }
  };

  handleAssetDownloadComplete = () => {
    this.setState({ needsAssetDownload: false, missingAssetSlugs: [] });
  };

  // After login, ask the cosmetics catalog which custom-token versions
  // are current and ask the local image cache which of those are
  // missing on disk. If any deltas exist, mount the TokenUpdateGate so
  // the player can't enter a match with stale art. Failures here are
  // silent. The gate is a nice-to-have, not a guard rail that should
  // ever block login itself.
  checkTokenUpdates = async () => {
    // Web: token art is fetched by URL with its ?v= tag, so a version
    // bump busts the CDN cache without any pre-download gate.
    if (isWeb()) return;
    try {
      const catalog = await getCosmeticsCached();
      // Two sources to verify: paid token cosmetics (kind: 'token' in
      // the packs list) and universal tokens shipped to every client.
      // Both follow the same slug+version contract, so they merge into
      // a single list of items to pre-fetch.
      const paidItems = (catalog?.packs || [])
        .filter((p) => p.kind === 'token' && p.tokenSlug && p.tokenVersion)
        .map((p) => ({ slug: p.tokenSlug, version: p.tokenVersion }));
      const universalItems = (catalog?.universalTokens || [])
        .filter((t) => t.tokenSlug && t.tokenVersion)
        .map((t) => ({ slug: t.tokenSlug, version: t.tokenVersion }));
      const items = [...paidItems, ...universalItems];
      if (items.length === 0) return;

      const api = getLocalApiOrigin();
      const res = await fetch(`${api}/api/assets/check-token-updates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => null);
      const missing = Array.isArray(data?.missing) ? data.missing : [];
      if (missing.length > 0) {
        this.setState({ pendingTokenUpdates: missing });
      }
    } catch (err) {
      console.warn('[token-updates] check failed:', err?.message || err);
    }
  };

  handleTokenUpdatesComplete = () => {
    this.setState({ pendingTokenUpdates: null });
  };

  addToast = (toastData) => {
    const duration = toastData.actions ? 8000 : 4000;
    const primaryAction = toastData.actions?.find(a => a.primary);
    const secondaryAction = toastData.actions?.find(a => !a.primary);

    toast(toastData.title, {
      description: toastData.message,
      duration,
      // Wax-seal medallion on the left edge. Avatar when the
      // notification is about a person, bell glyph otherwise. Typed
      // toasts (toast.success/error/…) get their seals from the
      // Toaster's icons config instead.
      icon: <HeraldAvatarSeal avatar={toastData.avatar} />,
      action: primaryAction ? {
        label: primaryAction.label,
        onClick: () => this.handleToastAction(null, primaryAction.key, toastData),
      } : undefined,
      cancel: secondaryAction ? {
        label: secondaryAction.label,
        onClick: () => this.handleToastAction(null, secondaryAction.key, toastData),
      } : undefined,
    });
  };


  handleToastAction = async (toastId, actionKey, toastData) => {
    if (actionKey === 'open-mailbox') {
      this.setState({ mailboxOpen: true });
    } else if (actionKey === 'open-notification') {
      this.openNotificationTarget(toastData?.notification);
    } else if (actionKey === 'accept-invite') {
      // Route through deck picker. The accept is sent after the
      // player selects a deck in confirmDeckAndQueue.
      this.refreshSavedDecks();
      this.setState({
        arenaView: 'deck-select',
        pendingInviteAcceptSenderId: toastData?.senderId,
      });
    } else if (actionKey === 'decline-invite') {
      try { friendsApi.declineMatchInvite(toastData?.senderId); } catch {}
    } else if (actionKey === 'allow-spectate' || actionKey === 'deny-spectate') {
      // Spectate protocol not yet implemented on the new server. No-op.
    } else if (actionKey === 'open-play') {
      // Used by the "Resume requested" toast (Task 6.3). Jumps
      // straight to SessionBrowser so the host can hit Resume on
      // the row the guest is waiting on.
      this.setState({ arenaView: 'play' });
    }
  };

  handleInviteAccepted = (result, { isHost = false } = {}) => {
    this.refreshSavedDecks();
    this.setState({
      arenaView: 'deck-select',
      pendingInviteRoomCode: result.roomCode,
      isInviteMatch: true,
      isInviteHost: isHost,
      // Session automation mode from the server's room (the inviter chose it;
      // invite matches default to manual). Pre-sync fallback only. The
      // MatchState fold is the authority once the board mounts.
      currentAutomations: result.automations !== false,
    });
  };

  handleSpectateAllowed = (roomCode) => {
    this.setState({
      isSpectating: true,
      isGameBoardOpen: true,
      spectateRoomCode: roomCode,
    });
  };

  handleLeaveSpectate = () => {
    // If we're still in a draft event, route back to the current draft
    // phase rather than dumping the user to the hub. During an active
    // tournament that's the bracket view; after the whole tournament
    // completes (draft:complete flipped draftPhase to 'results'), it's
    // the leaderboard/results view.
    const draftView = this.state.draftEventId
      ? (this.state.draftPhase === 'results' ? 'draft-results'
        : this.state.draftPhase === 'tournament' ? 'draft-tournament'
        : null)
      : null;
    this.setState({
      isSpectating: false,
      isGameBoardOpen: false,
      spectateRoomCode: null,
      sessionMode: null,
      roomCode: null,
      isArenaMatch: false,
      isCasualSandbox: false,
      // Mirror the standard GameBoard exit teardown so the next match
      // mount can't accidentally inherit spectate-time props.
      currentDraftMatchId: null,
      currentInstanceId: null,
      currentInstanceIsHost: false,
      currentInstanceVisibility: null,
      currentInstanceMode: null,
      isResumeJoin: false,
      ...(draftView ? { arenaView: draftView } : {}),
    });
  };

  handleTradeAccepted = (result, partnerName) => {
    this.setState({
      tradeActive: true,
      tradeRoomCode: result.roomCode,
      tradePartnerName: partnerName,
    });
  };

  handleTradeOfferChanged = (offer) => {
    // Will sync via PeerJS in future
  };

  handleTradeLockIn = () => {
    // Will sync via PeerJS in future
  };

  handleTradeConfirm = async (myOffer, theirOffer) => {
    // Trading is not implemented on the new server yet. Close the panel so
    // the UI stays functional until the protocol is reintroduced.
    this.addToast({ title: t('Trade Unavailable'), message: t('Trading is temporarily disabled.') });
    this.setState({ tradeActive: false, tradePartnerName: null, tradeRoomCode: null });
  };

  handleTradeCancel = () => {
    this.setState({ tradeActive: false, tradePartnerName: null, tradeRoomCode: null });
  };

  handleFriendInvite = (friendId) => {
    // If this invite is for an instance we just created (host is
    // already committed + picked their deck), skip the deck-picker
    // detour and just send the chat invite with the instance id.
    // The recipient's accept handler picks a deck and joins the
    // instance directly.
    const pending = this.state.pendingInstanceForInvite;
    if (pending?.instance) {
      const inst = pending.instance;
      // Record a server-side invitation. It surfaces in the friend's
      // "Queue & Invites" menu via GET /instances/invited. No chat
      // message. If the host later starts a different match this
      // lobby is abandoned and the invite drops out of their menu on
      // its own (phase filter), so there's nothing stale to clean up.
      apiInviteToInstance(inst.id, friendId, 'join').catch((err) => {
        console.warn('[instance] invite send failed:', err);
        toast.error(t('Invite failed. Try again.'));
      });
      toast.info(t('Invite sent.'));
      this.setState({ friendInvitePickerOpen: false, pendingInstanceForInvite: null });
      return;
    }
    // Route through deck picker first. The invite is sent after the
    // player selects a deck in confirmDeckAndQueue. Close the mailbox
    // too so the deck picker isn't buried under the social overlay
    // + call dock (which both sit above the deck-select view's
    // z-index).
    this.refreshSavedDecks();
    this.setState({
      arenaView: 'deck-select',
      pendingInviteFriendId: friendId,
      friendsOpen: false,
      friendInvitePickerOpen: false,
      viewingFriendProfile: null,
      mailboxOpen: false,
    });
  };

  handleAcceptMatchInviteFromChat = async (senderId, msg) => {
    // Close mailbox/sidebar, route through deck picker. If the
    // chat message carries an instanceId (unified instance system),
    // route the deck-pick into apiJoinInstance instead of the
    // legacy friendsApi.acceptMatchInvite path.
    this.refreshSavedDecks();
    const instanceId = msg?.metadata?.instanceId;
    const isResume = !!msg?.metadata?.resume;
    if (instanceId && isResume) {
      // Resume-invite: the guest is already in the participants
      // list and already picked a deck during the original
      // session, so there's no deck-select detour. The accept
      // click here is what actually starts the ready-check.
      // /resume-start broadcasts readyCheckStarted to both sides,
      // and both ReadyCheckModals then open in lockstep. Doing
      // that on the host's Resume click would force the guest
      // into a modal for a match they haven't acknowledged yet.
      this.setState({ mailboxOpen: false, friendsOpen: false });
      try {
        const fresh = await apiGetInstance(instanceId);
        if (!fresh) {
          toast.error(t('That match is no longer available.'));
          return;
        }
        // Ensure the WS subscription is attached BEFORE resumeStart,
        // otherwise the server's readyCheckStarted broadcast fires
        // into a listener that doesn't exist yet (happens because
        // an accepting guest typically hasn't subscribed to this
        // instance at boot. It wasn't in listInstances on page load).
        try { await this._ensureInstanceSubs(); } catch {}
        const { resumeStart: apiResumeStart } = await import('./utils/arena/instancesApi');
        await apiResumeStart(fresh.id);
        // The server sets the ready-check deadline on resume-start.
        // Re-fetch and reconcile so the modal opens from authoritative
        // state rather than an optimistic local guess.
        const afterStart = await apiGetInstance(fresh.id).catch(() => fresh);
        this.reconcileInstance(afterStart);
      } catch (err) {
        toast.error(err.message || t('Could not open match.'));
      }
      return;
    }
    if (instanceId) {
      const inviteMode = msg?.metadata?.mode;
      this.setState({
        arenaView: 'deck-select',
        joiningInstanceId: instanceId,
        pendingQueueMode: inviteMode === 'sandbox' ? 'sandbox' : 'ranked',
        pendingQueueFormat: inviteMode === 'match' ? (msg?.metadata?.format || 'constructed') : null,
        mailboxOpen: false,
        friendsOpen: false,
      });
      return;
    }
    this.setState({
      arenaView: 'deck-select',
      pendingInviteAcceptSenderId: senderId,
      mailboxOpen: false,
      friendsOpen: false,
    });
  };

  handleDeclineMatchInviteFromChat = (senderId) => {
    try { friendsApi.declineMatchInvite(senderId); } catch {}
  };

  handleViewFriendProfile = (profileId) => {
    this.setState({ viewingFriendProfile: profileId });
  };

  // Top-player click in the Hall of Fame routes here. Closes the HoF
  // overlay so subsequent navigation (e.g. opening a published deck
  // from the profile) doesn't end up rendering behind the HoF panel,
  // and sets profileReturnToHallOfFame so the eventual profile close
  // restores the HoF instead of dumping the user on the hub.
  handleViewFriendProfileFromHallOfFame = (profileId) => {
    this.setState({
      viewingFriendProfile: profileId,
      hallOfFameOpen: false,
      profileReturnToHallOfFame: true,
    });
  };

  handleCloseFriendProfile = () => {
    this.setState((s) => ({
      viewingFriendProfile: null,
      profileReturnToHallOfFame: false,
      hallOfFameOpen: s.profileReturnToHallOfFame ? true : s.hallOfFameOpen,
    }));
  };

  handleNewNotifications = (notifications) => {
    if (notifications.some((n) => !OWN_CUE_NOTIFICATION_TYPES.has(n.type) && notificationSoundEnabled(n.type))) {
      playUI(UI.NOTIFICATION_SOFT);
    }
    for (const n of notifications) {
      if (n.type === 'friend-request') {
        const senderId = n.senderId;
        const senderName = n.senderName || t('A player');
        const avatarUrl = this.getArenaAvatarUrl(n.senderAvatar);
        const resolveRequest = async (action) => {
          try {
            await action(senderId);
          } catch (err) {
            // 404 means the request was already handled (auto-accepted via
            // a reciprocal Add, accepted from another device, or declined
            // elsewhere). Anything else is worth logging.
            const msg = err?.message || '';
            if (!/not found/i.test(msg) && !/404/.test(msg)) {
              console.warn('[friend-request toast] action failed:', msg);
            }
          }
          // The server only emits friend:accepted to the OTHER player, so
          // the acceptor never gets a WS event. Pull the authoritative
          // friend list to clear the pending row from the sidebar.
          await refreshFriendList().catch(() => {});
        };
        toast.custom((id) => (
          <FriendRequestToast
            toastId={id}
            senderName={senderName}
            avatarUrl={avatarUrl}
            onAccept={() => resolveRequest(friendsApi.acceptFriendRequest)}
            onDecline={() => resolveRequest(friendsApi.declineFriendRequest)}
          />
        ), { duration: 10000 });
      } else if (n.type === 'friend-accepted') {
        this.addToast({
          title: t('Friend Added'),
          message: t('{name} accepted your friend request', { name: n.name }),
          avatar: n.avatar,
        });
      } else if (n.type === 'match-invite') {
        // Show a notification that opens the chat. The actual Accept/
        // Decline buttons are rendered inline in the ChatView message.
        ringBell();
        this.addToast({
          title: t('Match Invite'),
          message: t('{name} invited you to a match', { name: n.senderName }),
          senderId: n.senderId,
        });
        // Store the sender ID so ChatView can show accept/decline
        this._pendingMatchInviteSenderId = n.senderId;
      } else if (n.type === 'invite-accepted') {
        // Both players already chose their decks. Show the friend-mode
        // prep screen ("Preparing match with X"), then transition to the
        // game board after a delay. This is a private friend match. No rank
        // readout, no "Opponent Found" language, since both players know
        // who they're playing. No sound either: the summons rang when the
        // invite arrived, and this is that invite playing out.
        const opp = n.opponent || {};
        const oppName = opp.name || n.name || n.senderName || t('Friend');
        const opponentAvatarUrl = this.getArenaAvatarUrl(opp.profileAvatar);
        this._matchRewardApplied = false;
        this.setState({
          arenaMatchmaking: true,
          arenaView: 'matchmaking',
          arenaMatchmakingMode: 'friend',
          arenaMatchmakingOpponent: { ...opp, name: oppName, avatarUrl: opponentAvatarUrl },
        });
        const delay = n.isHost ? 1500 : 4000;
        setTimeout(() => {
          this.setState({
            arenaMatchmaking: false,
            arenaView: 'hub',
            isGameBoardOpen: true,
            isArenaMatch: true,
            isRankedMatch: false,
            sessionMode: n.isHost ? 'new' : 'join',
            roomCode: n.roomCode,
            // Session automation mode from the server's room (the inviter
            // chose it; invite matches default to manual). Pre-sync fallback
            // only. The MatchState fold is the authority after mount.
            currentAutomations: n.automations !== false,
          });
        }, delay);
      } else if (n.type === 'draft-player-disconnected') {
        const secs = Math.round((n.gracePeriodMs || 120000) / 1000);
        this.addToast({
          title: t('Player Disconnected'),
          message: t('{name} lost connection. They have {seconds}s to reconnect or the draft will be cancelled.', { name: n.playerName, seconds: secs }),
        });
      } else if (n.type === 'draft-player-reconnected') {
        this.addToast({
          title: t('Player Reconnected'),
          message: t('{name} is back!', { name: n.playerName }),
        });
      } else if (n.type === 'draft-aborted') {
        this.addToast({
          title: t('Draft Cancelled'),
          message: t('The draft has been cancelled. Your entry has been refunded.'),
        });
        // Only clear draft state and navigate when the cancel is for the
        // draft this client is actually in (mirrors the guard in
        // handleDraftEventCancelled). A broadcast for some other event,
        // e.g. an old one getting swept, must not eject the player from
        // their current tournament.
        if (n.eventId && n.eventId === this.state.draftEventId) {
          this.setState({
            draftEventId: null,
            draftPhase: null,
            arenaView: 'hub',
          });
        }
      } else if (n.type === 'matchmaking-matched') {
        clearInterval(this.arenaQueuePollTimer);
        this.arenaQueuePollTimer = null;
        const opp = n.opponent || {};
        const opponentAvatarUrl = this.getArenaAvatarUrl(opp.profileAvatar);
        const matchMode = n.mode || 'ranked';
        this.setState({
          arenaMatchmakingOpponent: { ...opp, name: opp.name || opp.username || opp.displayName || t('Opponent'), avatarUrl: opponentAvatarUrl },
          arenaMatchmakingMode: matchMode,
          arenaMatchId: n.roomCode,
        });
        ringBell();
        const delay = n.isHost ? 1500 : 4000;
        setTimeout(() => {
          this.setState({
            arenaMatchmaking: false,
            arenaView: 'hub',
            isGameBoardOpen: true,
            isArenaMatch: true,
            // Only ranked matches affect LP / rank; sandbox is still
            // an arena match structurally (online, server-paired) but
            // not ranked.
            isRankedMatch: matchMode === 'ranked',
            isSandboxMatch: matchMode === 'sandbox',
            sessionMode: n.isHost ? 'new' : 'join',
            roomCode: n.roomCode,
          });
        }, delay);
      } else if (n.type === 'spectate-request') {
        this.addToast({
          title: t('Spectate Request'),
          message: t('{name} wants to watch your match', { name: n.spectatorName }),
          spectatorId: n.spectatorId,
          actions: [
            { label: t('Allow'), key: 'allow-spectate', primary: true },
            { label: t('Deny'), key: 'deny-spectate' },
          ],
        });
      }
      if (n.type === 'new-mail') {
        // The mail.received notification carries the toast now; this
        // path only keeps the mail chime.
        playUI(UI.MAIL_RECEIVE);
      }
    }
  };

  handleRejoinMatch = (roomCode, isArenaMatch) => {
    if (!roomCode || this.state.isGameBoardOpen) return;
    this.setState({
      mailboxOpen: false,
      friendsOpen: false,
      isGameBoardOpen: true,
      isArenaMatch: isArenaMatch || false,
      isRankedMatch: false,
      sessionMode: 'join',
      roomCode,
    });
  };

  handleSessionActive = (data) => {
    if (!data?.roomCode) return;
    // Server confirmed a live session. Cancel the delayed cleanup
    clearTimeout(this._sessionCleanupTimer);
    // Already in a game. Ignore (the existing auto-reconnect handles it)
    if (this.state.isGameBoardOpen) return;

    // Draft/sealed tournament matches have ONE canonical re-entry path: the
    // tournament dashboard → ready-check → match_start, which restores from
    // the durable per-match save blob (keyed by matchId). The generic
    // session:active auto-reconnect can't restore them. Its roomCode-keyed
    // server cache is wiped on a server restart, so it fresh-spawns over the
    // match (blank board + re-rolled initiative). Two reconnect paths for the
    // same thing is exactly the redundancy that caused that bug, so for a
    // draft match we DON'T auto-mount here. We route to the tournament
    // screen and let the single path handle restore.
    if (data.draftMatchId || data.draftEventId) {
      const eventId = data.draftEventId;
      if (eventId) {
        this.setState({ draftEventId: eventId, draftPhase: 'tournament', arenaView: 'draft-tournament' });
        try { this._startDraftEventWatcher(eventId); } catch {}
        import('./utils/arena/draftApi')
          .then(({ getDraftEvent }) => getDraftEvent(eventId))
          .then((ev) => { if (ev?.mode) this.setState({ draftEventMode: ev.mode }); })
          .catch(() => {});
      }
      return;
    }

    // Store to localStorage for mailbox "Rejoin" button
    try {
      localStorage.setItem('valkenhall-active-session', JSON.stringify({
        roomCode: data.roomCode,
        isHost: data.isHost,
        isArenaMatch: data.isArenaMatch || false,
        startedAt: Date.now(),
      }));
    } catch {}

    // Solo (one pilot, both seats): remount with the SAME shape as
    // _launchInstanceHost's solo mount. isCasualSandbox drives the acting-seat
    // provider + dual-seat renderer; instanceId keys the durable match channel.
    // Without these the reload remounted a solo match as a regular online game
    // pinned to seat p1. Pass turn silently gated and every action during
    // p2's turn rejected 'authz' by the server.
    if (data.solo) {
      this.setState({
        isGameBoardOpen: true,
        isArenaMatch: true,
        isRankedMatch: false,
        isCasualSandbox: true,
        isSandboxMatch: data.mode === 'sandbox',
        currentInstanceMode: data.mode || null,
        currentInstanceId: data.instanceId || null,
        currentInstanceIsHost: true,
        currentInstanceVisibility: 'solo',
        sessionMode: 'new',
        sessionId: null,
        roomCode: data.roomCode,
        arenaMatchmakingOpponent: null,
      });
      return;
    }

    // Resolve opponent display info from the server payload so the life
    // HUD and logbook still show a name + avatar after an app restart.
    // Without this, arenaMatchmakingOpponent stays null and those UIs
    // fall back to "Opponent" with no image.
    const opp = data.opponent;
    const opponentInfo = opp ? {
      ...opp,
      name: opp.name || t('Opponent'),
      avatarUrl: this.getArenaAvatarUrl(opp.profileAvatar),
    } : null;

    // Auto-rejoin: open GameBoard immediately
    this.setState({
      isGameBoardOpen: true,
      isArenaMatch: data.isArenaMatch || false,
      isRankedMatch: data.mode === 'ranked',
      // Restore the match mode authoritatively from the server payload. A
      // reconnect must not silently flip an ownership-ignoring sandbox match to
      // ownership-respecting (that emptied the Dragonlord set-aside pool + any
      // owned-pool gate after a reload).
      isSandboxMatch: data.mode === 'sandbox',
      currentInstanceMode: data.mode || null,
      sessionMode: 'join',
      roomCode: data.roomCode,
      arenaMatchmakingOpponent: opponentInfo,
      // NB: deliberately do NOT set currentDraftMatchId/isResumeJoin here.
      // Doing so routes the reconnect restore through the draft-round-resume
      // block, which loads the ~60s autoSave blob (often stale, captured
      // mid-mulligan) instead of the fresh roomCode-keyed scheduleLocalSave
      // state. The roomCode path (roomInfo.resumed → restoreLocalGameState)
      // restores hand + mulligan + board from the 3s-fresh snapshot, which is
      // what we want for a connection hiccup. (isArenaMatch is still reported
      // so the End Match control stays visible.)
    });

    // Backfill any fields that the session:active payload doesn't carry
    // (most notably discordHandle). Fetching the canonical public
    // profile guarantees the HUD has everything the profile supports,
    // so the Discord badge still shows after a reconnect even if the
    // session payload is stale or trimmed.
    if (opp?.id) {
      import('./utils/friendsApi').then(({ getPublicProfile }) => getPublicProfile(opp.id))
        .then((profile) => {
          if (!profile) return;
          this.setState((s) => s.arenaMatchmakingOpponent && s.arenaMatchmakingOpponent.id === opp.id
            ? {
                arenaMatchmakingOpponent: {
                  ...s.arenaMatchmakingOpponent,
                  discordHandle: profile.discordHandle ?? s.arenaMatchmakingOpponent.discordHandle ?? null,
                },
              }
            : null);
        })
        .catch(() => {});
    }
  };

  // The GameBoard's exit path: tear down match state, park the server-side
  // instance as paused when we know it, and route back to the right screen.
  // Extracted from the inline onExit closure so the stuck-loading escape
  // hatch (handleAbandonStuckLoad) can share it.
  handleGameBoardExit = () => {
    this.gameBoardRef = null;
    const returningToArena = this.state.isArenaMatch;
    // If we're still inside a draft event, route back to the
    // phase we're in rather than dumping the user to the hub.
    // Mid-tournament → bracket view; after the final match the
    // server's draft:complete event has already flipped
    // draftPhase to 'results', so route to the leaderboard.
    const draftView = this.state.draftEventId
      ? (this.state.draftPhase === 'results' ? 'draft-results'
        : this.state.draftPhase === 'tournament' ? 'draft-tournament'
        : null)
      : null;
    // When arena-match exit isn't into a draft phase, route
    // via arenaBackTarget so Solo (which launched from the
    // Play mode picker) lands back on the picker rather
    // than the main hub.
    const arenaDestination = returningToArena && !draftView
      ? (this.state.arenaBackTarget || 'hub')
      : null;
    stopMusic(2000);
    try { localStorage.removeItem('valkenhall-active-session'); } catch {}
    // If the user force-exited a multiplayer instance
    // mid-match (no winner confirmation, no completion
    // flow fired), park the instance as paused on the
    // server so the save list shows a useful state
    // instead of "In progress" forever. handleMatchReward
    // already set _completedInstanceId when the match
    // ended gracefully; the server's /pause endpoint is
    // idempotent against completed/abandoned terminal
    // states so double-firing is safe.
    const exitingInstanceId = this.state.currentInstanceId;
    const exitingVisibility = this.state.currentInstanceVisibility;
    const exitingWasCompleted = this._completedInstanceId === exitingInstanceId;
    this._completedInstanceId = null;
    this._launchingInstanceId = null;
    // Solo included: leaving the board parks the server-side match
    // as paused so the save list shows a resumable row (the room's
    // disconnect grace would do it eventually; this is immediate
    // and also stops the reconcile heartbeat from re-launching an
    // instance still flagged active).
    if (exitingInstanceId && exitingVisibility && !exitingWasCompleted) {
      apiPauseInstance(exitingInstanceId).catch((err) => {
        console.warn('[instance] pauseInstance on exit failed:', err?.message || err);
      });
    }
    // A replay exiting through the board's own exit path (escape menu) must
    // tear down its driver too, or the playback timer outlives the board and
    // the non-null replaySession wedges every future Watch.
    this.state.replaySession?.driver?.destroy();
    this.setState({
      isGameBoardOpen: false, sessionMode: null, sessionId: null, roomCode: null,
      isArenaMatch: false, isRankedMatch: false, isCasualSandbox: false, isSpectating: false, spectateRoomCode: null,
      replaySession: null,
      arenaMatchmakingOpponent: null,
      currentInstanceId: null,
      currentInstanceIsHost: false,
      currentInstanceVisibility: null,
      currentInstanceMode: null,
      isResumeJoin: false,
      currentDraftMatchId: null,
      // Reset the MatchAssetGate state so the next match
      // doesn't inherit the previous opponent's preload or
      // the previous ready flag.
      opponentDeckForPreload: null,
      matchAssetsReady: false,
      ...(draftView
        ? { arenaView: draftView }
        : arenaDestination
          ? { arenaView: arenaDestination, arenaBackTarget: 'hub' }
          : {}),
    });
    this._ensureInstanceSubs();
  };

  // Escape hatch from a stuck MatchAssetGate overlay: a crash during match
  // creation leaves a part-created match that can never finish loading (no
  // roomInfo decks, no snapshot), and session:active re-mounts it on every
  // launch with no way out. Release the server-side lock FIRST, then run the
  // standard exit. Pause, not forfeit: the match lands in the saved-games
  // list. Awaiting the release while the board is still open closes the
  // relaunch race (the reconcile heartbeat won't act while a board is open).
  handleAbandonStuckLoad = async () => {
    const roomCode = this.state.roomCode || null;
    try {
      if (this.state.currentDraftMatchId) {
        // Draft round rooms are owned by the tournament flow: just leave
        // the room (the server keeps draft rooms alive for rejoin) and let
        // the dashboard be the re-entry point.
        wsSend('room:leave', { roomCode });
      } else {
        await releaseStuckMatch({
          roomCode,
          instanceId: this.state.currentInstanceId || null,
        });
      }
    } catch (err) {
      console.warn('[app] stuck-match release failed:', err?.message || err);
    }
    this.handleGameBoardExit();
  };

  handleDraftActive = (data) => {
    if (!data?.eventId) return;
    if (this.state.draftEventId === data.eventId) return;
    // Only auto-open the time-sensitive PICKING phase (pick-and-pass. Others
    // are waiting on you). For the async phases (building / tournament) do NOT
    // force the screen open on every reload; the player returns to those via the
    // "Your Tournaments" tab when they choose to.
    if (data.status !== 'drafting') return;
    this.openDraftLobby(data.eventId);
  };

  getArenaAvatarUrl = (cardId) => {
    if (!cardId || !this.state.sorceryCards) return null;
    const card = this.state.sorceryCards.find((c) => c.unique_id === cardId);
    return card?.printings?.[0]?.image_url || null;
  };

  // Resolve a friend's display info from the cached friend list so the
  // friend-invite matchmaking screen can show "Preparing match with X"
  // the moment the user clicks invite. No need to wait for the server
  // to echo invite:accepted first.
  getFriendDisplayInfo = (friendId) => {
    if (!friendId) return null;
    const friend = (this.state.friendListData?.friends || []).find((f) => f.id === friendId);
    if (!friend) return { id: friendId, name: t('Friend'), avatarUrl: null };
    return {
      id: friend.id,
      name: friend.name || t('Friend'),
      profileAvatar: friend.profileAvatar || null,
      avatarUrl: this.getArenaAvatarUrl(friend.profileAvatar),
    };
  };

  // Find the opponent participant and resolve them to display info
  // suitable for arenaMatchmakingOpponent (name + avatar). Returns
  // immediately with cached friend data if available, then fires off
  // a public-profile fetch to backfill so avatar + Discord handle
  // populate even for non-friends. Without this the HUD on a resumed
  // match shows "P2 / OPP" instead of the opponent's name + avatar.
  _resolveInstanceOpponent = (instance) => {
    if (!instance) return null;
    const myId = this.state.arenaProfile?.id;
    const opponentPart = (instance.participants || []).find(
      (p) => p.userId && p.userId !== myId,
    );
    if (!opponentPart?.userId) return null;
    const friend = (this.state.friendListData?.friends || [])
      .find((f) => f.id === opponentPart.userId);
    const seed = friend ? {
      id: friend.id,
      name: friend.name || t('Opponent'),
      profileAvatar: friend.profileAvatar || null,
      avatarUrl: this.getArenaAvatarUrl(friend.profileAvatar),
    } : {
      id: opponentPart.userId,
      name: t('Opponent'),
      profileAvatar: null,
      avatarUrl: null,
    };
    import('./utils/friendsApi')
      .then(({ getPublicProfile }) => getPublicProfile(opponentPart.userId))
      .then((profile) => {
        if (!profile) return;
        this.setState((s) => {
          const cur = s.arenaMatchmakingOpponent;
          if (!cur || cur.id !== opponentPart.userId) return null;
          return {
            arenaMatchmakingOpponent: {
              ...cur,
              name: profile.name || profile.username || cur.name,
              profileAvatar: profile.profileAvatar || cur.profileAvatar,
              avatarUrl: profile.profileAvatar
                ? this.getArenaAvatarUrl(profile.profileAvatar)
                : cur.avatarUrl,
              discordHandle: profile.discordHandle ?? cur.discordHandle ?? null,
            },
          };
        });
      })
      .catch(() => {});
    return seed;
  };

  getArenaSavedDecks = () => {
    // Server is the source of truth for decks. Use savedDecks directly.
    return (this.state.savedDecks || []).map((d) => ({
      id: d.id,
      name: d.name,
      cards: d.cards || [],
      cardCount: d.cardCount ?? d.cards?.length ?? 0,
      previewUrl: d.previewUrl || null,
      savedAt: d.savedAt || d.updatedAt || null,
      format: d.format || 'constructed',
      gameMode: d.gameMode || 'constructed',
      shared: d.shared || false,
      likes: d.likes || 0,
      showcase: d.showcase || null,
      favorite: !!d.favorite,
      tags: Array.isArray(d.tags) ? d.tags : [],
    }));
  };

  getArenaDecksForGameBoard = () => {
    const decks = this.state.savedDecks || [];
    return decks.map((d) => ({
      id: d.id,
      name: d.name,
      cards: d.cards || [],
      cardCount: d.cardCount ?? d.cards?.length ?? 0,
      format: 'constructed',
      previewUrl: d.previewUrl || null,
    }));
  };

  // Deck whose card art the MatchAssetGate must verify before the
  // GameBoard mounts. For arena matchmaking we know the player's deck
  // up-front via arenaSelectedDeckId. For other entry paths (loaded
  // sandbox, fresh sandbox via picker, spectator) we have no deck at
  // mount time. The gate falls back to preloading just the global
  // assets (cardbacks, playmat, music). The opponent's deck arrives
  // via the sync bridge and is handled inside GameBoard's connection
  // flow; that's a separate gate we can layer later.
  _getPreloadDeck = () => {
    if (this.state.isArenaMatch && this.state.arenaSelectedDeckId) {
      const decks = this.state.savedDecks || [];
      return decks.find((d) => d.id === this.state.arenaSelectedDeckId) || null;
    }
    return null;
  };

  // Whether MatchAssetGate should hold its overlay until the opponent's
  // deck has also been preloaded. True for any session with a remote
  // peer (ranked matchmaking, friend invites, sandbox multiplayer);
  // false for solo and spectator paths so phase 2 short-circuits.
  // Spectators see two unknown decks via state:sync. Gating both
  // sides is a separate iteration; for now they fall back to the
  // existing lazy-load behaviour.
  _matchNeedsOpponentPreload = () => {
    if (this.state.isSpectating) return false;
    // R2b. True hidden info: in auth mode the opponent's deck contents never
    // reach the client, so there is no opponent card art to preload. The
    // opponent's piles render as identity-free face-down placeholder stacks.
    // Gating phase 2 on an opponent deck that will never arrive (we bypass
    // _queueOpponentSpawn / onOpponentDeckResolved) would hang the gate forever.
    if (!this.state.isCasualSandbox && isAuthMatchEnabled()) return false;
    return !!this.state.roomCode;
  };

  // Match-music URL the gate should prefetch. Null when no fresh
  // track will start (custom-music suppressor active, volume zero, or
  // the same MP3 is already playing because the user came from the
  // hub which uses the same audio file). Returning null skips audio
  // prefetch entirely so we don't fight a track that's already loaded.
  _getMatchMusicUrl = () => {
    if (isMusicSuppressed()) return null;
    if (getEffectiveMusicVolume() <= 0) return null;
    if (getCurrentTrack() === 'arena-hub' || getCurrentTrack() === 'arena-match') return null;
    return '/game-assets/snd-arena-hub-music.mp3';
  };

  updateArenaName = async (name) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const updatedProfile = { ...arenaProfile, name };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save profile:', e));
  };

  updateArenaAvatar = async (cardId) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const updatedProfile = { ...arenaProfile, profileAvatar: cardId };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save profile:', e));
  };

  // Save the user-edited personality fields (bio + pronouns).
  // customStatus is no longer user-edited. It's the auto-derived
  // activity label managed by presenceManager.updateActivity, so it's
  // intentionally absent here. Optimistic update so the mini-profile
  // reflects the new values immediately even if the server write is slow.
  updateArenaPersonality = async (updates) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const updatedProfile = {
      ...arenaProfile,
      bio: updates.bio ?? arenaProfile.bio ?? '',
      pronouns: updates.pronouns ?? arenaProfile.pronouns ?? '',
    };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save personality:', e));
  };

  // Presence (online / idle / dnd / invisible) + optional expiry
  // timestamp. Server is expected to revert to online once
  // Date.now() >= presenceExpiresAt; for now the client stores both
  // and applies optimistically so the avatar dot reflects the
  // choice immediately.
  updateArenaPresence = async ({ status, expiresAt }) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const updatedProfile = {
      ...arenaProfile,
      presenceStatus: status,
      presenceExpiresAt: expiresAt,
    };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save presence:', e));
  };

  // Which Hall of Fame crown the player has chosen to display under
  // their name. NULL falls back to highest-wins at render time. The
  // server stores the choice without ownership validation. Clients
  // hide the title silently if the player no longer holds the crown.
  updateDisplayedCrown = async (avatarId) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const updatedProfile = {
      ...arenaProfile,
      displayedCrownAvatarId: avatarId,
    };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save displayed crown:', e));
  };

  // Badges the player has chosen to pin to their profile header (the
  // front-of-profile strip). Anything beyond the pinned list lives in
  // the read-only Badges modal. Up to 3 IDs; the client enforces the
  // cap and a stable order.
  updatePinnedBadges = async (badgeIds) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const sanitized = Array.isArray(badgeIds)
      ? badgeIds.filter((id) => typeof id === 'string').slice(0, 3)
      : [];
    const updatedProfile = {
      ...arenaProfile,
      pinnedBadgeIds: sanitized,
    };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save pinned badges:', e));
  };

  // Up to 8 cards the player has pinned to their profile showcase.
  // Each entry is `{ cardId, foiling }`. Server stores as-is; clients
  // cross-check against the live collection at render time, so a
  // sold-off card silently drops out. Cap mirrors SHOWCASE_CAP in
  // FriendProfileOverlay.
  // Card whose art is used as the player's profile hero backdrop.
  // NULL falls back to the profile avatar (the legacy behaviour).
  updateProfileBackground = async (cardId) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const updatedProfile = { ...arenaProfile, backgroundCardId: cardId || null };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save profile background:', e));
  };

  updateShowcaseCards = async (cards) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    const sanitized = Array.isArray(cards)
      ? cards.filter((c) => c?.cardId).slice(0, 8).map((c) => ({ cardId: c.cardId, foiling: c.foiling || 'S' }))
      : [];
    const updatedProfile = { ...arenaProfile, showcaseCards: sanitized };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save showcase:', e));
  };

  handleLogout = async () => {
    stopPresence();
    stopNotifications();
    stopMusic(0);
    // Wipe the Moments feed so the next user signing in on this
    // device doesn't inherit the previous user's buckets.
    momentsManager.clear().catch((err) => console.error('[moments] clear:', err));
    // Drop the instance subscriptions with the session. Left in place, the
    // ready-check heartbeat kept listing instances without a token.
    for (const off of this._instanceSubs.values()) { try { off(); } catch {} }
    this._instanceSubs.clear();
    await clearStoredToken();
    this.setState({
      loggedIn: false,
      authChecking: false,
      arenaProfile: null,
      arenaLoading: false,
      settingsOpen: false,
      isGameBoardOpen: false,
      sessionMode: null,
      friendListData: null,
      friendsSidebarOpen: false,
      globalChatMentions: 0,
      focusGlobalMessageId: null,
      // Bumped to land the social hub on its Notifications tab.
      mailboxFocusNotifications: null,
      mailboxUnreadByTab: { friend: 0, auction: 0, news: 0 },
      // Matches waiting on the player + unread activity, from notificationsStore.
      notificationsCount: 0,
    });
  };

  hasValidUsername = () => {
    return validateUsername(this.state.arenaProfile?.name).ok;
  };

  registerArenaUsername = async (username) => {
    const { arenaProfile } = this.state;
    const updatedProfile = {
      ...arenaProfile,
      name: username,
    };
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile).catch((e) => console.error('Failed to save profile:', e));
  };

  // First-launch "Connect on Discord" nudge. Shown once after the
  // username prompt for any player who hasn't set a Discord handle.
  // Skipping or saving both mark the prompt as shown via localStorage
  // so it never reappears for this install.
  shouldShowDiscordPrompt = () => {
    const profile = this.state.arenaProfile;
    if (!profile?.id) return false;
    if (profile.discordHandle) return false;
    return !wasDiscordPromptShown(profile.id);
  };

  saveArenaDiscordHandle = async (handle) => {
    const { arenaProfile } = this.state;
    const updatedProfile = { ...arenaProfile, discordHandle: handle };
    // Mark the prompt as resolved BEFORE the network call so a flaky
    // connection still dismisses the modal. The handle is already in
    // local state, and the server PUT will retry on the next profile
    // save. The alternative (mark only on success) risks looping the
    // user back through the prompt every launch until the server's up.
    markDiscordPromptShown(arenaProfile.id);
    this.setState({ arenaProfile: updatedProfile });
    await saveArenaProfile(updatedProfile);
  };

  skipArenaDiscordPrompt = () => {
    const profile = this.state.arenaProfile;
    if (profile?.id) markDiscordPromptShown(profile.id);
    this.forceUpdate();
  };

  // Onboarding free-playmat pick succeeded (server already granted it +
  // flipped the gate). Optimistically flip the local flag so the screen
  // advances even if the follow-up refresh is slow, then reconcile with
  // the authoritative profile in the background.
  handleStarterPlaymatChosen = async () => {
    const { arenaProfile } = this.state;
    this.setState({ arenaProfile: { ...arenaProfile, starterPlaymatClaimed: true } });
    try {
      const token = await getStoredToken();
      if (token) {
        const fresh = await loadArenaProfile(token).catch(() => null);
        if (fresh) this.setState({ arenaProfile: this.profileFromServer(fresh, token) });
      }
    } catch (_) {}
  };

  confirmDeckAndQueue = async (deckId) => {
    const {
      arenaProfile, isInviteMatch, pendingInviteRoomCode,
      pendingInviteFriendId, pendingInviteAcceptSenderId,
      pendingInstanceCreate, joiningPublicInstance, joiningInstanceId,
    } = this.state;
    if (!arenaProfile?.serverToken) return;

    // ── Instance: create path (solo / friends / public). Deck is
    //    attached to the creator's participant row via the POST
    //    body so it lands atomically.
    if (pendingInstanceCreate) {
      const { visibility, modeKey, cfg } = pendingInstanceCreate;

      // DEV quick match (local server): one POST creates the private match
      // vs the other connected test account, readies both seats and flips it
      // active. Both boards launch off the reconcile, no further clicks.
      if (visibility === 'devquick') {
        this.setState({ pendingInstanceCreate: null, arenaView: 'hub' });
        try {
          const inst = await apiDevQuickMatch(deckId);
          this._ensureInstanceSubs();
          this.reconcileInstance(inst);
        } catch (err) {
          toast.error(`Dev quick match failed: ${err.message}`);
        }
        return;
      }

      // Solo and vs-Bot pick TWO decks through the same picker: the first
      // confirm stages "your deck" and re-shows the picker for the opponent
      // slot; the second confirm creates the instance with both ids.
      if ((visibility === 'solo' || visibility === 'bot') && !pendingInstanceCreate.creatorDeckId) {
        this.setState({
          pendingInstanceCreate: { ...pendingInstanceCreate, creatorDeckId: deckId },
        });
        return;
      }

      const mode = (await import('./utils/arena/gameModes')).GAME_MODES.find((m) => m.key === modeKey);
      // vs Bot is a friends-visibility instance on the server with the
      // botOpponent flag. The client-only 'bot' key never crosses the wire.
      const isBotMatch = visibility === 'bot';
      const twoDeckPick = visibility === 'solo' || isBotMatch;
      const seatsMin = visibility === 'solo' ? 1 : (cfg?.seatsMin ?? mode?.seatsMin ?? 2);
      const seatsMax = visibility === 'solo' ? 1 : (cfg?.seatsMax ?? mode?.seatsMax ?? 2);
      const soloCreatorDeckId = twoDeckPick ? pendingInstanceCreate.creatorDeckId : null;
      let inst;
      try {
        inst = await apiCreateInstance({
          visibility: isBotMatch ? 'friends' : visibility,
          mode: modeKey,
          rankedOptIn: !twoDeckPick && !!cfg?.rankedOptIn,
          seatsMin,
          seatsMax,
          format: cfg?.format || null,
          // 10 (Quick Match) or 20 (Standard), chosen in the
          // setup dialog. Server stores it on the instance row so
          // both clients seed the same life total.
          startingHp: cfg?.startingHp || 20,
          // Session-wide automation mode from the setup dialog. Explicit
          // boolean. An omitted field reads as "on" server-side (the
          // legacy-client default), which is the opposite of ours.
          automations: !!cfg?.automations,
          // Solo/bot attach both slots' decks atomically. The server
          // provisions the room (solo: one pilot; bot: human + bot seat) at
          // create time and the 201 carries room_code. Other visibilities
          // attach the chosen deck with the participant row.
          creatorDeckId: twoDeckPick ? soloCreatorDeckId : deckId,
          soloGuestDeckId: twoDeckPick ? deckId : undefined,
          botOpponent: isBotMatch || undefined,
        });
      } catch (err) {
        toast.error(t("Couldn't create instance: {message}", { message: err.message }));
        this.setState({ pendingInstanceCreate: null, arenaView: 'play' });
        return;
      }
      this._ensureInstanceSubs();

      if (visibility === 'solo') {
        // Solo is a REAL server match: one pilot, both seats, the same
        // auth pipeline as multiplayer. Mount the board as a fresh host
        // into the pre-provisioned room from the create response.
        if (!inst.room_code) {
          toast.error(t('Solo match could not be prepared. Please try again.'));
          this.setState({ pendingInstanceCreate: null, arenaView: 'play' });
          return;
        }
        cleanupOnMatchEntry({ profile: arenaProfile, excludeInstanceId: inst.id });
        this.setState({
          pendingInstanceCreate: null,
          arenaSelectedDeckId: soloCreatorDeckId,
          currentInstanceId: inst.id,
          currentInstanceIsHost: true,
          currentInstanceVisibility: 'solo',
          currentInstanceMode: modeKey,
          currentStartingHp: cfg?.startingHp || 20,
          currentAutomations: !!cfg?.automations,
          isGameBoardOpen: true,
          isArenaMatch: true,
          isRankedMatch: false,
          isCasualSandbox: true,
          sessionMode: 'new',
          sessionId: null,
          roomCode: inst.room_code,
          arenaView: 'hub',
          arenaBackTarget: 'play',
        });
        return;
      }

      if (isBotMatch) {
        // Bot match mounts through the NORMAL online-match path (seat p1,
        // per-seat projections). The server already attached the bot to
        // seat p2 and the create response carries the provisioned room.
        if (!inst.room_code) {
          toast.error(t('Bot match could not be prepared. Please try again.'));
          this.setState({ pendingInstanceCreate: null, arenaView: 'play' });
          return;
        }
        this.setState({ pendingInstanceCreate: null });
        await this._launchInstanceHost(inst, inst.room_code);
        return;
      }

      if (visibility === 'friends' && this.state.challengeTarget) {
        // Challenge from the hub: the lobby exists, send the invite straight
        // to the chosen player and wait in the hub. The invite lands in their
        // Queue & Invites menu with the bell; accept joins this instance.
        const target = this.state.challengeTarget;
        apiInviteToInstance(inst.id, target.id, 'join').catch((err) => {
          console.warn('[instance] challenge invite failed:', err);
          toast.error(t('Challenge failed. Try again.'));
        });
        toast.info(t('Challenge sent to {name}. Waiting for them to accept.', { name: target.name }));
        this.setState({
          pendingInstanceCreate: null,
          challengeTarget: null,
          hubQuickHost: false,
          arenaView: 'hub',
          arenaBackTarget: 'hub',
        });
        return;
      }

      if (visibility === 'friends') {
        // Open the friend invite picker on top of SessionBrowser.
        this.setState({
          pendingInstanceCreate: null,
          pendingInstanceForInvite: { instance: inst },
          friendInvitePickerOpen: true,
          arenaView: 'play',
          arenaBackTarget: 'hub',
        });
        return;
      }

      // Public: the instance is now in Gathering and visible in the
      // Public browser tab. Host returns to SessionBrowser.
      toast.info(t('Public {mode} created. Waiting for players.', { mode: modeLabel(modeKey) }));
      this.setState({
        pendingInstanceCreate: null,
        hubQuickHost: false,
        arenaView: this.state.hubQuickHost ? 'hub' : 'play',
        arenaBackTarget: 'hub',
      });
      return;
    }

    // ── Instance: join a public instance. Deck pick is the join
    //    action; on success reconcile from the returned instance so
    //    the ready-check modal opens if joining just filled the last seat.
    // ── Looking-for-match host: the pairing made the lobby, this is the
    //    deck for it. The guest's join then starts the ready-check.
    if (this.state.lfgHostInstanceId) {
      const instanceId = this.state.lfgHostInstanceId;
      this.setState({ lfgHostInstanceId: null, arenaView: 'hub', arenaBackTarget: 'hub' });
      try {
        await apiSetInstanceDeck(instanceId, deckId);
      } catch (err) {
        toast.error(err.message || t('Could not set the deck'));
      }
      this._ensureInstanceSubs();
      this.reconcileInstanceById(instanceId);
      return;
    }

    if (joiningPublicInstance?.instance) {
      const inst = joiningPublicInstance.instance;
      let full;
      try {
        full = await apiJoinInstance(inst.id, { deckId });
      } catch (err) {
        toast.error(err.message || t('Join failed'));
      }
      this._ensureInstanceSubs();
      if (full) cleanupOnMatchEntry({ profile: arenaProfile, excludeInstanceId: inst.id });
      this.reconcileInstance(full);
      this.setState({
        joiningPublicInstance: null,
        arenaView: this.state.arenaBackTarget === 'hub' ? 'hub' : 'play',
        arenaBackTarget: 'hub',
      });
      return;
    }

    // ── Instance: accept a friend instance-invite (chat). Joins
    //    the instance via its id from the chat metadata.
    if (joiningInstanceId) {
      const instanceId = joiningInstanceId;
      let full;
      try {
        full = await apiJoinInstance(instanceId, { deckId });
      } catch (err) {
        toast.error(err.message || t('Join failed'));
      }
      this._ensureInstanceSubs();
      if (full) cleanupOnMatchEntry({ profile: arenaProfile, excludeInstanceId: instanceId });
      this.reconcileInstance(full);
      this.setState({
        joiningInstanceId: null,
        arenaView: this.state.arenaBackTarget === 'hub' ? 'hub' : 'play',
        arenaBackTarget: 'hub',
      });
      return;
    }

    // Sandbox queue skips ownership checks but still enforces structural
    // rules (avatar-specific pairs, etc.). Ranked rejects sandbox-marked
    // decks outright.
    const isSandbox = this.state.pendingQueueMode === 'sandbox';
    const deck = this.getArenaSavedDecks()?.find((d) => d.id === deckId);
    if (deck?.cards) {
      if (!isSandbox && (deck.gameMode || 'constructed') === 'sandbox') {
        toast.error(t("Sandbox decks can't be used in ranked play. Switch the deck to Constructed first."));
        return;
      }
      const ownedMap = buildOwnedMap(arenaProfile.collection || []);
      const cardCatalog = buildCardCatalog(this.state.sorceryCards);
      const verdict = canPlayDeck(deck.cards, ownedMap, { cardCatalog, skipOwnership: isSandbox });
      if (!verdict.canPlay) {
        toast.error(verdict.reason || t('This deck is invalid.'));
        return;
      }
    }

    this.setState({ arenaSelectedDeckId: deckId, arenaMatchmakingOpponent: null });

    // ── Sending a friend invite (deck selected, now dispatch) ──
    // Send both the WebSocket invite event (server creates the room)
    // AND a chat message so the invite appears inline in the friend's
    // chat conversation with Accept/Decline buttons.
    if (pendingInviteFriendId) {
      const friendId = pendingInviteFriendId;
      this.setState({
        pendingInviteFriendId: null,
        arenaView: 'matchmaking',
        arenaMatchmaking: true,
        arenaMatchmakingMode: 'friend',
        arenaMatchmakingOpponent: this.getFriendDisplayInfo(friendId),
      });
      try {
        friendsApi.sendMatchInvite(friendId, deckId);
        sendChatMessage(friendId, {
          type: 'match-invite',
          body: 'invited you to a match!',
          metadata: { deckId },
        }).catch(() => {});
      } catch (err) {
        this.addToast({ title: t('Invite Failed'), message: err.message });
        this.setState({ arenaMatchmaking: false, arenaView: 'hub' });
      }
      return;
    }

    // ── Accepting a friend invite (deck selected, now accept) ──
    // After sending the accept, the server creates the room and sends
    // invite:accepted with the roomCode. That handler opens the game.
    if (pendingInviteAcceptSenderId) {
      const senderId = pendingInviteAcceptSenderId;
      this.setState({
        pendingInviteAcceptSenderId: null,
        arenaView: 'matchmaking',
        arenaMatchmaking: true,
        arenaMatchmakingMode: 'friend',
        arenaMatchmakingOpponent: this.getFriendDisplayInfo(senderId),
      });
      try {
        friendsApi.acceptMatchInvite(senderId, deckId);
        this.addToast({ title: t('Invite Accepted'), message: t('Setting up the match...') });
      } catch (err) {
        this.addToast({ title: t('Accept Failed'), message: err.message });
      }
      // The server will send invite:accepted to the inviter, who then
      // calls handleInviteAccepted → deck-select → game board.
      // This player waits for matchmaking:matched or invite:accepted
      // with the room code (handled by handleNewNotifications).
      return;
    }

    // Friend invite: skip public queue, use the private room directly
    if (isInviteMatch && pendingInviteRoomCode) {
      const isInviteHost = this.state.isInviteHost || false;
      cleanupOnMatchEntry({ profile: arenaProfile });
      this.setState({
        isGameBoardOpen: true,
        isArenaMatch: true,
        isRankedMatch: false,
        sessionMode: isInviteHost ? 'new' : 'join',
        roomCode: pendingInviteRoomCode,
        arenaView: 'hub',
        pendingInviteRoomCode: null,
        isInviteMatch: false,
        isInviteHost: false,
      });
      return;
    }

    // No legacy queue matchmaking fallback. The instance-first
    // flow covers every path that used to land here, and the old
    // startMatchmaking / startSandboxMatchmaking handlers no longer
    // exist. confirmDeckAndQueue should have returned earlier via
    // one of the branches above; if we reach this point the caller
    // did something unexpected and we just bail back to the hub.
    console.warn('[confirmDeckAndQueue] no branch matched. Returning to hub');
    this.setState({ arenaView: 'hub' });
  };

  cancelMatchmaking = async () => {
    const { arenaProfile } = this.state;
    clearInterval(this.arenaQueuePollTimer);
    this.arenaQueuePollTimer = null;

    if (arenaProfile?.serverToken) {
      await leaveQueue(arenaProfile.serverToken).catch(() => {});
    }

    this.setState((s) => ({
      arenaMatchmaking: false,
      // Matchmaking cancel. Return to whichever screen the user
      // came from (play-screen mode picker or main hub). Same
      // arenaBackTarget sentinel the deck-select cancel consults.
      arenaView: s.arenaBackTarget || 'hub',
      arenaBackTarget: 'hub',
      arenaMatchmakingOpponent: null,
      arenaMatchmakingMode: null,
      isArenaMatch: false,
      isRankedMatch: false,
      isCasualSandbox: false,
      arenaSelectedDeckId: null,
    }));
  };

  // L1 starter pick has been unified into handleProgressionDeckChosen.
  // Both paths now route through the same server endpoint
  // (/me/progression/claim-deck), and the L1 branch additionally
  // stamps the legacy starter_deck column server-side and the
  // profileAvatar preference client-side.
  handleProgressionDeckChosen = async (deck) => {
    const { arenaProfile, sorceryCards } = this.state;
    if (!deck || !sorceryCards?.length) {
      this.addToast({ title: t('Loading Cards'), message: t('Please wait a moment and try again.') });
      return;
    }

    const resolvedCards = resolveStarterDeck(deck, sorceryCards);
    const cardIndex = new Map();
    for (const c of sorceryCards) cardIndex.set(c.unique_id, c);

    const grantItems = [];
    for (const card of resolvedCards) {
      const foiling = card.foiling || 'S';
      const artGroup = card.artGroup || ART_GROUP_BASE;
      const existing = grantItems.find(
        (i) => i.cardId === card.cardId && i.foiling === foiling && i.artGroup === artGroup,
      );
      if (existing) existing.quantity++;
      else grantItems.push({ cardId: card.cardId, artGroup, foiling, quantity: 1 });
    }

    // L1 starter pick. Capture the avatar card id so we can stamp it
    // onto profileAvatar after the claim succeeds. The server's
    // claim-deck endpoint stamps the legacy starter_deck column for
    // L1 automatically, so we only need to handle the avatar pref
    // here. Subsequent picks (L2+) leave profileAvatar untouched.
    const isL1Pick = (arenaProfile.starterDecksOwned?.length || 0) === 0;
    const avatarCard = isL1Pick ? resolvedCards.find((c) => c.zone === 'avatar') : null;

    let claim;
    try {
      claim = await claimProgressionDeck(deck.id, grantItems);
    } catch (e) {
      console.error('Failed to claim progression deck:', e);
      this.addToast({ title: t('Could not claim deck'), message: e.message || t('Try again in a moment.') });
      return;
    }

    // Save a fresh deck template into the player's saved decks so the
    // new precon shows up in the deck builder + deck picker alongside
    // the L1 starter.
    const deckId = `deck-${Date.now()}`;
    const previewCards = [];
    const seen = new Set();
    for (const entry of resolvedCards) {
      if (seen.has(entry.cardId)) continue;
      const card = cardIndex.get(entry.cardId);
      // Prefer the printing the deck actually grants, so a deck whose avatar
      // is an alt art previews with that art and not the booster printing.
      const printing = card?.printings?.find((p) => p.unique_id === entry.printingId)
        || card?.printings?.[0];
      const imageUrl = printing?.image_url;
      if (imageUrl) {
        seen.add(entry.cardId);
        previewCards.push({ name: card.name, imageUrl });
        if (previewCards.length >= 10) break;
      }
    }
    const savedCards = resolvedCards.map((c) => ({
      cardId: c.cardId,
      cardName: cardIndex.get(c.cardId)?.name || '',
      printingId: c.printingId,
      foiling: c.foiling || 'S',
      isSideboard: c.zone === 'collection',
    }));
    const featuredCards = computeFeaturedCards(savedCards, sorceryCards, 3);
    try {
      await saveSavedDeck({
        id: deckId,
        name: deck.name,
        format: 'constructed',
        gameMode: 'constructed',
        // Marks the row as a system grant rather than a deck the player
        // built, so sharing it can't earn the share_deck achievement. The
        // flag is write-once server-side. Later edits never clear it.
        isPrecon: true,
        cards: savedCards,
        previewCards,
        featuredCards,
      }, 'sorcery');
    } catch (e) {
      console.warn('Failed to save progression deck template:', e);
    }

    // Re-fetch the full profile so pendingDeckGrants + the
    // Progression-screen snapshot reflect the claim. The claim
    // response only returns the changed fields; everything else
    // (xp, etc.) is unchanged but the derived pending count and
    // the next-claimable per-type values need to come from the
    // server recompute.
    const fresh = await loadArenaProfile(arenaProfile.serverToken).catch(() => null);
    const merged = fresh
      ? { ...arenaProfile, ...fresh }
      : {
          ...arenaProfile,
          starterDecksOwned: claim.starterDecksOwned,
          collection: claim.collection,
          pendingDeckGrants: Math.max(0, (arenaProfile.pendingDeckGrants || 0) - 1),
        };
    // Stamp profileAvatar from the L1 starter pick. saveArenaProfile
    // is fire-and-forget since the only field changing is the avatar
    // preference; a failed save just falls back to the next profile
    // load picking up nothing. No broken state.
    if (avatarCard) {
      merged.profileAvatar = avatarCard.cardId;
      saveArenaProfile(merged).catch((e) => console.error('Failed to save L1 avatar pref:', e));
    }
    this.setState({ arenaProfile: merged });
    await this.refreshSavedDecks();
    this.addToast({ title: t('{name} added', { name: deck.name }), message: t('Cards granted to your collection.') });

    // Close the picker and bump the refresh tag so the still-mounted
    // Progression screen re-hydrates from the new profile and
    // advances its focus to the next unclaimed level.
    this.setState({
      pickerOpen: false,
      progressionRefreshTag: (this.state.progressionRefreshTag || 0) + 1,
    });
  };

  // --- Instance-first play flow handlers ---

  // Called whenever arenaProfile becomes non-null (componentDidUpdate)
  // to keep the global WS subscription set in sync with the user's
  // instance list. Drives the ReadyCheckModal global overlay.
  _ensureInstanceSubs = async () => {
    try {
      const instances = await apiListInstances();
      const ids = new Set(instances.map((i) => i.id));
      for (const [id, off] of this._instanceSubs) {
        if (!ids.has(id)) { try { off(); } catch {} this._instanceSubs.delete(id); }
      }
      for (const inst of instances) {
        if (this._instanceSubs.has(inst.id)) continue;
        this._instanceSubs.set(inst.id, () => {});
        apiSubscribeInstance(inst.id, (event) => this._handleInstanceEvent(inst, event), (synced) => this.reconcileInstance(synced))
          .then((off) => this._instanceSubs.set(inst.id, off))
          .catch((err) => {
            console.error('[app] instance subscribe failed:', err);
            this._instanceSubs.delete(inst.id);
          });
      }
    } catch (err) {
      console.warn('[app] _ensureInstanceSubs skipped:', err.message);
    }
  };

  _handleInstanceEvent = async (instance, event) => {
    if (!event?.type) return;
    const myId = this.state.arenaProfile?.id;
    if (event.type === 'readyCheckStarted' || event.type === 'phaseTransition') {
      // Events are hints. Re-fetch authoritative state and let reconcileInstance
      // decide the view. A missed event is now harmless. Other triggers reconcile.
      await this.reconcileInstanceById(instance.id);
      return;
    }
    if (event.type === 'resumeRequested') {
      if (event.userId && event.userId !== myId) {
        // Actionable toast: tap "Open Play" to jump directly to the
        // SessionBrowser save list where the Resume button lives.
        // Saves a manual hub navigation step.
        this.addToast({
          title: t('Resume requested'),
          message: t('A player wants to resume your {mode}.', { mode: modeLabel(instance.mode) }),
          actions: [
            { key: 'open-play', label: t('Open Play'), primary: true },
          ],
        });
      }
    } else if (event.type === 'abandoned') {
      // Both players dropped and neither returned in time. Terminal and
      // non-resumable. Surface the abandoned modal (closing the board if
      // it's the one currently open) instead of leaving a dead board or a
      // phantom result. reconcileInstance covers the cold-reconnect path
      // where this live event was missed. A draft/sealed row is closed by
      // its event ending; draft:cancelled already told the player.
      if (!isDraftInstance(instance)) this._showAbandonedMatch(instance.id);
    } else if (event.type === 'completed') {
      // Terminal. Nothing to clean up locally; the server consumed the
      // instance's invitations, and every list view re-derives from
      // server state on its next refresh. The reward result is surfaced
      // separately via the match:settled push.
    } else if (event.type === 'paused') {
      // No-op at the app level. SessionBrowser's own subscription
      // refreshes the list.
    }
  };

  // Surface the "Match abandoned" modal for a terminal abandoned instance.
  // Idempotent per instance (the live event and the cold-reconnect reconcile
  // can both fire for the same id) and closes the board if that instance is
  // the one currently open, mirroring how the completed/forfeit case tears
  // the board down before showing its result.
  _abandonedSeen = new Set();

  _showAbandonedMatch = (instanceId) => {
    if (!instanceId) return;
    // Show once per instance per session: the abandoned row lingers in the
    // instance list until the server's archive sweeper retires it, so without
    // this guard a later reconcile poll would re-pop the modal the player
    // already dismissed.
    if (this._abandonedSeen.has(instanceId)) return;
    if (this.state.abandonedMatchModal?.instanceId === instanceId) return;
    this._abandonedSeen.add(instanceId);
    const isOpenBoard = this.state.isGameBoardOpen && this.state.currentInstanceId === instanceId;
    if (isOpenBoard) {
      this.gameBoardRef = null;
      stopMusic(2000);
      try { localStorage.removeItem('valkenhall-active-session'); } catch {}
      this._completedInstanceId = null;
      this._launchingInstanceId = null;
      this.setState({
        isGameBoardOpen: false, sessionMode: null, sessionId: null, roomCode: null,
        isArenaMatch: false, isRankedMatch: false, isCasualSandbox: false, isSpectating: false, spectateRoomCode: null,
        arenaMatchmakingOpponent: null,
        currentInstanceId: null,
        currentInstanceIsHost: false,
        currentInstanceVisibility: null,
        currentInstanceMode: null,
        isResumeJoin: false,
        currentDraftMatchId: null,
        opponentDeckForPreload: null,
        matchAssetsReady: false,
        arenaView: 'hub',
        abandonedMatchModal: { instanceId },
      });
    } else {
      this.setState({ abandonedMatchModal: { instanceId } });
    }
  };

  // Single funnel: given an authoritative instance, drive the match-launch UI
  // from deriveSessionView. Idempotent + ordering-guarded so any trigger
  // (subscribe sync, lifecycle event, reconnect, focus, heartbeat, /ready
  // response) can call it freely. This is what makes a missed broadcast
  // non-fatal. The view is a function of current state, not a latched event.
  reconcileInstance = (instance) => {
    if (!instance?.id) return;
    const myId = this.state.arenaProfile?.id;
    if (!myId) return;

    // Terminal abandoned: a returning player whose live match was abandoned
    // while they were offline finds the row in their instance list (it isn't
    // archived yet). Surface the modal instead of letting deriveSessionView
    // fall through to 'none' and silently drop them on the hub. Guarded to
    // participants only. A stray row we don't belong to shouldn't pop a modal.
    if (instance.phase === 'abandoned') {
      const me = (instance.participants || []).find((p) => p.userId === myId);
      if (me && !isDraftInstance(instance)) this._showAbandonedMatch(instance.id);
      return;
    }

    // Ordering guard: ignore an instance older than the last one we applied.
    const stamp = instance.last_activity_at ? new Date(instance.last_activity_at).getTime() : 0;
    const prev = this.state._reconcileSeenAt?.[instance.id] || 0;
    if (stamp && stamp < prev) return;
    if (stamp) {
      this.setState((s) => ({
        _reconcileSeenAt: { ...(s._reconcileSeenAt || {}), [instance.id]: stamp },
      }));
    }

    const view = deriveSessionView(instance, myId);

    if (view.show === 'launchHost' || view.show === 'launchGuest') {
      // We are launching into this match. Past ready-check. Clear any lingering
      // overlay FIRST, before the early-return guards below, so a launch reconcile
      // that arrives after the board already opened still dismisses it.
      if (this.state.activeReadyCheck?.instance?.id === instance.id) {
        this.setState({ activeReadyCheck: null });
      }
      // Idempotent: don't relaunch a match we're already in.
      if (this.state.isGameBoardOpen && this.state.currentInstanceId === instance.id) return;
      // Serialize launches globally: never start a launch while another is
      // in flight or ANY board is open (solo included). A backlog of stale
      // active instances would otherwise storm-launch on boot, each mount
      // stomping the previous one.
      if (this._launchingInstanceId) return;
      if (this.state.isGameBoardOpen) return;
      this._launchingInstanceId = instance.id;
      if (view.show === 'launchHost') this._launchInstanceHost(instance, view.roomCode);
      else this._launchInstanceGuest(instance, view.roomCode, view.resumed);
      return;
    }

    if (view.show === 'readyCheck') {
      // Past ready-check once this instance's board is open: a stale re-derive
      // (a raced fetch that still sees the pre-active instance with a future
      // deadline) must never resurrect the overlay on top of the live match.
      if (this.state.isGameBoardOpen && this.state.currentInstanceId === instance.id) return;
      if (this.state.activeReadyCheck?.instance?.id !== instance.id) {
        this.setState({ activeReadyCheck: { instance } });
      }
      return;
    }

    // view.show === 'none': close a ready-check we were showing for this instance.
    if (this.state.activeReadyCheck?.instance?.id === instance.id) {
      this.setState({ activeReadyCheck: null });
    }
  };

  reconcileInstanceById = async (instanceId) => {
    try {
      const fresh = await apiGetInstance(instanceId);
      if (fresh) this.reconcileInstance(fresh);
    } catch (err) {
      console.warn('[app] reconcileInstanceById failed:', err?.message || err);
    }
  };

  // Safety net for the focus/heartbeat triggers. A ready-check that opens
  // AFTER we settle into waiting (the host case: you create a lobby, then
  // the guest joins minutes later) only reaches us via the one-shot
  // readyCheckStarted broadcast. If that's missed. Electron throttles
  // timers/sockets in a backgrounded window, the machine slept, or the
  // subscription attached after the event. There was no recovery: the old
  // focus/heartbeat reconciled only an already-open activeReadyCheck, so a
  // missed open could never be recovered and the host sat forever with no
  // prompt. Re-derive from authoritative list state instead, so any of our
  // gathering/paused lobbies can still open their modal (or launch).
  _reconcilePending = async () => {
    const activeId = this.state.activeReadyCheck?.instance?.id;
    if (activeId) { this.reconcileInstanceById(activeId); return; }
    // Only worth a poll when we're sitting in some lobby and not already
    // in a game. The per-instance WS push stays the fast path; this is the
    // belt-and-suspenders that makes a dropped event non-fatal.
    // Signed out, nothing to reconcile: the list needs a token, and a stale
    // subscription map from the previous session must not keep it polling.
    if (!this.state.arenaProfile?.id) return;
    if (this.state.isGameBoardOpen || this._instanceSubs.size === 0) return;
    try {
      const instances = await apiListInstances();
      for (const inst of instances) this.reconcileInstance(inst);
    } catch (err) {
      console.warn('[app] _reconcilePending failed:', err?.message || err);
    }
  };

  _launchInstanceHost = async (instance, roomCode) => {
    const me = (instance.participants || []).find(
      (p) => p.userId === this.state.arenaProfile?.id,
    );
    cleanupOnMatchEntry({ profile: this.state.arenaProfile, excludeInstanceId: instance.id });
    // Solo (one pilot, both seats): always a fresh host-new mount into the
    // server room. The board is a pure view of the server mirror, so there
    // is no local blob to stage. Resume state arrives via match:full.
    if (instance.visibility === 'solo') {
      this.setState({
        currentInstanceId: instance.id,
        currentInstanceIsHost: true,
        currentInstanceVisibility: 'solo',
        currentInstanceMode: instance.mode,
        currentStartingHp: instance.starting_hp || 20,
        currentAutomations: instance.automations !== false,
        arenaSelectedDeckId: me?.deckId || null,
        arenaMatchmakingOpponent: null,
        isGameBoardOpen: true,
        isArenaMatch: true,
        isRankedMatch: false,
        isCasualSandbox: true,
        sessionMode: 'new',
        sessionId: null,
        roomCode,
        arenaView: 'hub',
        arenaBackTarget: 'play',
      });
      return;
    }
    // If the host pre-staged a local blob via handleInstanceAction
    // (resume from paused), mount in load mode with that sessionId
    // so GameBoard restores from the saved state. Otherwise fresh
    // new-mode creates a brand-new room on the relay.
    const blobMap = this.state._pendingResumeBlobs || {};
    let stagedSessionId = blobMap[instance.id] || null;
    // Self-heal the resume: the pre-flight stage in handleInstanceAction
    // races the phaseTransition→active launch, and when it loses, no blob
    // is staged → we'd mount 'new' and fresh-spawn over a real game (the
    // intermittent "resume shows mulligan + wiped board" bug). If a save
    // for THIS instance exists on disk, this is a resume. Load it directly
    // so 'load' mode is guaranteed regardless of the race. A brand-new
    // match has no save, so this stays null and we correctly mount 'new'.
    if (!stagedSessionId) {
      try {
        const { loadSessionByInstance } = await import('./utils/game/sessionStorage');
        const stub = await loadSessionByInstance(instance.id, this.state.arenaProfile?.id);
        if (stub?.id) {
          stagedSessionId = stub.id;
          console.log('[resume-debug] launchInstanceHost recovered blob from disk:', stub.id);
        }
      } catch (err) {
        console.warn('[resume] disk-blob fallback lookup failed:', err);
      }
    }
    const nextBlobMap = { ...blobMap };
    delete nextBlobMap[instance.id];
    this.setState({
      currentInstanceId: instance.id,
      currentInstanceIsHost: true,
      currentInstanceVisibility: instance.visibility,
      currentInstanceMode: instance.mode,
      currentStartingHp: instance.starting_hp || 20,
      currentAutomations: instance.automations !== false,
      arenaSelectedDeckId: me?.deckId || null,
      arenaMatchmakingOpponent: this._resolveInstanceOpponent(instance),
      isGameBoardOpen: true,
      isArenaMatch: true,
      isRankedMatch: !!instance.ranked_opt_in,
      isCasualSandbox: false,
      sessionMode: stagedSessionId ? 'load' : 'new',
      sessionId: stagedSessionId,
      roomCode,
      arenaView: 'hub',
      arenaBackTarget: 'play',
      _pendingResumeBlobs: Object.keys(nextBlobMap).length ? nextBlobMap : null,
    });
  };

  _launchInstanceGuest = (instance, roomCode, isResumeJoin = false) => {
    const me = (instance.participants || []).find(
      (p) => p.userId === this.state.arenaProfile?.id,
    );
    cleanupOnMatchEntry({ profile: this.state.arenaProfile, excludeInstanceId: instance.id });
    this.setState({
      currentInstanceId: instance.id,
      currentInstanceIsHost: false,
      currentInstanceVisibility: instance.visibility,
      currentInstanceMode: instance.mode,
      currentStartingHp: instance.starting_hp || 20,
      currentAutomations: instance.automations !== false,
      arenaSelectedDeckId: me?.deckId || null,
      arenaMatchmakingOpponent: this._resolveInstanceOpponent(instance),
      isGameBoardOpen: true,
      isArenaMatch: true,
      isRankedMatch: !!instance.ranked_opt_in,
      isCasualSandbox: false,
      sessionMode: 'join',
      sessionId: null,
      roomCode,
      arenaView: 'hub',
      arenaBackTarget: 'play',
      isResumeJoin,
    });
  };

  handlePickVisibility = async (visibility) => {
    // Matchmaking, first in the Play menu. Not a visibility: opens the
    // looking-for-a-match panel over whatever screen we are on.
    if (visibility === 'queue') {
      this.setState({ showLfg: true });
      return;
    }
    // Play-screen path. Drop any hub-initiated intent so a cancelled
    // challenge can never auto-invite its target from a later lobby.
    if (this.state.hubQuickHost || this.state.challengeTarget) {
      this.setState({ hubQuickHost: false, challengeTarget: null });
    }
    // Solo has exactly one playable mode (Sandbox) so the
    // "Choose Game Mode" picker is just a one-tile dead-end.
    // Confirm intent first, then launch the sandbox table directly.
    if (visibility === 'solo') {
      const ok = await this._askConfirm({
        title: t('Start a solo match?'),
        message: t('Pick a deck for each side, then pilot both seats at the sandbox table.'),
        confirmLabel: t('Start Match'),
        cancelLabel: t('Cancel'),
      });
      if (!ok) return;
      this.setState({ pickerVisibility: 'solo' }, () => {
        this.handlePickMode('sandbox');
      });
      return;
    }
    // vs Bot mirrors the solo entry: one playable mode (Sandbox), two deck
    // picks (yours, then the bot's), then straight into the match.
    if (visibility === 'bot') {
      const ok = await this._askConfirm({
        title: t('Start a bot match?'),
        message: t("Pick your deck and the bot's deck, then play a real match against the practice bot."),
        confirmLabel: t('Start Match'),
        cancelLabel: t('Cancel'),
      });
      if (!ok) return;
      this.setState({ pickerVisibility: 'bot' }, () => {
        this.handlePickMode('sandbox');
      });
      return;
    }
    this.setState({ pickerVisibility: visibility });
  };

  handleCancelPicker = () => {
    this.setState((s) => ({
      pickerVisibility: null,
      setupForMode: null,
      challengeTarget: null,
      hubQuickHost: false,
      arenaView: s.hubQuickHost ? 'hub' : s.arenaView,
    }));
  };

  handlePickMode = (modeKey) => {
    const visibility = this.state.pickerVisibility;
    if (!visibility) return;
    // Drafts and sealed get the full-featured legacy
    // CreateDraftModal. It owns set-unlock gates, pod size
    // selector (2/4/6/8), optional friend-invite picker, entry
    // cost preview.
    if (modeKey === 'draft' || modeKey === 'sealed') {
      this.setState({
        draftCreateSetup: { visibility, modeKey },
        pickerVisibility: null,
      });
      return;
    }
    // Everything else. Match and sandbox, at every visibility. Goes
    // through the setup dialog. Sandbox used to skip it (no format, no
    // HP cap, no ranked stakes made it a one-button detour), but the
    // automation mode is asked for every session, so it has a real
    // choice to present now. handleSetupConfirm routes on to the deck
    // picker; for friends the invite picker fires after the deck is
    // chosen, and solo picks TWO decks (yours, then the opponent
    // slot's) via confirmDeckAndQueue.
    this.setState({ setupForMode: { visibility, modeKey } });
  };

  handleCancelSetup = () => {
    if (this.state.hubQuickHost) { this.handleCancelPicker(); return; }
    this.setState({ setupForMode: null });
  };

  handleCancelDraftCreate = () => {
    if (this.state.hubQuickHost) { this.setState({ draftCreateSetup: null }); this.handleCancelPicker(); return; }
    this.setState({ draftCreateSetup: null });
  };

  handleSetupConfirm = async (cfg) => {
    const { visibility, modeKey } = this.state.setupForMode || {};
    if (!visibility || !modeKey) return;
    this.setState({ setupForMode: null, pickerVisibility: null });

    // Drafts and sealed don't land here anymore. HandlePickMode
    // routes them directly to draftCreateSetup + CreateDraftModal
    // so the full legacy create form is presented. Defensive
    // guard so a stale caller still bails rather than silently
    // creating an under-configured instance.
    if (modeKey === 'draft' || modeKey === 'sealed') {
      console.warn('[handleSetupConfirm] draft/sealed reached the generic path. Use draftCreateSetup instead');
      return;
    }

    // Match / Sandbox. Route through the deck picker. The actual
    // /instances POST happens in confirmDeckAndQueue once a deck
    // is chosen, so the creator's deck is attached to their
    // participant row atomically with instance creation.
    // For 'match' the cfg.format selects the rarity ruleset
    // ('constructed' or 'peasant'); sandbox has no format axis.
    this.setState({
      pendingInstanceCreate: { visibility, modeKey, cfg },
      pendingQueueMode: modeKey === 'sandbox' ? 'sandbox' : 'ranked',
      pendingQueueFormat: modeKey === 'match' ? (cfg?.format || 'constructed') : null,
      arenaView: 'deck-select',
      arenaBackTarget: this.state.hubQuickHost ? 'hub' : 'play',
    });
  };

  // DEV quick match (local server): route through the standard deck picker
  // with the 'devquick' visibility; confirmDeckAndQueue does the one-POST
  // start. Sandbox queue mode so sandbox decks are pickable.
  startDevQuickMatch = () => {
    this.setState({
      pendingInstanceCreate: { visibility: 'devquick', modeKey: 'match', cfg: {} },
      pendingQueueMode: 'sandbox',
      pendingQueueFormat: null,
      arenaView: 'deck-select',
      arenaBackTarget: 'hub',
    });
  };

  // CreateDraftModal.onCreate callback for the unified instance
  // flow (SessionBrowser → Draft/Sealed path). One composite
  // /instances POST creates the instance plus its linked
  // draft_event server-side; this handler then stashes an initial
  // blob snapshot, sends chat invites for private events, and
  // drops the user into DraftLobby. Same UX the legacy
  // DraftBrowser.handleCreate path delivers, minus scheduledAt
  // exposure.
  handleDraftCreateFromSession = async (params) => {
    const setup = this.state.draftCreateSetup;
    if (!setup) return;
    const { visibility, modeKey } = setup;
    const { setKey, podSize, scheduledAt, paymentMethod } = params;
    // A hub challenge pre-invites its target alongside anyone picked in
    // the modal.
    const challenge = this.state.challengeTarget;
    const invitePlayerIds = [...new Set([...(params.invitePlayerIds || []), ...(challenge && visibility === 'friends' ? [challenge.id] : [])])];
    let draftEvent;
    let inst;
    try {
      const { getDraftEvent, inviteToDraft } = await import('./utils/arena/draftApi');
      // Composite create: ONE request makes the instance AND the
      // linked draft_event server-side, so a client crash can never
      // orphan a charged, instance-less event (the old two-call flow
      // could). paymentMethod rides along so the server's entry debit
      // takes the Keystone path when the modal selected it.
      inst = await apiCreateInstance({
        visibility,
        mode: modeKey,
        rankedOptIn: false,
        seatsMin: 2,
        seatsMax: podSize,
        format: setKey,
        automations: !!params.automations,
        draft: { setKey, scheduledAt, paymentMethod },
      });
      if (!inst?.draft_event_id) throw new Error('Draft event was not created');
      draftEvent = await getDraftEvent(inst.draft_event_id);
      if (inst?.id) {
        try {
          const { createInitialDraftState } = await import('./utils/game/draftSaveState');
          const { autoSaveDraftInstance } = await import('./utils/game/instanceSaveAdapter');
          const draftState = createInitialDraftState({
            instanceId: inst.id,
            visibility,
            podSize,
            format: setKey,
            hostUserId: this.state.arenaProfile?.id,
          });
          await autoSaveDraftInstance({ instanceId: inst.id, visibility, draftState });
        } catch (e) {
          console.warn('[draft] initial blob save failed:', e?.message || e);
        }
      }
      // Private drafts: dispatch chat invites + record draft_invites
      // server-side. Same two-channel delivery as the legacy
      // DraftBrowser.handleCreate path. Chat message surfaces an
      // Accept button in the Social tab; recordInvite grants the
      // recipient access to the private event.
      if (invitePlayerIds.length > 0) {
        const setLabels = { beta: 'Beta', arthurian: 'Arthurian Legends', gothic: 'Gothic' };
        const setLabel = setLabels[setKey] || setKey;
        const inviteBody = modeKey === 'sealed' ? 'invited you to a Sealed event!' : 'invited you to a draft!';
        await Promise.all(invitePlayerIds.map((friendId) => (
          sendChatMessage(friendId, {
            type: 'draft-invite',
            body: inviteBody,
            metadata: {
              eventId: draftEvent.id,
              instanceId: inst?.id || null,
              mode: modeKey,
              setKey, setLabel,
              podSize,
              entryCost: draftEvent.entryCost,
            },
          }).catch((err) => console.warn('[draft] chat invite failed:', err?.message))
        )));
        await inviteToDraft(draftEvent.id, invitePlayerIds).catch(() => {});
      }
      if (inst?.id) {
        this.setState({
          currentDraftInstanceId: inst.id,
          currentDraftInstanceVisibility: visibility,
          currentDraftInstanceIsHost: true,
        });
      }
      this._ensureInstanceSubs();
    } catch (err) {
      const msg = err?.message || String(err);
      toast.error(t("Couldn't create {mode}: {message}", { mode: modeLabel(modeKey), message: msg }));
      throw err; // propagate so CreateDraftModal shows its error state
    }
    this.setState({ draftCreateSetup: null, challengeTarget: null, hubQuickHost: false });
    if (challenge && visibility === 'friends') toast.info(t('Challenge sent to {name}.', { name: challenge.name }));
    if (draftEvent?.id) this.openDraftLobby(draftEvent.id);
  };

  handleInstanceAction = async (instance, action) => {
    switch (action) {
      case 'resume':
      case 'rejoin':
      case 'open': {
        // Draft / sealed instances route into the DraftLobby for
        // the linked draft_event. The lobby + subsequent screens
        // hydrate from the server-authoritative draft_events
        // state machine; our blob provides the save-list UX (one
        // row per tournament) plus the checkpoint snapshots for
        // crash-recovery.
        if (instance.mode === 'draft' || instance.mode === 'sealed') {
          if (!instance.draft_event_id) {
            // A draft instance with no linked event is a broken
            // row (possibly created by an earlier pre-link build,
            // or its draft_event got garbage-collected). Without
            // the link there's no lobby to route to, no pick
            // order to restore. Offer to clean it up rather than
            // falling through to the match-instance markReady
            // path which would show a misleading "Resume
            // request sent" toast.
            const abandon = await this._askConfirm({
              title: t('Broken Draft'),
              message: t('This draft has no linked event data and can’t be opened. Abandon it?'),
              confirmLabel: t('Abandon'),
              danger: true,
            });
            if (abandon) {
              const myIdBroken = this.state.arenaProfile?.id;
              const amCreator = (instance.participants || [])
                .some((p) => p.userId === myIdBroken && p.role === 'creator');
              try {
                if (amCreator) await apiAbandonInstance(instance.id);
                else await apiLeaveInstance(instance.id);
              } catch (err) {
                toast.error(err?.message || t('Could not remove the draft.'));
                return;
              }
              window.dispatchEvent(new CustomEvent('valkenhall:instances-changed'));
            }
            return;
          }
          const myId = this.state.arenaProfile?.id;
          const isHost = (instance.participants || [])
            .some((p) => p.userId === myId && p.role === 'creator');
          // Track which instance's checkpoints we're writing so
          // handleDraftStarted / handleBuildingPhase / etc. can
          // persist blob snapshots without threading the id
          // through every handler.
          this.setState({
            currentDraftInstanceId: instance.id,
            currentDraftInstanceVisibility: instance.visibility,
            currentDraftInstanceIsHost: isHost,
          });
          // Host resuming a paused draft re-invites every other
          // participant: one chat-invite per absent player so it
          // shows up in their Queue dropdown (via
          // InstanceQueueIndicator's draft-invite picker) AND in
          // their Social tab. Gives offline / closed-app
          // participants a clear way back in without relying on
          // them being subscribed to the WS at the exact moment.
          // Skipped for gathering (original invites still valid)
          // and terminal phases.
          // Fire re-invites whenever the host re-opens an in-flight
          // draft. Paused OR active. Paused rows are the "resume
          // from save" case; active rows cover mid-tournament
          // rejoin (round match exited, host returns via save row)
          // where the opponent otherwise gets no signal that the
          // host is back and ready to finish the round.
          if (isHost && (instance.phase === 'paused' || instance.phase === 'active')) {
            this._rebroadcastDraftInvites(instance).catch((err) => {
              console.warn('[draft] resume re-invite failed:', err?.message || err);
            });
          }
          this.openDraftLobby(instance.draft_event_id);
          return;
        }
        if (instance.visibility === 'solo') {
          // Solo resume rides the instance /ready path wholesale: the one
          // participant readying re-mints the room from the durable deck ids
          // and flips the instance active (phaseTransition carries the fresh
          // roomCode). The reconcile below launches the board through the
          // same deriveSessionView → launchHost funnel multiplayer uses;
          // board state comes from the server mirror, not a local blob.
          try {
            await apiMarkReady(instance.id);
          } catch (err) {
            toast.error(err?.message || t('Could not resume the solo match.'));
            return;
          }
          await this.reconcileInstanceById(instance.id);
          return;
        }

        // Multiplayer. Pre-flight the host's local blob so
        // _launchInstanceHost can mount in load mode once the
        // phaseTransition→active event fires.
        const myId = this.state.arenaProfile?.id;
        const me = (instance.participants || []).find((p) => p.userId === myId);
        const isHost = me?.role === 'creator';
        let resumePhase = instance.phase;
        if (isHost && (resumePhase === 'paused' || resumePhase === 'active')) {
          const { loadSessionByInstance } = await import('./utils/game/sessionStorage');
          const stub = await loadSessionByInstance(instance.id, this.state.arenaProfile?.id).catch(() => null);
          if (!stub) {
            toast.error(t('Local save missing for this instance. Instance will be abandoned.'));
            try { await apiAbandonInstance(instance.id); } catch {}
            return;
          }
          this.setState((s) => ({
            _pendingResumeBlobs: {
              ...(s._pendingResumeBlobs || {}),
              [instance.id]: stub.id,
            },
          }));
          // A non-graceful close (app crash, connection drop, both players
          // disconnecting) leaves the instance 'active' instead of 'paused'.
          // The resume-invite flow below. And the server's /invite +
          // /resume-start. All require 'paused'. Without pausing first the
          // host falls into the markReady branch (the "Resume requested"
          // toast) and the guest never receives an invite. Pause it now so
          // the normal paused-resume invite path runs.
          if (resumePhase === 'active') {
            try {
              await apiPauseInstance(instance.id);
              resumePhase = 'paused';
            } catch (err) {
              console.warn('[match] pause-before-resume failed:', err?.message || err);
            }
          }
        }

        try {
          // Paused-resume flow: the host's Resume click is ONLY an
          // invite. No ready-check starts and no modal opens yet.
          // the guest has to see the invite in their queue, open
          // it, and accept. Only THEN does the accepting client
          // call /resume-start, which broadcasts readyCheckStarted
          // to both sides so both modals appear together.
          const { markReady: apiMarkReady } = await import('./utils/arena/instancesApi');
          if (isHost && resumePhase === 'paused') {
            await this._rebroadcastMatchInvites(instance).catch((err) => {
              console.warn('[match] resume re-invite failed:', err?.message || err);
            });
            // The invitation rows just written are what make the
            // "waiting for opponent" entry appear (pending_invite_count
            // on the instance payload). Poke every list to re-fetch
            // now instead of on the next 30s poll.
            window.dispatchEvent(new CustomEvent('valkenhall:instances-changed'));
            const othersCount = (instance.participants || [])
              .filter((p) => p.userId && p.userId !== myId).length;
            toast.info(
              othersCount === 1
                ? t('Resume invite sent. Waiting for your opponent to accept.')
                : t('Resume invite sent to {n} players.', { n: othersCount }),
            );
          } else {
            // Guest clicking Resume (or any rejoin into an already-
            // active instance) marks self ready against an already-
            // open check.
            await apiMarkReady(instance.id);
            toast.info(isHost ? t('Resume requested.') : t('Ready to resume. Waiting for host.'));
          }
        } catch (err) {
          toast.error(err.message || t('Resume failed'));
        }
        break;
      }
      case 'request-resume':
        try {
          await apiRequestResume(instance.id);
          toast.info(t('Host has been pinged.'));
        } catch (err) {
          toast.error(err.message || t('Ping failed'));
        }
        break;
      case 'claim-win':
        try {
          await apiClaimWin(instance.id);
          toast.success(t('Claimed.'));
        } catch (err) {
          toast.error(err.message || t('Not eligible'));
        }
        break;
      case 'view-result':
        toast.info(t('Result: {result}', { result: JSON.stringify(instance.result || {}) }));
        break;
      case 'dismiss':
      case 'discard': {
        // Server-authoritative discard. The row's visibility is derived
        // entirely from server state, so removing it means actually
        // changing that state: creators abandon the instance, joined
        // players leave it (gathering frees the seat; paused/active
        // concedes). There is deliberately NO local hide path. A
        // client-side dismissed set is what used to let the save list
        // and the Queue & Invites panel disagree about the same match.
        const myId = this.state.arenaProfile?.id;
        const me = (instance.participants || []).find((p) => p.userId === myId);
        const isHost = me?.role === 'creator';
        const isTerminal = instance.phase === 'completed' || instance.phase === 'abandoned';
        const isLiveMatch = instance.phase === 'paused' || instance.phase === 'active';
        if (!isTerminal) {
          const isDraftRow = instance.mode === 'draft' || instance.mode === 'sealed';
          const ok = await this._askConfirm({
            title: isHost ? (isDraftRow ? t('Discard draft?') : t('Discard match?')) : (isLiveMatch ? t('Forfeit match?') : t('Leave lobby?')),
            message: isHost
              ? t('Everyone in it will lose access and any save data will be gone.')
              : (isLiveMatch
                ? t('Leaving an unfinished match concedes it. Your opponent gets the win.')
                : t('You can rejoin later if you get invited again.')),
            confirmLabel: isHost ? t('Discard') : (isLiveMatch ? t('Forfeit') : t('Leave')),
            danger: true,
          });
          if (!ok) return;
          try {
            // /abandon cascades server-side into any linked draft_event
            // (abort + escrow refunds). No follow-up cancel call needed for
            // creators. A non-creator on a linked draft/sealed lobby must
            // leave via the draft event: the plain instance /leave route 409s
            // those rows ("leave drafts via the draft event"), and only the
            // draft-event leave releases the held entry-fee escrow.
            const route = resolveDiscardLeave(instance, myId);
            if (route.kind === 'abandonInstance') {
              await apiAbandonInstance(instance.id);
            } else if (route.kind === 'leaveDraftEvent') {
              const { leaveDraftEvent } = await import('./utils/arena/draftApi');
              await leaveDraftEvent(route.eventId);
            } else {
              await apiLeaveInstance(instance.id);
            }
          } catch (err) {
            // The row stays visible. A discard that didn't reach the
            // server must not pretend it worked.
            toast.error(err?.message || t('Could not remove the match. Try again.'));
            return;
          }
        }
        // Wipe any local blob still tied to this instance so a later
        // resume can't accidentally restore a discarded save.
        try {
          const { clearInstanceLocalBlob } = await import('./utils/game/instanceSaveAdapter');
          await clearInstanceLocalBlob(instance.id);
        } catch {}
        toast.success(t('Removed from save list'));
        // Poke every mounted list (SessionBrowser, queue indicator) to
        // re-fetch now that the server state changed, and drop the WS
        // subscription for the row we just retired.
        window.dispatchEvent(new CustomEvent('valkenhall:instances-changed'));
        this._ensureInstanceSubs();
        break;
      }
      default:
        console.warn('[app] unhandled save row action:', action);
    }
  };

  handleJoinPublic = async (instance) => {
    // Draft / sealed instances don't need a deck at join time.
    // decks are built during the draft itself. Delegate to
    // openDraftLobby, which already handles paymentMethod selection
    // (auto-prefers Keystone when available), WS subscribe to the
    // linked instance channel, and the readyCheckStarted race.
    // Calling joinDraftEvent here directly was bypassing all of
    // that: the server fell back to a coins-only debit, blocking
    // keystone-only players from entering and charging coins even
    // when the joiner had a free-entry Keystone available. Which
    // also made refunds come back in the wrong currency relative to
    // the player's expectations.
    if ((instance.mode === 'draft' || instance.mode === 'sealed')
        && instance.draft_event_id) {
      await this.openDraftLobby(instance.draft_event_id);
      return;
    }
    // Match / sandbox. Deck picker first, then apiJoinInstance.
    // Carry the host's format choice into the picker so a joiner
    // can only bring a deck that matches the lobby's format.
    this.setState({
      joiningPublicInstance: { instance },
      pendingQueueMode: instance.mode === 'sandbox' ? 'sandbox' : 'ranked',
      pendingQueueFormat: instance.mode === 'match' ? (instance.format || 'constructed') : null,
      arenaView: 'deck-select',
      // Joining from the hub's Open Tables returns to the hub.
      arenaBackTarget: this.state.arenaView === 'hub' ? 'hub' : 'play',
    });
  };

  handleApplyUpdate = () => {
    this.updateManager?.apply();
  };

  handleClaimSeasonReward = async (level) => {
    const { currentSeason, arenaProfile } = this.state;
    if (!currentSeason || !arenaProfile?.seasonProgress) return;

    // Server is authoritative. It validates the tier against its own
    // table + the player's stored progress, applies the reward
    // atomically, and returns the new totals + collection. The client
    // only sends the tier number.
    let result;
    try {
      result = await apiClaimSeasonTier(level);
    } catch (e) {
      const msg = e?.message || t('Failed to claim reward');
      console.error('Season tier claim failed:', e);
      toast.error(msg);
      return;
    }

    const updatedProfile = {
      ...arenaProfile,
      coins: result.newTotals?.coins ?? arenaProfile.coins,
      arcanaShards: result.newTotals?.arcanaShards ?? arenaProfile.arcanaShards,
      collection: result.collection ?? arenaProfile.collection,
      seasonProgress: result.seasonProgress ?? arenaProfile.seasonProgress,
    };
    this.setState({ arenaProfile: updatedProfile });

    // Resolve a display name for the foil reward against the local
    // catalog so we can toast a friendly message. The server only
    // returns cardId. The client owns the name lookup.
    const reward = result.reward || {};
    let foilName = null;
    if (reward.foilCardId && Array.isArray(this.state.sorceryCards)) {
      const card = this.state.sorceryCards.find((c) => c.unique_id === reward.foilCardId);
      foilName = card?.name || null;
    }

    if (reward.coins) toast.success(t('+{n} coins!', { n: reward.coins }));
    if (reward.arcanaShards) toast.success(t('+{n} Arcana!', { n: reward.arcanaShards }));
    if (foilName) toast.success(t('Foil {rarity}: {name}!', { rarity: reward.foilRarity, name: foilName }), { duration: 5000 });
  };

  openArcaneTrials = () => {
    playUI(UI.OPEN);
    if (!this.state.currentSeason) {
      this.initSeason();
    } else {
      // Already have a cached season. Refetch so the active-quest
      // counters reflect any progress earned between visits.
      this.refreshCurrentSeason();
    }
    this.setState({ arenaView: 'arcane-trials' });
  };

  isHubVisible() {
    return this.state.arenaView === 'hub';
  }

  openPlay = () => {
    playUI(UI.OPEN);
    this.setState({ arenaView: 'play' });
  };

  // Challenge any online player (friend or not) from the ladder or their
  // profile. Same setup + deck pick as a Friends match; the invite goes out
  // automatically once the lobby exists.
  handleChallenge = (player, modeKey = 'match', { setKey = null } = {}) => {
    if (!player?.id || player.id === this.state.arenaProfile?.id) return;
    playUI(UI.OPEN);
    this.setState({
      arenaView: 'play',
      pickerVisibility: 'friends',
      hubQuickHost: true,
      challengeTarget: { id: player.id, name: player.name || t('Player') },
      challengeSetKey: setKey,
      viewingFriendProfile: null,
      friendsOpen: false,
      mailboxOpen: false,
    }, () => this.handlePickMode(['sandbox', 'draft', 'sealed'].includes(modeKey) ? modeKey : 'match'));
  };

  // A looking-for-match offer and its lifecycle. 'matched' opens the
  // overlay (both players get it at a moment they were not watching for,
  // so it rings the same bell an invite does). 'accepted' is the partner's
  // yes. 'start' means both said yes and the server built what the match
  // needs. 'cancelled' is a decline / drop / timeout; the server re-lists
  // whoever was still willing (`requeued`).
  handleLfgEvent = (type, data) => {
    if (type === 'matched') {
      ringBell();
      this.setState({ lfgMatch: { ...data, phase: 'offer' }, showLfg: false });
      return;
    }
    const match = this.state.lfgMatch;
    if (!match || match.matchId !== data?.matchId) return;
    if (type === 'accepted') {
      // `accepted` lists every seat that has said yes so far. An older
      // server sends none: it only ever paired, so the one yes is everyone.
      const acceptedIds = data.accepted || [];
      const rest = match.opponents?.length ? match.opponents : [match.opponent].filter(Boolean);
      const opponentAccepted = data.accepted ? rest.every((o) => acceptedIds.includes(o.id)) : true;
      this.setState({ lfgMatch: { ...match, acceptedIds, opponentAccepted } });
    } else if (type === 'start') {
      this.proceedLfgMatch({ ...match, ...data });
    } else if (type === 'cancelled') {
      this.setState({ lfgMatch: null });
      const who = data.opponent?.name || t('Your opponent');
      if (data.reason === 'error') toast.error(t('Could not start the match. Back in the queue.'));
      else if (data.reason === 'timeout') toast(data.requeued ? t('{name} did not answer. Back in the queue.', { name: who }) : t('Match offer expired.'));
      else if (data.reason === 'payment') toast.error(data.requeued ? t('{name} could not pay the entry. Back in the queue.', { name: who }) : t('Not enough to pay the entry.'));
      else if (data.requeued) toast(t('{name} declined. Back in the queue.', { name: who }));
    }
  };

  acceptLfgMatch = (paymentMethod = null) => {
    const match = this.state.lfgMatch;
    if (!match) return;
    lfg.accept(paymentMethod);
    this.setState({ lfgMatch: { ...match, phase: 'accepted' } });
  };

  // Both accepted. Constructed / sandbox: on to the deck picker (host sets
  // the deck, guest joins with it). Draft / sealed: the server built the
  // event and charged both seats, so both land in its lobby, where the
  // ready check is already open.
  proceedLfgMatch = (match) => {
    const { kind, instanceId, eventId, mode, format, role } = match;
    this.setState({ lfgMatch: null });
    if (kind === 'event') {
      this.openDraftLobby(eventId);
      return;
    }
    this.refreshSavedDecks();
    this.setState({
      arenaView: 'deck-select',
      arenaBackTarget: 'hub',
      pendingQueueMode: mode === 'sandbox' ? 'sandbox' : 'ranked',
      pendingQueueFormat: mode === 'sandbox' ? null : (format || 'constructed'),
      lfgHostInstanceId: role === 'host' ? instanceId : null,
      joiningInstanceId: role === 'guest' ? instanceId : null,
      pickerVisibility: null,
      setupForMode: null,
      friendsOpen: false,
      mailboxOpen: false,
      viewingFriendProfile: null,
    });
  };

  // Decline. The server drops us from the registry and re-lists the partner.
  dismissLfgMatch = () => {
    this.setState({ lfgMatch: null });
    lfg.decline();
  };

  // Dev: open the overlay with a fake partner so the moment can be iterated
  // on without pairing two accounts.
  simulateLfgMatch = async (kind = 'instance', role = 'host', mode = 'ranked') => {
    // Borrow a real ladder row so the partner has art, a title and a rank.
    let opponent = { id: 'sim', name: 'Sylva Thornweave', profileAvatar: null, tier: 'master', division: 2 };
    try {
      const { getLeaderboard } = await import('./utils/arena/matchmakingApi');
      const page = await getLeaderboard({ offset: 0, limit: 3 });
      const row = (page?.rows || []).find((r) => r.id !== this.state.arenaProfile?.id && r.profileAvatar);
      if (row) opponent = { ...row, id: `sim-${row.id}` };
    } catch {}
    ringBell();
    const { entryCostForMode, keystoneCostForMode } = await import('./utils/arena/draftApi');
    const priced = kind === 'event' ? { entryCost: entryCostForMode(mode), keystoneCost: keystoneCostForMode(mode) } : {};
    this.setState({
      lfgMatch: { kind, mode, role, format: 'constructed', rules: 'manual', setKey: 'gothic', instanceId: null, opponent, phase: 'offer', expiresAt: Date.now() + 120000, ...priced },
    });
  };

  // Decks the player can actually bring to a queue: the same rule the
  // deck picker applies (ownership, ladder vs sandbox marking).
  getPlayableDecks = ({ sandbox = false } = {}) => {
    const all = this.getArenaSavedDecks() || [];
    const ownedMap = buildOwnedMap(this.state.arenaProfile?.collection || []);
    const cardCatalog = buildCardCatalog(this.state.sorceryCards);
    return all.filter((d) => {
      const deckMode = d.gameMode || 'constructed';
      if (!sandbox && deckMode === 'sandbox') return false;
      return canPlayDeck(d.cards || [], ownedMap, { cardCatalog, skipOwnership: sandbox, gameMode: deckMode }).canPlay;
    });
  };

  // --- Draft handlers ---

  openDraftBrowser = () => {
    playUI(UI.OPEN);
    this.setState({ arenaView: 'draft', arenaBackTarget: 'play' });
  };

  // Attach a draft event to the current session and watch its
  // lifecycle. No separate "draft lobby" screen anymore. The user
  // stays on whichever view they were on (SessionBrowser for the
  // creator, hub/social for an invitee) and the queue indicator
  // surfaces the pending draft. Phase transitions (drafting,
  // building, tournament, complete, cancelled) route the user
  // directly into the matching screen via a top-level watcher.
  // `paymentMethod` (coins | keystone) comes from the Notifications menu's
  // pay-with picker; without it the entry is auto-paid with Keystones when
  // the player holds enough, gold otherwise.
  openDraftLobby = async (eventId, { paymentMethod: chosenPayment } = {}) => {
    let mode = 'standard';
    try {
      const { getDraftEvent, joinDraftEvent, keystoneCostForMode } = await import('./utils/arena/draftApi');
      const ev = await getDraftEvent(eventId);
      if (ev?.mode === 'sealed') mode = 'sealed';
      const alreadyIn = !!ev?.participants?.some((p) => p.playerId === this.state.arenaProfile?.id);
      // Chat-invite flow: the invitee hasn't joined yet when they
      // click Accept. Fire joinDraftEvent eagerly. Server 409s if
      // they're already a participant (harmless), 402 if they can't
      // afford, 403 if the set is locked. Anything other than "in"
      // the event after this call → show the actionable error to the
      // user and bail. Auto-prefer Keystones (free entry) only when
      // the player holds enough for THIS mode. Sealed costs 2, draft
      // costs 1. Preferring keystone with too few made the server 402
      // "Insufficient keystones" even when the player could pay gold.
      if (!alreadyIn) {
        try {
          const keystoneCost = keystoneCostForMode(mode);
          const paymentMethod = chosenPayment
            || ((this.state.arenaProfile?.keystones || 0) >= keystoneCost ? 'keystone' : 'coins');
          await joinDraftEvent(eventId, { paymentMethod });
          if (!chosenPayment && paymentMethod === 'coins' && (ev?.entryCost || 0) > 0) {
            toast.info(t('Not enough Keystones. Entry paid with gold.'));
          }
        } catch (err) {
          const msg = String(err?.message || '');
          if (!/already/i.test(msg)) {
            toast.error(msg || t('Could not join event'));
            return;
          }
        }
      }
    } catch (err) {
      console.warn('[openDraftLobby] event lookup failed:', err);
    }
    // Find the linked instance so checkpoint save hooks know where
    // to write. Best-effort; no dedicated endpoint.
    let linkedInstance = null;
    try {
      const { listInstances } = await import('./utils/arena/instancesApi');
      const list = await listInstances();
      linkedInstance = list.find((i) => i.draft_event_id === eventId) || null;
    } catch {}
    this.setState({
      draftEventId: eventId,
      draftPhase: 'lobby',
      draftEventMode: mode,
      draftQueueOpen: false,
      ...(linkedInstance ? {
        currentDraftInstanceId: linkedInstance.id,
        currentDraftInstanceVisibility: linkedInstance.visibility,
        currentDraftInstanceIsHost: (linkedInstance.participants || [])
          .some((p) => p.userId === this.state.arenaProfile?.id && p.role === 'creator'),
      } : {}),
    });
    // Attach the WS subscription to the linked instance BEFORE
    // anything else. On pod-fill the server fires readyCheckStarted
    // on the instance channel. If the joiner hasn't subscribed
    // yet (their listInstances at boot didn't include this
    // instance), they miss the broadcast and never see the modal.
    // The host's screen lights up because they were already
    // subscribed. reconcileInstance then covers the race where the
    // broadcast fires before the sub attaches, by deriving the view
    // from the freshly-fetched instance locally.
    if (linkedInstance) {
      try { await this._ensureInstanceSubs(); } catch {}
      this.reconcileInstance(linkedInstance);
    }
    this._startDraftEventWatcher(eventId);
  };

  // Listens for draft lifecycle events and routes the user into
  // the appropriate screen as phases transition. Combines a WS
  // subscription (fast path) with a 2s poll (fallback if the WS
  // blips). Idempotent against the already-fired flags.
  _startDraftEventWatcher = (eventId) => {
    if (!eventId) return;
    if (this._draftWatchEventId === eventId) return;
    this._stopDraftEventWatcher();
    this._draftWatchEventId = eventId;
    this._draftWatchFired = {};
    import('./utils/arena/draftApi').then(({ subscribeToDraftEvents, getDraftEvent, getDraftPickHistory }) => {
      if (this._draftWatchEventId !== eventId) return;
      const fireStarted = (d) => {
        const fired = this._draftWatchFired;
        if (fired.started) return;
        fired.started = true;
        // Sealed skips the picking phase. Server goes draft:started
        // → auto-records pool → draft:building, so we wait for the
        // building event to route. Standard routes NOW so the
        // DraftPicker mounts before the subsequent draft:pack message
        // arrives on the wire.
        if (d?.mode === 'sealed' || this.state.draftEventMode === 'sealed') return;
        this.handleDraftStarted({ eventId, mode: d?.mode || 'standard', seats: d?.seats });
      };
      const fireBuilding = async (d) => {
        const fired = this._draftWatchFired;
        if (fired.building || fired.buildingInFlight) return;
        fired.buildingInFlight = true;
        try {
          const mode = d?.mode || this.state.draftEventMode || 'standard';
          let picks = d?.draftedCards || [];
          // The live draft:building WS event includes draftedCards; a
          // poll-based resume (sealed cracking the pool, or any mode where the
          // player reconnects after the pod already transitioned to building)
          // does not. Fetch the pick history when picks are empty so the deck
          // builder never mounts without the player's pool.
          if (!picks.length) {
            try {
              const res = await getDraftPickHistory(eventId);
              picks = res?.picks || [];
            } catch {
              picks = [];
            }
          }
          // Never commit the building transition with an empty pool. A transient
          // fetch failure must not strand the player on an empty deck builder.
          // leave fired.building false so the 2s poll retries, and never wipe a
          // pool we already have.
          const resolved = resolveDraftPool({ incoming: picks, current: this.state.draftedCards });
          if (!resolved.length) return;
          fired.building = true;
          this.handleDraftBuildingPhase({ eventId, draftedCards: resolved, setKey: d?.setKey, mode });
        } finally {
          fired.buildingInFlight = false;
        }
      };
      const fireTournament = (d) => {
        const fired = this._draftWatchFired;
        if (fired.tournament) return;
        fired.tournament = true;
        this.handleDraftTournamentStart({ eventId, mode: d?.mode });
      };
      const fireComplete = (d) => {
        const fired = this._draftWatchFired;
        if (fired.complete) return;
        fired.complete = true;
        this.handleDraftComplete({ eventId, mode: d?.mode });
      };
      // WS path: fire phase handlers synchronously off the payload
      // so the routed screen is mounted before the next message
      // (draft:pack, draft:round_start) arrives. Going through an
      // HTTP fetch first reintroduces the race that leaves
      // DraftPicker stuck on "Waiting for packs..." when the pack
      // message is delivered to no listener.
      this._draftWatchUnsub = subscribeToDraftEvents({
        started: (d) => { if (d?.eventId === eventId) fireStarted(d); },
        building: (d) => { if (d?.eventId === eventId) fireBuilding(d); },
        round_start: (d) => { if (d?.eventId === eventId) fireTournament(d); },
        complete: (d) => { if (d?.eventId === eventId) fireComplete(d); },
        cancelled: (d) => { if (d?.eventId === eventId) this.handleDraftCancelled?.(); },
      });
      // Poll fallback: catches phase transitions that landed before
      // the watcher attached (resume into a drafting/tournament
      // event) or any WS drop. Uses the full event status since we
      // have no payload here.
      const consumeStatus = async (event) => {
        if (!event || this._draftWatchEventId !== event.id) return;
        if (event.status === 'drafting') fireStarted({ mode: event.mode, seats: event.seats });
        else if (event.status === 'building') await fireBuilding({ mode: event.mode, setKey: event.setKey });
        else if (event.status === 'tournament') fireTournament({ mode: event.mode });
        else if (event.status === 'complete') fireComplete({ mode: event.mode });
        else if (event.status === 'cancelled') this.handleDraftCancelled?.();
      };
      this._draftWatchPoll = setInterval(() => {
        if (this._draftWatchEventId !== eventId) return;
        getDraftEvent(eventId).then(consumeStatus).catch(() => {});
      }, 2000);
      getDraftEvent(eventId).then(consumeStatus).catch(() => {});
    }).catch((err) => {
      console.warn('[draft] watcher setup failed:', err?.message || err);
    });
  };

  _stopDraftEventWatcher = () => {
    this._draftWatchEventId = null;
    this._draftWatchFired = null;
    if (this._draftWatchUnsub) { try { this._draftWatchUnsub(); } catch {} this._draftWatchUnsub = null; }
    if (this._draftWatchPoll) { clearInterval(this._draftWatchPoll); this._draftWatchPoll = null; }
  };





  // --- Draft blob checkpoint helper ---
  //
  // Persists a snapshot of the draft state to the host's local
  // blob at a phase transition. Host-only; guests no-op because
  // they don't own the save file. Safe to call even if the
  // instance linkage isn't set. Returns silently.
  // Host-side helper: when resuming a paused draft, send a
  // fresh draft-invite chat to every other participant so the
  // pause→active hand-off surfaces in their Queue dropdown and
  // Social tab. Uses the existing sendChatMessage pipe; the
  // metadata.resume flag flags the message so accepting doesn't
  // charge entry cost again (server's joinEvent 409s on 'already
  // joined' which is harmless on accept. No re-pay).
  _rebroadcastDraftInvites = async (instance) => {
    const myId = this.state.arenaProfile?.id;
    const others = (instance.participants || [])
      .filter((p) => p.userId && p.userId !== myId);
    if (!others.length) return;
    const setLabels = { beta: 'Beta', arthurian: 'Arthurian Legends', gothic: 'Gothic' };
    const setKey = instance.format || null;
    const setLabel = setKey ? (setLabels[setKey] || setKey) : null;
    const metadata = {
      eventId: instance.draft_event_id,
      instanceId: instance.id,
      mode: instance.mode,
      setKey,
      setLabel,
      podSize: instance.seats_max,
      resume: true,
    };
    await Promise.all(others.map((p) => (
      sendChatMessage(p.userId, {
        type: 'draft-invite',
        body: 'is ready to resume the draft. Rejoin when you\u2019re back!',
        metadata,
      }).catch((err) => console.warn('[draft] resume invite failed:', err?.message))
    )));
  };

  // Mirror of _rebroadcastDraftInvites but for match / sandbox
  // instances. Fires a match-invite chat to every non-host
  // participant so the resume shows up in their Queue dropdown
  // even if their WS sub missed the readyCheckStarted broadcast
  // (offline, app restart, laggy network).
  // Promise-returning themed confirm. Resolves to true when the
  // user clicks the primary action, false otherwise (cancel,
  // backdrop click, Escape). Drop-in replacement for window.confirm
  // with a few extra knobs (title, button labels, danger styling).
  _askConfirm = ({
    title, message, confirmLabel, cancelLabel, danger = false,
  } = {}) => new Promise((resolve) => {
    const done = (ok) => {
      this.setState({ pendingConfirm: null });
      resolve(ok);
    };
    this.setState({
      pendingConfirm: {
        title, message, confirmLabel, cancelLabel, danger,
        onConfirm: () => done(true),
        onCancel: () => done(false),
      },
    });
  });

  // Host-side: when resuming a paused match, send each other
  // participant a server-side resume invitation. It surfaces in their
  // "Queue & Invites" menu (GET /instances/invited, kind='resume');
  // accepting calls /resume-start. Replaces the old match-invite chat.
  _rebroadcastMatchInvites = async (instance) => {
    const myId = this.state.arenaProfile?.id;
    const others = (instance.participants || [])
      .filter((p) => p.userId && p.userId !== myId);
    if (!others.length) return;
    await Promise.all(others.map((p) => (
      apiInviteToInstance(instance.id, p.userId, 'resume')
        .catch((err) => console.warn('[match] resume invite failed:', err?.message))
    )));
  };

  _persistDraftCheckpoint = async (mutate) => {
    const {
      currentDraftInstanceId: instanceId,
      currentDraftInstanceVisibility: visibility,
      currentDraftInstanceIsHost: isHost,
    } = this.state;
    if (!instanceId || !isHost || !visibility) return;
    try {
      const [
        { loadDraftInstance, autoSaveDraftInstance },
        draftSaveState,
      ] = await Promise.all([
        import('./utils/game/instanceSaveAdapter'),
        import('./utils/game/draftSaveState'),
      ]);
      const prev = await loadDraftInstance(instanceId);
      const base = prev || draftSaveState.createInitialDraftState({
        instanceId,
        visibility,
        hostUserId: this.state.arenaProfile?.id,
      });
      const next = mutate(base, draftSaveState);
      if (!next) return;
      await autoSaveDraftInstance({
        instanceId,
        visibility,
        draftState: next,
      });
    } catch (err) {
      console.warn('[draft] checkpoint persist failed:', err?.message || err);
    }
  };

  _handleDraftPickConfirmed = (data) => {
    // The server's draft:pick_confirmed event carries the pick
    // details but does NOT always include a playerId. In the
    // standard draft protocol it's sent back to the picking seat
    // as a confirmation of their own pick. draft:auto_picked is
    // also sent only to the affected seat. In either case, the
    // host's client could receive events for its own picks (good)
    // OR, if broadcast semantics change, for other seats' picks
    // (where a missing playerId would be ambiguous).
    //
    // Rather than silently crediting a pick to the host, only
    // record when we can identify the picker with high confidence:
    // either data.playerId is explicit, or we can infer it's the
    // current user from the event being delivered to them
    // personally (the current server behaviour).
    const card = data?.card;
    if (!card) return;
    const userId = data?.playerId || this.state.arenaProfile?.id;
    if (!userId) return;
    // Guard: if the explicit playerId is absent AND we're not
    // the host, skip. We can't know whose pick this was without
    // it, and attributing to the host would corrupt multi-seat
    // pick history.
    if (!data?.playerId && !this.state.currentDraftInstanceIsHost) return;
    this._persistDraftCheckpoint((state, { recordPick }) => (
      recordPick(state, { userId, cardId: card.cardId })
    ));
  };

  _handleDraftRoundResult = (data) => {
    const pairings = data?.pairings || [];
    if (!pairings.length) return;
    // Note: parentheses are load-bearing. Earlier iteration had
    // `Number(...) || (...) || this.state.draftEventId ? X : 0`
    // which `||`-associated the ternary condition and always
    // resolved to an undefined `currentRoundNumber`, writing
    // round-N results into round-N-1 on the blob.
    const roundNumber = Number(data?.round)
      || (pairings[0] && pairings[0].round)
      || 0;
    this._persistDraftCheckpoint((state, { recordPairingResult }) => {
      const target = roundNumber > 0 ? roundNumber : state.roundNumber || 1;
      const existingRound = state.rounds.find((r) => r.roundNumber === target);
      if (!existingRound) return null;
      let next = state;
      existingRound.pairings.forEach((p, idx) => {
        const fresh = pairings.find((x) =>
          (x.player1Id === p.player1Id && x.player2Id === p.player2Id)
          || x.id === p.matchId,
        );
        if (fresh && fresh.winnerId && !p.winnerId) {
          next = recordPairingResult(next, {
            roundNumber: target,
            pairingIdx: idx,
            winnerId: fresh.winnerId,
            forfeit: !!fresh.forfeit,
          });
        }
      });
      return next;
    });
  };

  handleDraftStarted = (data) => {
    // Sealed has no picking phase. The server records the full
    // 90-card pool on start and immediately fires draft:building.
    // Bailing out here keeps us from flashing the empty DraftPicker
    // between draft:started and draft:building; DraftLobby stays up
    // until building arrives and routes the user into the pack-
    // opening ceremony.
    if (data?.mode === 'sealed' || this.state.draftEventMode === 'sealed') return;
    this.setState({ arenaView: 'draft-picking', draftPhase: 'picking' });
    // Checkpoint: entering PICKING. Seats are now known so we can
    // record the full participant roster.
    this._persistDraftCheckpoint((state, { advancePhase, addParticipant, DRAFT_PHASES }) => {
      let next = state;
      for (const seat of (data?.seats || [])) {
        next = addParticipant(next, {
          userId: seat.playerId,
          name: seat.playerName || null,
        });
      }
      return advancePhase(next, DRAFT_PHASES.PICKING);
    });
  };

  handleDraftBuildingPhase = (data) => {
    const isSealed = (data?.mode === 'sealed') || this.state.draftEventMode === 'sealed';
    this.setState({
      // Sealed goes through a pack-opening ceremony first. The user
      // cracks all 6 packs (no individual card picking, the pool is
      // already theirs) and only then lands in the deck builder.
      // Standard draft transitions straight into the builder because
      // the picking phase already handled card selection.
      arenaView: isSealed ? 'sealed-reveal' : 'draft-building',
      draftPhase: isSealed ? 'sealed-reveal' : 'building',
      draftedCards: resolveDraftPool({ incoming: data?.draftedCards, current: this.state.draftedCards }),
      draftEventSetKey: data?.setKey || this.state.draftEventSetKey || null,
    });
    // Checkpoint: entering DECKBUILDING. For sealed the blob also
    // captures the auto-dealt full pool (90 cards) so a resume can
    // rehydrate picks without going back to the server.
    this._persistDraftCheckpoint((state, { advancePhase, DRAFT_PHASES }) => {
      const myId = this.state.arenaProfile?.id;
      const myPicks = (data?.draftedCards || []).map((c) => c.cardId || c);
      const nextParticipants = state.participants.map((p) =>
        p.userId === myId
          ? { ...p, draftedCards: myPicks.length ? myPicks : p.draftedCards }
          : p,
      );
      return advancePhase(
        { ...state, participants: nextParticipants },
        DRAFT_PHASES.DECKBUILDING,
      );
    });
    // Server grants drafted cards at the picking→building transition so the
    // player actually owns what they drafted. Refresh the profile so the
    // deck builder's ownership display and any downstream gate reflect it.
    getStoredToken().then((token) => {
      if (token) loadArenaProfile(token).then((p) => {
        if (p) this.setState({ arenaProfile: this.profileFromServer(p, token) });
      }).catch(() => {});
    });
  };

  // Called by SealedPackOpener when the user clicks past the final
  // pack. Transitions the arena view into the deck builder with the
  // already-granted 90-card pool.
  handleSealedRevealComplete = () => {
    this.setState({ arenaView: 'draft-building', draftPhase: 'building' });
  };

  // After a player submits their deck, drop them on the tournament dashboard
  // (the waiting room) instead of a blocking overlay. The round may not have
  // started yet (others still building). The dashboard shows a waiting state
  // and lets them leave; round_start swaps in the bracket when it fires.
  handleDeckSubmitted = () => {
    playUI(UI.CONFIRM);
    this.setState({ arenaView: 'draft-tournament', draftPhase: 'tournament' });
  };

  handleDraftTournamentStart = (data) => {
    // Sealed skips the tournament browser. There's only one pairing
    // and it's between the two friends who started the event, so
    // drop straight into the match room instead.
    if (data?.mode === 'sealed' && data.myRoomId) {
      this.handleDraftPlayMatch(data.myRoomId);
      return;
    }
    this.setState({ arenaView: 'draft-tournament', draftPhase: 'tournament' });
    // Checkpoint: entering ROUND N. Record the round's pairings so
    // a mid-tournament exit can restore the bracket view on resume.
    this._persistDraftCheckpoint((state, { advancePhase, setRoundPairings, DRAFT_PHASES }) => {
      const roundNumber = Number(data?.round) || state.roundNumber + 1 || 1;
      const pairings = (data?.pairings || []).map((p) => ({
        player1Id: p.player1Id || p.player1 || p.playerId || null,
        player2Id: p.player2Id || p.player2 || null,
      })).filter((p) => p.player1Id);
      let next = state;
      if (pairings.length) {
        next = setRoundPairings(next, { roundNumber, pairings });
      }
      return advancePhase(next, DRAFT_PHASES.ROUND, { roundNumber });
    });
  };

  // ── Match replay playback ─────────────────────────────────────────
  // Fetch the per-perspective replay view from the server, build the playback
  // driver, and mount the GameBoard through the spectate path with the driver
  // standing in for the socket. The overlay (chronicle + transport) renders on
  // top via replaySession.
  // From the result screen: tear the live board down first, then mount the
  // replay of the match that just ended.
  handleWatchReplayFromResult = (roomCode) => {
    const opponentName = this.state.arenaMatchmakingOpponent?.name;
    this.handleGameBoardExit();
    this.handleWatchReplay(roomCode, { opponentName });
  };

  // Fires once both preconditions of a pending /replay/<token> URL hold:
  // the catalog is loaded and the auth check has settled.
  _maybeOpenReplayLink = async () => {
    const token = this._pendingReplayLink;
    if (!token || !this.state.sorceryCards || this.state.authChecking) return;
    this._pendingReplayLink = null;
    try {
      const { roomCode } = await resolveReplayLink(token);
      await this.handleWatchReplay(roomCode, { link: token });
    } catch (err) {
      toast.error(err?.message || t('This replay link is not valid.'));
    } finally {
      this.setState({ replayLinkPending: false });
    }
  };

  // A link viewer wants to comment: drop to the login screen, and reopen the
  // same replay once they are in (the link goes back to pending).
  handleReplayRequestLogin = () => {
    const link = this.state.replaySession?.link;
    this.handleLeaveReplay();
    if (link) {
      this._pendingReplayLink = link;
      window.history.replaceState(null, '', `/replay/${link}`);
    }
  };

  handleWatchReplay = async (roomCode, { opponentName, link = null, noteKey = null, commentId = null } = {}) => {
    if (!roomCode || this.state.replaySession || this._replayFetchInFlight) return;
    this._replayFetchInFlight = true;
    const toastId = toast.loading(t('Loading replay...'));
    try {
      // Always the full-information view: a replay happens after the match,
      // so both hands and every identity are visible. The only choice left
      // to the viewer is WHICH SEAT'S SIDE to watch from (replayViewpoint).
      const [view, commentsRes] = await Promise.all([
        fetchReplay(roomCode, 'omniscient', link),
        fetchReplayComments(roomCode, link).catch(() => ({ comments: {} })),
      ]);
      const driver = new ReplayDriver(view);
      const ownName = this.state.arenaProfile?.name || t('You');
      // Solo: the pilot played both seats, so "name vs name" is meaningless.
      // Label the seats by their Avatars instead.
      const avatars = view.meta?.avatars || {};
      const names = view.meta?.solo
        ? { p1: avatars.p1?.name || ownName, p2: avatars.p2?.name || ownName }
        : !view.ownSeat
          // Shared replay: the viewer played neither seat, so label both
          // sides by their Avatars.
          ? { p1: avatars.p1?.name || t('Player 1'), p2: avatars.p2?.name || t('Player 2') }
          : view.ownSeat === 'p2'
            ? { p1: opponentName || t('Opponent'), p2: ownName }
            : { p1: ownName, p2: opponentName || t('Opponent') };
      toast.dismiss(toastId);
      this.setState({
        replaySession: {
          roomCode, view, driver, names, link,
          // Notification deep link: open this row's note thread and light
          // up the comment (ReplayOverlay initialNoteKey / highlightCommentId).
          noteKey, commentId,
          comments: commentsRes.comments || {},
          // Start on your own side of the table (p1 for solo/spectator).
          viewpoint: view.ownSeat === 'p2' ? 'p2' : 'p1',
        },
        viewingFriendProfile: null,
        isSpectating: true,
        isGameBoardOpen: true,
        sessionMode: 'spectate',
        roomCode,
        isArenaMatch: true,
        isRankedMatch: false,
        isCasualSandbox: false,
        currentAutomations: view.base?.automations !== false,
        // Same defensive clears as handleDraftSpectate: leaked instance/draft
        // context would give the replay mount a real save scope to write to.
        ...CLEARED_MATCH_CONTEXT,
      });
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.message || t('Failed to load the replay.'));
    } finally {
      this._replayFetchInFlight = false;
    }
  };

  // Swap which seat's side of the table the board is viewed from. Purely
  // client-side and in place: the view and driver stay (same full-information
  // data), the mounted board flips its seat (replayViewpoint controller) and
  // the driver re-emits its current frame for it. Nothing remounts.
  handleSetReplayViewpoint = (viewpoint) => {
    const session = this.state.replaySession;
    if (!session || session.viewpoint === viewpoint) return;
    this.setState({ replaySession: { ...session, viewpoint } });
  };

  // Post a comment on a replay feed row. Optimistic: the row's thread gets
  // the comment immediately (a viewpoint swap remounts the overlay and must
  // not lose it), then the server's row replaces it so the id, timestamp and
  // like state are the server's.
  handlePostReplayComment = async (key, body) => {
    const session = this.state.replaySession;
    const profile = this.state.arenaProfile;
    if (!session || !profile || !body.trim()) return;
    const tempId = `pending-${Date.now()}`;
    const optimistic = {
      id: tempId,
      playerId: profile.id,
      playerName: profile.name,
      playerAvatar: profile.profileAvatar,
      playerDisplayedCrownAvatarId: profile.displayedCrownAvatarId || null,
      playerIsFounder: !!profile.isFounder,
      playerIsQaTester: !!profile.isQaTester,
      body,
      createdAt: new Date().toISOString(),
      likes: 0,
      liked: false,
    };
    this.patchReplayThread(key, (thread) => [...thread, optimistic]);
    try {
      const saved = await postReplayComment(session.roomCode, key, body, session.link);
      this.patchReplayThread(key, (thread) => thread.map((c) => (c.id === tempId ? saved : c)));
    } catch (err) {
      console.warn('[replay] comment post failed:', err?.message || err);
      this.patchReplayThread(key, (thread) => thread.filter((c) => c.id !== tempId));
      toast.error(t('Failed to post the comment.'));
      // Rethrow so the composer keeps the draft: the row it optimistically
      // showed is gone, and retyping a lost comment is the worse failure.
      throw err;
    }
  };

  handleDeleteReplayComment = async (key, commentId) => {
    const session = this.state.replaySession;
    if (!session) return;
    const previous = session.comments[key] || [];
    this.patchReplayThread(key, (thread) => thread.filter((c) => c.id !== commentId));
    try {
      await deleteReplayComment(session.roomCode, commentId);
    } catch (err) {
      console.warn('[replay] comment delete failed:', err?.message || err);
      this.patchReplayThread(key, () => previous);
      toast.error(t('Failed to delete the comment.'));
    }
  };

  handleToggleReplayCommentLike = async (key, commentId) => {
    const session = this.state.replaySession;
    if (!session) return;
    try {
      const result = await toggleReplayCommentLike(session.roomCode, commentId, session.link);
      this.patchReplayThread(key, (thread) => thread.map((c) => (
        c.id === commentId ? { ...c, liked: !!result.liked, likes: result.likes || 0 } : c
      )));
    } catch (err) {
      console.warn('[replay] comment like failed:', err?.message || err);
      toast.error(t('Failed to like the comment.'));
    }
  };

  // Rewrite one row's thread in the live replay session. Empty threads drop
  // out of the map so the feed's "has comments" marker stays truthful.
  patchReplayThread = (key, update) => {
    this.setState((state) => {
      const session = state.replaySession;
      if (!session) return null;
      const comments = { ...session.comments };
      const thread = update(comments[key] || []);
      if (thread.length) comments[key] = thread;
      else delete comments[key];
      return { replaySession: { ...session, comments } };
    });
  };

  // Rename or pin one of the player's matches: optimistic local update
  // (pinned rows float to the top, matching the server's sort), then persist.
  handleUpdateMatchMeta = (roomCode, patch) => {
    const profile = this.state.arenaProfile;
    if (!profile || !roomCode) return;
    this.setState({ arenaProfile: { ...profile, matchHistory: applyMatchMetaPatch(profile.matchHistory, roomCode, patch) } });
    updateMatchMeta(roomCode, patch).catch((err) => {
      console.warn('[replay] match meta update failed:', err?.message || err);
      toast.error(t('Failed to update the match.'));
    });
  };

  handleLeaveReplay = () => {
    this.state.replaySession?.driver?.destroy();
    this.setState({ replaySession: null, currentAutomations: true });
    this.handleLeaveSpectate();
    if (window.location.pathname.startsWith('/replay/')) window.history.replaceState(null, '', '/');
  };

  handleCopyReplayLink = async () => {
    const session = this.state.replaySession;
    if (!session) return;
    try {
      const { token } = await createReplayLink(session.roomCode, session.link);
      // Same web target as the channel this client talks to: the beta server's
      // links must open on qa, whose bundle and server both know the token.
      const host = isWeb() ? window.location.origin
        : getUpdateChannel() === 'beta' ? 'https://qa.valkenhall.com' : 'https://play.valkenhall.com';
      await navigator.clipboard.writeText(`${host}/replay/${token}`);
      toast.success(t('Link copied. Anyone with it can watch this replay.'));
    } catch (err) {
      toast.error(err?.message || t('Failed to copy the link.'));
    }
  };

  // Send the open replay to a friend as a chat attachment (the same card
  // the DM replay picker sends). Only a participant may share this way; the
  // server rejects everyone else.
  handleShareReplayToFriend = async (friend) => {
    const session = this.state.replaySession;
    const profile = this.state.arenaProfile;
    if (!session || !friend?.id || !profile) return;
    const { view, names } = session;
    // The sender's own row for this match, when they still hold it: the
    // custom name, and on a solo replay the only record of which seat won.
    const historyRow = (profile.matchHistory || []).find((m) => m.roomCode === session.roomCode) || null;
    try {
      await sendChatMessage(friend.id, {
        type: 'replay',
        metadata: { ...replayMetaFromView({ view, names, profile, historyRow }), roomCode: session.roomCode },
      });
      toast.success(t('Replay sent to {name}.', { name: friend.name }));
    } catch (err) {
      toast.error(err?.message || t('Failed to share the replay.'));
    }
  };

  // Post the open replay to the Tavern: the same card as a DM share. The
  // server pins the match's public link onto it, so every reader can watch.
  handleShareReplayToTavern = async () => {
    const session = this.state.replaySession;
    const profile = this.state.arenaProfile;
    if (!session || !profile) return;
    const { view, names } = session;
    const historyRow = (profile.matchHistory || []).find((m) => m.roomCode === session.roomCode) || null;
    try {
      await postReplayToTavern({ ...replayMetaFromView({ view, names, profile, historyRow }), roomCode: session.roomCode });
      toast.success(t('Posted to the Tavern.'));
    } catch (err) {
      toast.error(err?.message || t('Failed to post the replay.'));
    }
  };

  // A replay opened from the social drawer's Tavern tab: close the drawer
  // before the board mounts, as the DM panel does.
  // A forwarded Tavern message in a DM: the same route a reply
  // notification takes, so the drawer opens on the Tavern tab and the view
  // scrolls to the message. Cleared first so the same card opens twice.
  handleOpenTavernMessage = (messageId) => {
    if (!messageId) return;
    this.setState({ focusGlobalMessageId: null }, () => {
      this.setState({ mailboxOpen: true, mailboxChatFriend: null, focusGlobalMessageId: messageId });
    });
  };

  handleWatchReplayFromMailbox = (roomCode, opts) => {
    this.setState({ mailboxOpen: false, mailboxChatFriend: null });
    this.handleWatchReplay(roomCode, opts);
  };

  handleDraftSpectate = (roomCode) => {
    // Defensively clear any match-context state the user might have
    // inherited from a prior pairing. If currentDraftMatchId or
    // currentInstanceId leaks into the spectator's GameBoard props,
    // _instanceSaveId() resolves to a real save id and the spectated
    // state gets stamped into the user's local blob. The next "Play
    // Match" then resumes from that poisoned blob and the player
    // lands on the spectated game's board instead of their own.
    this.setState({
      isSpectating: true,
      isGameBoardOpen: true,
      spectateRoomCode: roomCode,
      sessionMode: 'spectate',
      roomCode: roomCode,
      isArenaMatch: true,
      isRankedMatch: false,
      currentDraftMatchId: null,
      currentInstanceId: null,
      currentInstanceIsHost: false,
      currentInstanceVisibility: null,
      currentInstanceMode: null,
      isResumeJoin: false,
    });
  };

  _handleDraftMatchReadyCheck = async (data) => {
    if (!data?.eventId || data.eventId !== this.state.draftEventId) return;
    if (!data.matchId) return;
    // We're ALREADY in this match and the opponent is reconnecting. Don't
    // pop a redundant "Ready?" modal over our live board. Auto-confirm so
    // their match_start fires and they rejoin; we keep playing uninterrupted.
    if (this.state.isGameBoardOpen && this.state.currentDraftMatchId === data.matchId) {
      try {
        const { confirmDraftMatchReady } = await import('./utils/arena/draftApi');
        await confirmDraftMatchReady(data.eventId, data.matchId);
      } catch (err) {
        console.warn('[draft] auto-confirm ready (already in match) failed:', err?.message || err);
      }
      return;
    }
    // Prefer the per-recipient opponent the server now embeds in the
    // payload. 4+ player pods can have multiple matches in flight in
    // the same round, and the legacy "fetch standings + find by
    // matchId" path races with the server's currentRound derivation
    // when result-reporting and startRound interleave.
    let opponentName = data.opponent?.name || t('Opponent');
    if (!data.opponent?.name) {
      try {
        const { getDraftStandings } = await import('./utils/arena/draftApi');
        const standings = await getDraftStandings(data.eventId);
        const pairing = (standings?.currentPairings || []).find((p) => p.matchId === data.matchId || p.id === data.matchId);
        const myId = this.state.arenaProfile?.id;
        const opp = pairing && (pairing.player1Id === myId
          ? { id: pairing.player2Id, name: pairing.player2Name }
          : { id: pairing.player1Id, name: pairing.player1Name });
        if (opp?.name) opponentName = opp.name;
      } catch {}
    }
    this.setState({
      draftMatchReadyCheck: { eventId: data.eventId, matchId: data.matchId, opponentName },
    });
  };

  // The opponent left the ready-check while we were waiting. Don't strand
  // us on a silent "waiting for others." Tell us, and drop the modal back
  // to the dashboard where the No-show? claim is available after the grace.
  _handleDraftMatchReadyLeft = (data) => {
    if (!data?.matchId) return;
    const rc = this.state.draftMatchReadyCheck;
    if (!rc || rc.matchId !== data.matchId) return;
    toast.message(t('Your opponent stepped away. You can wait, or claim the win if they do not return.'));
    this.setState({ draftMatchReadyCheck: null });
  };

  _handleDraftMatchStart = async (data) => {
    if (!data?.eventId || data.eventId !== this.state.draftEventId) return;
    if (!data.roomId) return;
    // Server now embeds the per-recipient opponent in match_start.
    // Prefer that authoritative payload over any standings lookup.
    // it eliminates the round-races that produced the wrong-opponent
    // HUD in 4+ player pods.
    let opponentInfo = null;
    let opp = data.opponent?.id ? { id: data.opponent.id, name: data.opponent.name } : null;
    if (!opp) {
      try {
        const { getDraftStandings } = await import('./utils/arena/draftApi');
        const standings = await getDraftStandings(this.state.draftEventId);
        const myId = this.state.arenaProfile?.id;
        const pairing = (standings?.currentPairings || []).find((p) => p.matchId === data.matchId || p.id === data.matchId);
        opp = pairing && (pairing.player1Id === myId
          ? { id: pairing.player2Id, name: pairing.player2Name }
          : { id: pairing.player1Id, name: pairing.player1Name });
      } catch {}
    }
    if (opp?.id) {
      try {
        const { getPublicProfile } = await import('./utils/friendsApi');
        const profile = await getPublicProfile(opp.id);
        opponentInfo = {
          id: opp.id,
          name: profile?.name || opp.name || t('Opponent'),
          profileAvatar: profile?.profileAvatar,
          avatarUrl: profile?.profileAvatar ? this.getArenaAvatarUrl(profile.profileAvatar) : null,
          discordHandle: profile?.discordHandle || null,
        };
      } catch {
        opponentInfo = { id: opp.id, name: opp.name || t('Opponent') };
      }
    }
    this.setState({
      draftMatchReadyCheck: null,
      isGameBoardOpen: true,
      sessionMode: 'join',
      roomCode: data.roomId,
      isArenaMatch: true,
      isRankedMatch: false,
      arenaMatchmakingOpponent: opponentInfo,
      // Scopes the GameBoard's local save to this specific round
      // match. Without it, every round would autosave into the
      // same draft-instance blob and the previous round's state
      // would stomp the new one. With it set, autoSave writes to
      // a per-match blob that resume can load back.
      currentDraftMatchId: data.matchId || null,
      // Server flips match.status → 'in_progress' on first ready-
      // check; subsequent match_starts carry resumed=true. The
      // guest needs this flag so componentDidMount skips the
      // fresh-deck-spawn + opening-draw branch and instead
      // requests a state:sync from the host. Without it, the
      // guest re-draws their opening hand on every rejoin.
      isResumeJoin: !!data.resumed,
    });
  };

  // DraftTournament's "Join Match" button. Instead of jumping
  // straight into the P2P room (which could point at a stale
  // roomId if the other seat dropped and killed the room), we
  // trigger a per-match ready-check. Both players see the "Are
  // you ready?" modal. When both confirm, the server ensures the
  // room exists (recreating it if needed) and fires match_start
  // with the authoritative roomId, which _handleDraftMatchStart
  // consumes to mount the GameBoard.
  handleDraftPlayMatch = async (pairing) => {
    // Back-compat: older callers passed a roomId string. Accept
    // both shapes so the routing lands on the new ready-check.
    const matchId = typeof pairing === 'object' ? pairing?.matchId : null;
    if (!matchId) {
      toast.error(t('Match is not ready yet.'));
      return;
    }
    try {
      const { requestDraftMatchStart } = await import('./utils/arena/draftApi');
      await requestDraftMatchStart(this.state.draftEventId, matchId);
    } catch (err) {
      toast.error(err?.message || t('Could not start match.'));
    }
  };

  // Accept a draft-MATCH invite from the queue. Set the tournament context
  // first. _handleDraftMatchStart drops a match_start whose eventId !=
  // draftEventId, and Exit Game routes back to the tournament screen off
  // draftPhase/draftEventId. Then confirm ready, which fires match_start and
  // drops us straight into the match (no lobby/dashboard stop).
  handleAcceptDraftMatchInvite = async (eventId, matchId) => {
    let mode = 'standard';
    let linkedInstance = null;
    try {
      const { getDraftEvent } = await import('./utils/arena/draftApi');
      const ev = await getDraftEvent(eventId);
      if (ev?.mode === 'sealed') mode = 'sealed';
    } catch {}
    try {
      const { listInstances } = await import('./utils/arena/instancesApi');
      const list = await listInstances();
      linkedInstance = list.find((i) => i.draft_event_id === eventId) || null;
    } catch {}
    this.setState({
      draftEventId: eventId,
      draftEventMode: mode,
      draftPhase: 'tournament',
      arenaView: 'draft-tournament',
      draftQueueOpen: false,
      ...(linkedInstance ? {
        currentDraftInstanceId: linkedInstance.id,
        currentDraftInstanceVisibility: linkedInstance.visibility,
        currentDraftInstanceIsHost: (linkedInstance.participants || [])
          .some((p) => p.userId === this.state.arenaProfile?.id && p.role === 'creator'),
      } : {}),
    }, async () => {
      try { this._startDraftEventWatcher(eventId); } catch {}
      try {
        const { confirmDraftMatchReady } = await import('./utils/arena/draftApi');
        await confirmDraftMatchReady(eventId, matchId);
      } catch (err) {
        toast.error(err?.message || t('Could not join the match.'));
      }
    });
  };

  handleDraftComplete = (data) => {
    // Called from multiple paths: the rich WS subscription (full prizes
    // + standings + draftedCards), the draft-watch fire-complete path
    // (stripped to {eventId, mode}), and DraftLobby's status-poll
    // fallback (also stripped). They race in any order, and an
    // unconditional setState would let a stripped late-arriver wipe
    // the rich data with nulls/[]. The bug behind "draft-results
    // shows prizes as null even though the server granted everything".
    // Fall back to existing state so whoever delivers richest fields
    // wins regardless of order.
    this.setState((state) => ({
      arenaView: 'draft-results',
      draftPhase: 'results',
      draftFinalStandings: data?.standings || state.draftFinalStandings || [],
      draftPrizes: data?.prizes || state.draftPrizes || null,
      draftedCards: data?.draftedCards || state.draftedCards || [],
    }));
    // Refresh profile to pick up granted cards
    getStoredToken().then((token) => {
      if (token) loadArenaProfile(token).then((p) => {
        if (p) this.setState({ arenaProfile: this.profileFromServer(p, token) });
      }).catch(() => {});
    });
    // Final checkpoint: record standings + reward card, advance to
    // COMPLETED. This is the save-list terminal state. The row
    // now shows "Won" / "Lost" with standings readable via View
    // Results. Also finalize the instance server-side so
    // SessionBrowser's save list reflects completion immediately.
    this._persistDraftCheckpoint((state, { advancePhase, setStandings, setRewards, DRAFT_PHASES }) => {
      const standings = (data?.standings || []).map((row, i) => ({
        userId: row.playerId || row.userId,
        wins: row.wins || 0,
        losses: row.losses || 0,
        place: row.place || i + 1,
      }));
      const rewards = [];
      if (data?.prizeCard && standings[0]?.userId) {
        rewards.push({
          place: 1,
          userId: standings[0].userId,
          cardId: data.prizeCard.cardId,
          foiling: data.prizeCard.foiling || 'F',
        });
      }
      let next = setStandings(state, standings);
      next = setRewards(next, rewards);
      return advancePhase(next, DRAFT_PHASES.COMPLETED);
    }).then(async () => {
      // Re-read the draft-instance context from state here rather
      // than capturing it before the async checkpoint call.
      // otherwise a concurrent handleDraftBack (user navigating
      // away mid-completion) would leave captured values stale and
      // cause apiCompleteInstance to fire against a draft the user
      // has already abandoned, while the blob has been wiped.
      const { currentDraftInstanceId, currentDraftInstanceIsHost } = this.state;
      if (currentDraftInstanceId && currentDraftInstanceIsHost) {
        try {
          const myId = this.state.arenaProfile?.id;
          const winner = (data?.standings || []).find((s) => s.place === 1);
          await apiCompleteInstance(currentDraftInstanceId, {
            winner: winner?.playerId || winner?.userId || null,
            wonByMe: (winner?.playerId || winner?.userId) === myId,
            standings: data?.standings || [],
          });
        } catch (err) {
          console.warn('[draft] completeInstance failed:', err?.message || err);
        }
      }
      // Clear draft-instance context so future hook fires don't
      // touch the now-archived blob.
      this.setState({
        currentDraftInstanceId: null,
        currentDraftInstanceVisibility: null,
        currentDraftInstanceIsHost: false,
      });
    });
  };

  handleDraftCancelled = () => {
    toast(t('Draft event was cancelled. Entry refunded.'));
    // If this cancellation belonged to a unified-instance draft,
    // mark the instance abandoned server-side so the save row
    // leaves the user's list rather than hanging in 'gathering'.
    const instanceId = this.state.currentDraftInstanceId;
    const isHost = this.state.currentDraftInstanceIsHost;
    if (instanceId && isHost) {
      apiAbandonInstance(instanceId).catch(() => {});
    }
    this._stopDraftEventWatcher();
    this.setState({
      arenaView: 'hub',
      draftEventId: null,
      draftPhase: null,
      currentDraftInstanceId: null,
      currentDraftInstanceVisibility: null,
      currentDraftInstanceIsHost: false,
    });
  };

  handleDraftBack = async () => {
    // Deliberate "leave the tournament", not a silent bail to the menu. The
    // tournament continues without you (stay-in-bracket); you return anytime
    // from Play, Your Tournaments. Confirm so it can't happen by accident.
    const ok = await this._askConfirm({
      title: t('Leave the tournament?'),
      message: t("The tournament keeps going without you. Return anytime from Play, Your Tournaments to finish your deck and play your matches. If you don't come back, you may forfeit your matches."),
      confirmLabel: t('Leave'),
      cancelLabel: t('Stay'),
    });
    if (!ok) return;
    // Tell the other participants we left so they aren't silently building /
    // waiting alone. Best-effort, fired before we clear draftEventId below.
    const leavingEventId = this.state.draftEventId;
    if (leavingEventId) {
      import('./utils/arena/draftApi')
        .then(({ leaveDraftTournament }) => leaveDraftTournament(leavingEventId))
        .catch(() => {});
    }
    playUI(UI.CLOSE);
    this._stopDraftEventWatcher();
    this.setState({
      arenaView: 'hub',
      draftEventId: null,
      draftPhase: null,
      draftedCards: null,
      draftFinalStandings: null,
      draftPrizes: null,
      // Clear draft-instance context so subsequent checkpoint
      // hooks from lingering WS events (e.g. server tail) don't
      // reopen the blob for an instance the user has left.
      currentDraftInstanceId: null,
      currentDraftInstanceVisibility: null,
      currentDraftInstanceIsHost: false,
    });
  };

  handleOpenSettings = () => {
    this.setState({ settingsOpen: true, gameMenuOpen: false });
  };

  handleCloseSettings = () => {
    this.setState({ settingsOpen: false });
  };

  handleOpenSkillTree = () => {
    this.setState({ skillTreeOpen: true });
  };

  handleOpenHallOfFame = () => {
    this.setState({ hallOfFameOpen: true });
  };

  handleCloseHallOfFame = () => {
    this.setState({ hallOfFameOpen: false });
  };

  handleOpenSeasonRanking = () => {
    this.setState({ seasonRankingOpen: true });
  };

  handleCloseSeasonRanking = () => {
    this.setState({ seasonRankingOpen: false });
  };

  handleCloseSkillTree = () => {
    this.setState({ skillTreeOpen: false });
    // A Battle Mage spend (scholar_2/_4) bumps the displayed Arcane
    // Trials tier rewards via the server's GET /me/season payload,
    // and scholar_5 changes the quest slot count. Re-run initSeason
    // so the in-session UI reflects the new values without waiting
    // for the next login or match-end. Best-effort. Failure here
    // just means the player sees the old values until next refresh.
    this.initSeason().catch((err) => {
      console.error('[app] failed to refresh season after skill tree close:', err);
    });
  };

  // Why a notification deep link is off limits right now: a live match
  // (replays are fine) or a draft / sealed event past its lobby. Null
  // when the player is free to leave where they are.
  notificationLinkBlocker() {
    if (this.state.isGameBoardOpen && !this.state.replaySession) return t('Finish your match first.');
    const phase = this.state.draftEventId ? this.state.draftPhase : null;
    if (phase && phase !== 'lobby' && phase !== 'results') return t('Finish your draft event first.');
    return null;
  }

  // Where a clicked notification leads. One switch for every kind, so the
  // menu row and the toast's Open button land in the same place. Targets
  // outside the social drawer close it on the way out.
  openNotificationTarget = async (n) => {
    if (!n || !notificationHasTarget(n)) return;
    const blocker = this.notificationLinkBlocker();
    if (blocker) { toast.error(blocker); return; }
    const p = n.payload || {};
    const leaveDrawer = { mailboxOpen: false, mailboxChatFriend: null, focusGlobalMessageId: null };
    switch (n.kind) {
      case 'deck.comment':
      case 'deck.comment.like':
      case 'deck.like': {
        // Clear first so the same comment can be deep-linked twice in a row
        // (DeckEditor / DeckSidePanel key on the prop changing).
        this.setState({ ...leaveDrawer, deckHighlightCommentId: null });
        // The owner lands in their own editor; everyone else previews the
        // deck from the library.
        const opened = p.isOwner !== false && await this.handleOpenDeckInEditor(n.subjectId);
        if (!opened) {
          let deck = null;
          try { deck = await fetchLibraryDeck(n.subjectId); } catch {}
          if (!deck) { toast.error(t('That deck is no longer available.')); return; }
          this.setState({ galleryInitialTab: 'library' });
          await this.handleOpenLibraryDeckInEditor(deck);
        }
        if (p.commentId) this.setState({ deckHighlightCommentId: p.commentId });
        return;
      }
      case 'replay.comment':
      case 'replay.like': {
        const session = this.state.replaySession;
        const noteKey = p.rowKey || null;
        const commentId = p.commentId || null;
        if (session && session.roomCode === n.subjectId) {
          // Clear first so a second click on the same thread jumps again.
          this.setState({ ...leaveDrawer, replaySession: { ...session, noteKey: null, commentId: null } }, () => {
            this.setState((s) => (s.replaySession ? { replaySession: { ...s.replaySession, noteKey, commentId } } : null));
          });
          return;
        }
        // Another replay is open: swap to this one. The watch waits for the
        // leave to flush so its replaySession guard sees the cleared slot.
        if (session) this.handleLeaveReplay();
        this.setState(leaveDrawer, () => this.handleWatchReplay(n.subjectId, { link: p.link || null, noteKey, commentId }));
        return;
      }
      case 'chat.reply':
      case 'chat.mention':
      case 'chat.like':
        // Tavern tab in the social drawer works from every screen; the
        // view scrolls to the message and highlights it. Cleared first so
        // the same message can be opened twice.
        this.setState({ focusGlobalMessageId: null }, () => {
          this.setState({ mailboxOpen: true, mailboxChatFriend: null, focusGlobalMessageId: p.messageId || n.subjectId || null });
        });
        return;
      case 'chat.message':
        this.handleSendMailFromProfile(n.subjectId);
        return;
      case 'mail.received':
        this.handleOpenMailToLetter(n.subjectId);
        return;
      case 'auction.bid':
        this.setState({ ...leaveDrawer, arenaView: 'auction-house', auctionInitialTab: 'sell', auctionFocusListingId: n.subjectId });
        return;
      case 'friend.request':
        this.setState({ mailboxOpen: true, mailboxChatFriend: null, mailboxFocusNotifications: Date.now() });
        return;
      case 'friend.accepted':
        if (n.actorId) this.handleViewFriendProfile(n.actorId);
        return;
      default:
        return;
    }
  };

  // A Tavern deep link is spent once the drawer closes or leaves the tab;
  // left set, it would re-jump every time the Tavern remounts.
  handleTavernFocusDone = () => {
    if (this.state.focusGlobalMessageId) this.setState({ focusGlobalMessageId: null });
  };

  handleCloseMailbox = () => {
    this.setState({ mailboxOpen: false, mailboxSelectedMailId: null, mailboxView: null, mailboxComposeRecipientId: null, focusGlobalMessageId: null });
  };

  handleToggleMailbox = () => {
    // Toggle visibility only. Mailbox and ChatPanel stay mounted so
    // their internal state (selected tab, scroll, draft message,
    // chat history) survives close/reopen cycles. The remaining
    // intent-seed fields (mailboxSelectedMailId / mailboxView /
    // mailboxComposeRecipientId) already do nothing after Mailbox's
    // first mount, so they can stay as-is too.
    this.setState((s) => ({
      mailboxOpen: !s.mailboxOpen,
      friendsSidebarOpen: false, // close friends when opening mailbox
      focusGlobalMessageId: s.mailboxOpen ? null : s.focusGlobalMessageId,
    }));
  };

  // Every arena view mounts its own social-hub button + Mailbox, so
  // these three read the same way from a dozen call sites in render.
  // The badge on the button is the sum of the three tab badges: chat
  // unread, Tavern pings, and invites + activity + system mail.
  mailboxUnreadTabs() {
    const { friend, auction, news } = this.state.mailboxUnreadByTab;
    return {
      friend,
      global: this.state.globalChatMentions,
      notifications: this.state.notificationsCount + auction + news,
    };
  }

  mailboxBadgeCount() {
    const t = this.mailboxUnreadTabs();
    return t.friend + t.global + t.notifications;
  }

  canModerateChat() {
    return hasCap(this.state.arenaProfile, 'chat.moderate');
  }

  handleOpenMailToLetter = (mailId) => {
    this.setState({
      mailboxOpen: true,
      mailboxSelectedMailId: mailId,
      mailboxView: null,
    });
  };

  handleSendMailFromProfile = (friendId) => {
    // Find friend info from the friend list
    const friend = this.state.friendListData?.friends?.find((f) => f.id === friendId);
    this.setState({
      viewingFriendProfile: null,
      mailboxOpen: true,
      mailboxView: null,
      mailboxSelectedMailId: null,
      mailboxComposeRecipientId: null,
      mailboxChatFriend: friend ? {
        friendId: friend.id,
        friendName: friend.name,
        friendAvatar: friend.profileAvatar,
        friendDisplayedCrownAvatarId: friend.displayedCrownAvatarId,
        friendIsFounder: !!friend.isFounder,
        friendIsQaTester: !!friend.isQaTester,
        online: friend.online,
      } : { friendId, friendName: t('Player'), online: false },
    });
  };

  handleMailProfileUpdate = (updates) => {
    this.setState((s) => {
      const profile = { ...s.arenaProfile };
      if ('coins' in updates) profile.coins = updates.coins;
      if ('collection' in updates) profile.collection = updates.collection;
      return { arenaProfile: profile };
    });
  };

  // Lightweight profile re-fetch. Call after any user action that
  // mutates server-side profile state (chat card transfer, auction
  // claim, mail claim, achievement claim, purchase, etc.). Unlike
  // checkAuth, it does NOT run postLoginInit, so it won't restart hub
  // music, reconnect the presence WebSocket, re-trigger the
  // FirstRunDownload asset gate, or risk logging the user out on a
  // transient validateToken network blip.
  reloadProfile = async () => {
    const token = this.state.arenaProfile?.serverToken;
    if (!token) return;
    const fresh = await loadArenaProfile(token).catch(() => null);
    if (!fresh) return;
    this.setState({ arenaProfile: this.profileFromServer(fresh, token) });
  };

  // Server-pushed authoritative profile snapshot. Received via
  // presenceManager's `profile:update` subscription whenever the server
  // mutates our coins, shards, xp, rank, collection, pending packs,
  // achievements, or season progress. Regardless of which endpoint
  // triggered the change (admin grant, pack open, season claim, trade,
  // mail claim, etc). We reuse profileFromServer so the incoming
  // snapshot flows through the same normalization as login, preserving
  // the session token since the WS payload doesn't carry it.
  handleServerProfileUpdate = (fresh) => {
    if (!fresh || !fresh.id) return;
    this.setState((s) => {
      const token = s.arenaProfile?.serverToken;
      return { arenaProfile: this.profileFromServer(fresh, token) };
    });
  };

  // Opens the click-to-claim ceremony. The overlay mounts, plays the
  // reveal animation, calls the server claim endpoint via the
  // returned promise, then ticks the keystone counter on response.
  // No-op if the id isn't actually unclaimed (defensive. The panel
  // gates the affordance on the same condition).
  handleOpenClaimOverlay = (achievementId) => {
    const profile = this.state.arenaProfile;
    if (!profile || !Array.isArray(profile.unclaimedAchievements)) return;
    if (!profile.unclaimedAchievements.includes(achievementId)) return;
    this.setState({ claimingAchievementId: achievementId });
  };

  handleDismissClaimOverlay = () => {
    this.setState({ claimingAchievementId: null });
  };

  // Issued by AchievementClaimOverlay during its reveal phase. Hits
  // the server's atomic claim endpoint; on success the player's
  // keystone balance and unclaimed list are updated from the
  // response. Race-safe: the server's UPDATE only flips rows still
  // unclaimed, so duplicate clicks return claimed:false and the
  // local state stays consistent.
  handleClaimAchievement = async (achievementId) => {
    try {
      const result = await claimAchievement(achievementId);
      if (result?.claimed) {
        this.setState((state) => {
          const profile = state.arenaProfile;
          if (!profile) return null;
          return {
            arenaProfile: {
              ...profile,
              keystones: result.keystones ?? profile.keystones,
              unclaimedAchievements: Array.isArray(result.unclaimedAchievements)
                ? result.unclaimedAchievements
                : (profile.unclaimedAchievements || []).filter((id) => id !== achievementId),
            },
          };
        });
      } else if (result?.unclaimedAchievements) {
        // Server says already-claimed: reconcile local state so the
        // panel stops showing the affordance.
        this.setState((state) => {
          const profile = state.arenaProfile;
          if (!profile) return null;
          return {
            arenaProfile: {
              ...profile,
              keystones: result.keystones ?? profile.keystones,
              unclaimedAchievements: result.unclaimedAchievements,
            },
          };
        });
      }
      return result;
    } catch (err) {
      console.error('Failed to claim achievement:', err);
      throw err;
    }
  };

  // Count saved decks that aren't system-granted pre-cons. Pre-cons
  // saved via handleProgressionDeckChosen keep their canonical
  // PROGRESSION_DECKS name verbatim; anything else is
  // either a deck the player built from scratch in the editor or a
  // pre-con they personalised by renaming. Both count as "custom" for
  // the first_deck achievement.
  getCustomDeckCount = () => {
    return (this.state.savedDecks || []).filter((d) => !PROGRESSION_DECK_NAMES.has(d.name)).length;
  };

  // Highest like count across the player's shared decks. Feeds the
  // deck_likes_10 progress bar only. The achievement itself is awarded
  // server-side, since the likes that earn it are clicked by other people.
  getMaxDeckLikes = () => {
    return (this.state.savedDecks || []).reduce(
      (max, d) => (d.shared ? Math.max(max, d.likes || 0) : max),
      0,
    );
  };

  processAchievements = async (profile) => {
    const customDeckCount = this.getCustomDeckCount();
    const newlyUnlocked = checkAchievements(profile, this.state.sorceryCards, customDeckCount);
    if (newlyUnlocked.length === 0) return profile;

    // Detect-only: the server records the row with claimed_at NULL but
    // does NOT pay the keystone. The dopamine moment stays gated behind
    // a deliberate click. Either on the herald toast pushed below or
    // on the pulsing CLAIM affordance in the achievements panel (both
    // open AchievementClaimOverlay).
    const result = await detectAchievements(newlyUnlocked).catch((e) => {
      console.error('Failed to detect achievements:', e);
      return null;
    });

    if (!result || !Array.isArray(result.achievements)) return profile;
    for (const id of newlyUnlocked) pushAchievementToast(id);
    return {
      ...profile,
      achievements: result.achievements,
      unclaimedAchievements: result.unclaimedAchievements || [],
    };
  };

  handleMatchReward = async (reward) => {
    const { arenaProfile } = this.state;
    if (!arenaProfile) return;
    // Defence in depth: spectating must never write a draft result or
    // claim rewards. GameBoard already routes spectators through the
    // standalone Match Ended overlay (which never calls onMatchReward),
    // but a future refactor could re-introduce the path. This guard
    // ensures spectating can never inflate or deflate Swiss standings.
    if (this.state.isSpectating) return;

    // A no-contest settlement moves no counter and writes no history row:
    // the server recorded nothing, so neither does the local cache. Read
    // once here because the instance result, the draft report and the
    // stats cache all have to agree about it.
    const statsWrite = resolveStatsCacheWrite(reward, arenaProfile);

    // Complete the underlying instance (if any) so it leaves the
    // 'active' phase and the save list stops showing the match as
    // "In progress." Both players call this on their own reward
    // claim; server's last-write-wins on setResult is fine because
    // winner derivation is symmetric: winner = me if I won, else
    // the known opponent id. Also clears the host-local blob so
    // the completed row doesn't offer a resume action.
    const instId = this.state.currentInstanceId;
    if (instId) {
      const oppId = this.state.arenaMatchmakingOpponent?.id
        || this.state.arenaMatchmakingOpponent?.playerId
        || null;
      try {
        await apiCompleteInstance(
          instId,
          resolveCompletedInstanceResult(reward, arenaProfile.id, oppId),
        );
      } catch (err) {
        console.warn('[instance] completeInstance failed:', err?.message || err);
      }
      try {
        const { clearInstanceLocalBlob } = await import('./utils/game/instanceSaveAdapter');
        await clearInstanceLocalBlob(instId);
      } catch {}
      // Mark so the GameBoard onExit hook doesn't subsequently flip
      // the freshly completed instance back to paused when the user
      // dismisses the result overlay.
      this._completedInstanceId = instId;
    }

    // Report draft match result if this is a draft tournament game.
    // Identify the match by the `currentDraftMatchId` we captured in
    // `_handleDraftMatchStart` for this specific round. Never by
    // "find a pairing that contains me", which in 4+ player pods can
    // resolve to the wrong round (e.g. round 2's pairing freshly
    // server-generated) and immediately mark it complete with the
    // wrong winner before either player has even loaded in.
    //
    // Skip when arbitration returned a zero envelope (mutual_concede etc.).
    // `reward.won` is false on BOTH clients in that case, so each would POST
    // the opponent as winner and whichever request landed last would
    // (incorrectly) take the round and the prize card. The dispute-reprompt
    // loop handles the "both claimed win" case before we get here; this guards
    // any remaining no-rewards arbitration paths.
    //
    // ANY reason, not just the neutral-table ones the stats cache keys on: a
    // server reason nobody has added to that table yet must not silently start
    // handing out rounds again.
    const draftMatchId = this.state.currentDraftMatchId;
    if (this.state.draftEventId && this.state.draftPhase === 'tournament' && draftMatchId && !hasArbitrationReason(reward)) {
      try {
        const { reportDraftMatchResult, getDraftStandings } = await import('./utils/arena/draftApi');
        const data = await getDraftStandings(this.state.draftEventId);
        const myMatch = (data.currentPairings || []).find(
          (p) => p.matchId === draftMatchId || p.id === draftMatchId
        );
        if (myMatch && myMatch.status !== 'complete' && (myMatch.player1Id === arenaProfile.id || myMatch.player2Id === arenaProfile.id)) {
          const winnerId = reward.won ? arenaProfile.id : (myMatch.player1Id === arenaProfile.id ? myMatch.player2Id : myMatch.player1Id);
          await reportDraftMatchResult(this.state.draftEventId, myMatch.matchId || myMatch.id, winnerId);
        }
      } catch (err) {
        console.error('[handleMatchReward] draft match result report failed:', err);
      }
    }

    const opp = this.state.arenaMatchmakingOpponent;
    const opponentName = opp?.name || opp?.username || opp?.displayName || t('Opponent');

    // The server already applied coins/xp/shards/wins/losses and the
    // ranked ladder update (tier/division/LP/shields) atomically in
    // POST /profile/me/match/claim and wrote a match_history row as
    // part of the same transaction. Sync the client cache from the
    // returned totals and append a local history entry for immediate UI.
    const newCoins = reward.newTotals?.coins ?? (arenaProfile.coins + (reward.coins || 0));
    const newXp = reward.newTotals?.xp ?? (arenaProfile.xp + (reward.xp || 0));
    // Level-up celebration data. Prefer the server's authoritative
    // `levelsGained` (sent back with the claim response) and fall back
    // to a client-derived diff only if the server payload predates
    // that field. Keeps old clients/servers interoperable during
    // deploy windows.
    const oldLevel = levelFromXp(arenaProfile.xp || 0);
    const reachedLevel = levelFromXp(newXp);
    const levelsGained = typeof reward.levelsGained === 'number'
      ? reward.levelsGained
      : Math.max(0, reachedLevel - oldLevel);
    const newWins = statsWrite.wins;
    const newLosses = statsWrite.losses;
    const newShards = reward.newTotals?.arcanaShards ?? ((arenaProfile.arcanaShards || 0) + (reward.arcanaShards || 0));
    const newKeystones = reward.newTotals?.keystones ?? ((arenaProfile.keystones || 0) + (reward.questKeystones || 0));
    const newRank = reward.newTotals?.rank ?? arenaProfile.rank;
    const newShieldGames = reward.newTotals?.shieldGamesLeft ?? arenaProfile.shieldGamesLeft ?? 0;

    const updatedProfile = {
      ...arenaProfile,
      coins: newCoins,
      xp: newXp,
      arcanaShards: newShards,
      keystones: newKeystones,
      wins: newWins,
      losses: newLosses,
      rank: newRank,
      shieldGamesLeft: newShieldGames,
      matchHistory: statsWrite.appendHistory
        ? [
          {
            date: new Date().toISOString(),
            opponentName,
            won: reward.won,
            coinsEarned: reward.coins || 0,
            xpEarned: reward.xp || 0,
            durationMinutes: reward.durationMinutes || 0,
          },
          ...(arenaProfile.matchHistory || []),
        ]
        : (arenaProfile.matchHistory || []),
    };

    let withAchievements = await this.processAchievements(updatedProfile);

    // Quest progress is server-authoritative: the /me/match/claim
    // response carries the updated season_progress along with any
    // newly-completed quests. Mirror the progress into local state
    // and ALWAYS refetch the season so the Trials UI sees both the
    // refreshed `progress` counters AND any flipped `completed`
    // flags. Without this, progress increments (e.g. 3/5 → 4/5)
    // would never reach the active-quests grid until reload.
    let currentSeason = this.state.currentSeason;
    if (reward.seasonProgress) {
      withAchievements = { ...withAchievements, seasonProgress: reward.seasonProgress };
    }
    if (Array.isArray(reward.completedQuests) && reward.completedQuests.length > 0) {
      for (const q of reward.completedQuests) {
        const message = q.keystoneReward > 0
          ? (q.keystoneReward === 1
            ? t('Quest complete: {name} (+{xp} Season XP, +1 Keystone)', { name: q.name, xp: q.xpReward })
            : t('Quest complete: {name} (+{xp} Season XP, +{keystones} Keystones)', { name: q.name, xp: q.xpReward, keystones: q.keystoneReward }))
          : t('Quest complete: {name} (+{xp} Season XP)', { name: q.name, xp: q.xpReward });
        toast(message, { duration: 3500 });
      }
    }
    try {
      const result = await loadCurrentSeason();
      currentSeason = result.season;
    } catch (e) {
      console.error('Failed to refresh season after match:', e);
    }
    // Queue the level-up celebration if the player crossed a level
    // boundary during this match. The overlay renders at z-[300], well
    // below the ArenaMatchResult modal (z-[1200]), so it stays masked
    // until the player dismisses the rewards panel and returns to the
    // hub. Which is exactly the cinematic beat we want. Doesn't
    // overwrite a celebration that's already on screen (e.g. an
    // outstanding login replay), which would skip past it without the
    // player ever clicking Claim Deck.
    const nextState = { arenaProfile: withAchievements, currentSeason };
    // Level-up handoff: open the Progression screen so the player
    // walks the gauge and claims each cell explicitly. The screen
    // hydrates from server state on open, so multi-level matches
    // and "you missed a claim two matches ago" all surface through
    // the same single surface without any client-side queue.
    if (levelsGained > 0) {
      nextState.progressionScreenOpen = true;
    }
    this.setState(nextState);
  };

  // Called when the Progression screen credits a gold/skill reward.
  // pushes the new currency totals into the local profile so the top-
  // bar coin counter and skill-tree badge update without waiting for
  // the next profile:update WS push.
  handleProgressionRewardClaimed = ({ type, coins, grantedSkillPoints }) => {
    this.setState((s) => {
      const profile = s.arenaProfile;
      if (!profile) return null;
      const updates = {};
      if (type === 'gold' && typeof coins === 'number') updates.coins = coins;
      if (type === 'skill' && typeof grantedSkillPoints === 'number') {
        updates.grantedSkillPoints = grantedSkillPoints;
        updates.progression = {
          ...(profile.progression || {}),
          grantedSkillPoints,
        };
      }
      return { arenaProfile: { ...profile, ...updates } };
    });
  };

  // Called by the Progression screen when the player clicks a deck
  // cell. Opens the existing starter picker on top, which on confirm
  // routes through handleProgressionDeckChosen. The progression
  // screen stays mounted behind it so focus state survives the
  // picker round-trip.
  handleProgressionDeckCellClicked = () => {
    this.setState({ pickerOpen: true });
  };

  // Snapshot signature for the Progression auto-open guard. Any
  // server-driven change to claim state (level crossed, gold/SP/deck
  // claimed) flips one of these fields, so the signature is enough
  // to know "is this a new pending-claim situation we should re-show?"
  _progressionSignature = (prog) => {
    if (!prog) return '';
    return [
      prog.level ?? 0,
      prog.lastClaimedGoldLevel ?? 1,
      prog.grantedSkillPoints ?? 0,
      prog.claimedDeckCount ?? 0,
    ].join(':');
  };

  // Called on every hub entry. Opens the Progression screen iff the
  // server says the player has anything claimable AND we haven't
  // already shown it for this exact claim-state signature. The
  // signature changes naturally when the player claims something or
  // levels up, so the next hub return re-prompts; if they closed it
  // without claiming, the signature is unchanged and we respect that.
  _maybeAutoOpenProgression = () => {
    const p = this.state.arenaProfile;
    if (!p?.starterDeck) return;                            // L1 first-run owns its own picker path
    if (!p?.progression?.hasAnyPendingClaim) return;        // nothing to show
    if (this.state.progressionScreenOpen) return;           // already open
    if (this.state.pickerOpen) return;                      // picker on top of screen. Don't fight it
    const sig = this._progressionSignature(p.progression);
    if (sig === this._progressionAutoShownSig) return;      // dismissed for this exact state
    this._progressionAutoShownSig = sig;
    this.setState({ progressionScreenOpen: true });
  };

  // Applied by the Card Singles tab after a successful server-side purchase.
  // The server already deducted shards and added the card atomically; we
  // just sync the client cache. Shard purchases are always non-foil, so
  // the local collection delta uses foiling 'S'.
  handleShardPurchaseUpdate = ({ arcanaShards, collectionDelta }) => {
    this.setState((state) => {
      const profile = state.arenaProfile;
      if (!profile) return null;

      let collection = profile.collection || [];
      if (collectionDelta) {
        const { cardId, quantity } = collectionDelta;
        // Crafting always yields the card's base art in standard finish, so
        // the optimistic row has to name that identity. Matching on cardId
        // alone would credit the copy to an alt-art row if the player happens
        // to own one, and the next profile fetch would snap it back.
        const foiling = 'S';
        collection = [...collection];
        const existing = collection.find((c) =>
          c.cardId === cardId
          && (c.artGroup || ART_GROUP_BASE) === ART_GROUP_BASE
          && (c.foiling || 'S') === foiling
        );
        if (existing) {
          existing.quantity = (existing.quantity || 0) + quantity;
        } else {
          collection.push({ cardId, artGroup: ART_GROUP_BASE, foiling, quantity });
        }
      }

      const updated = {
        ...profile,
        arcanaShards: arcanaShards != null ? arcanaShards : profile.arcanaShards,
        collection,
      };
      return { arenaProfile: updated };
    });
  };

  openArenaStore = () => {
    playUI('snd-open-store.wav', { volume: 0.5 });
    playMusic('arena-store', { fadeInDuration: 3000 });
    this.setState({ arenaView: 'store' });
  };

  openAuctionHouse = () => {
    playUI('snd-open-auction.wav', { volume: 0.5 });
    this.setState({ arenaView: 'auction-house' });
  };

  static RECENT_PURCHASE_TTL_MS = 60_000;

  buyArenaPack = async (setKey, quantity = 1) => {
    playUI(UI.PURCHASE);

    // The server is authoritative for both the coin debit and the pack
    // roll. If the player can't afford it the server returns 402 and
    // nothing has changed locally yet. On success we update coins from
    // the server response and append the server-rolled packs (resolved
    // against the local card index) to the pending queue.
    let result;
    try {
      result = await purchasePacks(setKey, quantity);
    } catch (err) {
      console.error('Failed to purchase packs:', err);
      // Surface the server's message so a silent Purchase click doesn't
      // feel broken. Common case: 402 "Insufficient coins" when the UI
      // was showing an optimistic balance that drifted from the server.
      toast.error(err?.message || t('Purchase failed. Try again in a moment.'));
      // Re-sync the profile so the UI snaps to the authoritative balance.
      const token = await getStoredToken();
      if (token) {
        const fresh = await loadArenaProfile(token).catch(() => null);
        if (fresh) this.setState({ arenaProfile: this.profileFromServer(fresh, token) });
      }
      return;
    }

    // Alpha precons skip the pending_packs queue. The server has
    // already granted the cards directly. Drive a "deck collected"
    // modal instead of the pack-opening UI.
    if (result.precon) {
      await this.handlePreconPurchased(result);
      return;
    }

    const resolved = (result.packs || []).map((p) => resolvePack(p, this.state.sorceryCards));

    this.setState((state) => {
      // Dedupe by id. The server-driven _pendingPacksRaw hydration
      // can race with this append if some other path refetched the
      // profile mid-purchase. Without this, we'd end up with two
      // client entries pointing at the same server row.
      const existing = state.arenaPendingPacks || [];
      const existingIds = new Set(existing.map((p) => p.id));
      const toAppend = resolved.filter((p) => !existingIds.has(p.id));
      // Stamp every new id so the hydration in componentDidUpdate keeps
      // these entries even if a stale profile snapshot arrives before
      // the server-side commit propagates. Stamping all `resolved` ids
      // (not just the newly-appended ones) covers the case where the
      // hydration already grabbed them. They still count as in-flight
      // until a future snapshot echoes them back.
      const now = Date.now();
      for (const p of resolved) this._recentPurchaseIds.set(p.id, now);
      return {
        arenaProfile: { ...state.arenaProfile, coins: result.coins },
        arenaPendingPacks: [...existing, ...toAppend],
      };
    }, () => {
      playUI(UI.PURCHASE_PACK, { volume: 0.6 });
    });
  };

  // Handle the special "alpha precon" purchase response. Server has
  // already granted the cards; we just need to (a) reflect the new
  // collection + coins on the client, (b) auto-create one saved deck
  // per copy purchased, and (c) show the "deck collected" modal so the
  // player isn't routed into the booster-opening UI for a deterministic
  // sealed product.
  handlePreconPurchased = async (result) => {
    const { precon } = result;
    const def = ALPHA_PRECONS[precon.setKey];
    if (!def) return;

    const sorceryCards = this.state.sorceryCards || [];
    const cardById = new Map(sorceryCards.map((c) => [c.unique_id, c]));

    // Build the saved-deck cards array from the precon definition,
    // routing each entry to the right zone based on card type. Mirrors
    // the editor's save shape so the deck loads with the correct
    // spellbook/atlas/avatar split.
    const deckCards = [];
    for (const entry of def.contents) {
      const card = cardById.get(entry.cardId);
      if (!card) continue;
      const type = card.types?.[0] || '';
      const zone = type === 'Avatar' ? 'avatar' : type === 'Site' ? 'atlas' : 'spellbook';
      for (let i = 0; i < entry.qty; i++) {
        deckCards.push({
          cardId: entry.cardId,
          cardName: card.name,
          printingId: entry.printingId,
          zone,
          isSideboard: false,
        });
      }
    }

    // Featured-cards preview (avatar + a couple of headliners) drives
    // the DeckTile art. Pick the avatar first, then up to two
    // non-avatar cards.
    const previewCards = [];
    const seen = new Set();
    for (const entry of def.contents) {
      const card = cardById.get(entry.cardId);
      if (!card || seen.has(entry.cardId)) continue;
      const printing = card.printings?.find((p) => p.unique_id === entry.printingId)
        || card.printings?.[0];
      if (!printing?.image_url) continue;
      seen.add(entry.cardId);
      const isAvatar = card.types?.[0] === 'Avatar';
      // Avatar first
      if (isAvatar) {
        previewCards.unshift({ name: card.name, imageUrl: printing.image_url });
      } else if (previewCards.length < 10) {
        previewCards.push({ name: card.name, imageUrl: printing.image_url });
      }
    }

    // One saved-deck row per copy purchased. Auto-numbered names keep
    // them distinguishable in the collection. The base name has no
    // numeric suffix so the first copy reads cleanly.
    let savedSummary = null;
    for (let i = 0; i < precon.quantity; i++) {
      const suffix = i === 0 ? '' : ` (${i + 1})`;
      const payload = {
        id: `deck-${Date.now()}-${i}`,
        name: `${def.label}${suffix}`,
        format: 'constructed',
        gameMode: 'constructed',
        cards: deckCards,
        previewCards,
      };
      try {
        const result = await saveSavedDeck(payload, 'sorcery');
        if (i === 0) savedSummary = { ...payload, id: result?.id || payload.id };
      } catch (err) {
        console.error('[precon] failed to save deck:', err);
      }
    }

    // Refresh both savedDecks and the full profile so the new deck
    // and the granted cards both appear without a manual reload.
    await this.refreshSavedDecks();
    const token = this.state.arenaProfile?.serverToken;
    if (token) {
      const fresh = await loadArenaProfile(token).catch(() => null);
      if (fresh) {
        this.setState({ arenaProfile: this.profileFromServer(fresh, token) });
      }
    }

    playUI(UI.PURCHASE_PACK, { volume: 0.6 });

    this.setState({
      arenaProfile: { ...this.state.arenaProfile, coins: result.coins },
      preconCollected: {
        deck: savedSummary || {
          id: 'precon-preview',
          name: def.label,
          cards: deckCards,
          previewCards,
        },
        quantity: precon.quantity,
      },
    });
  };

  openNextPack = () => {
    playUI(UI.CHEST_OPEN);
    const { arenaPendingPacks } = this.state;
    if (!arenaPendingPacks || arenaPendingPacks.length === 0) return;

    const nextPack = arenaPendingPacks[0];
    const remaining = arenaPendingPacks.slice(1);

    // arenaAutoOpenPack must be reset here. A stale `true` from a
    // prior openFromSet would cause ArenaPackOpening to skip the
    // picker and auto-open the next pack, defeating the "let the
    // player choose which booster to open" affordance.
    // _currentPackOpened tracks whether this designated pack has been
    // opened yet; reset it so a back-out from the picker restores the
    // pack instead of treating it as spent.
    this._currentPackOpened = false;
    this.setState({
      arenaView: 'pack-opening',
      arenaOpenedPack: nextPack,
      arenaPendingPacks: remaining,
      arenaAutoOpenPack: false,
    });
  };

  pickPackFromSet = (setKey) => {
    const { arenaPendingPacks, arenaOpenedPack } = this.state;
    const allPacks = [arenaOpenedPack, ...(arenaPendingPacks || [])];
    const targetIdx = allPacks.findIndex((p) => p.setKey === setKey);
    if (targetIdx < 0) return;

    const targetPack = allPacks[targetIdx];
    const remaining = allPacks.filter((_, i) => i !== targetIdx);

    this.setState({
      arenaOpenedPack: targetPack,
      arenaPendingPacks: remaining,
    });
  };

  openFromSet = (setKey) => {
    // Visually swap in the chosen pack so the animation starts immediately.
    // The actual server open call happens in handlePackOpened once the
    // animation finishes, so the grant stays atomic with the reveal.
    const { arenaPendingPacks, arenaOpenedPack } = this.state;
    const allPacks = [arenaOpenedPack, ...(arenaPendingPacks || [])].filter(Boolean);
    const targetIdx = allPacks.findIndex((p) => p.setKey === setKey);
    if (targetIdx < 0) return;

    const targetPack = allPacks[targetIdx];
    const remaining = allPacks.filter((_, i) => i !== targetIdx);

    // autoOpen fires handlePackOpened, which flips _currentPackOpened
    // to true on success. Start it false so a failed open still treats
    // the pack as un-opened (and thus restorable).
    this._currentPackOpened = false;
    this.setState({
      arenaOpenedPack: targetPack,
      arenaPendingPacks: remaining,
      arenaAutoOpenPack: true,
    });
  };

  handlePackOpened = async () => {
    const { arenaOpenedPack, arenaProfile } = this.state;
    if (!arenaOpenedPack) return;

    // The server holds the pre-rolled pack contents and grants them
    // atomically when we open it by id. The response includes the new
    // collection so we can sync without a separate fetch.
    let serverCollection = arenaProfile.collection;
    let openFailed = false;
    let packCompletedQuests = [];
    if (arenaOpenedPack.id) {
      try {
        const result = await openPendingPack(arenaOpenedPack.id);
        serverCollection = result.collection;
        if (Array.isArray(result.completedQuests)) {
          packCompletedQuests = result.completedQuests;
        }
      } catch (e) {
        console.error('Failed to open pack on server:', e);
        openFailed = true;
        // A 404 here means the row was already deleted (a second
        // device finalized it, or stale state from an earlier bug).
        // openPack is idempotent on opened-but-not-finalized rows so
        // this only fires when the row is gone entirely. Surface it
        // so the user understands the reveal animation they're
        // watching reflects no actual grant.
        toast.error(t('This pack was already opened. No cards were granted.'));
      }
    }
    // Pack opens advance the `packs_opened` quest server-side. Toast
    // each completion and refresh the season so the Trials grid
    // mirrors the new progress + completed flags right away.
    for (const q of packCompletedQuests) {
      const message = q.keystoneReward > 0
        ? (q.keystoneReward === 1
          ? t('Quest complete: {name} (+{xp} Season XP, +1 Keystone)', { name: q.name, xp: q.xpReward })
          : t('Quest complete: {name} (+{xp} Season XP, +{keystones} Keystones)', { name: q.name, xp: q.xpReward, keystones: q.keystoneReward }))
        : t('Quest complete: {name} (+{xp} Season XP)', { name: q.name, xp: q.xpReward });
      toast(message, { duration: 3500 });
    }
    if (!openFailed) {
      try {
        const seasonResult = await loadCurrentSeason();
        if (seasonResult?.season) {
          this.setState({ currentSeason: seasonResult.season });
        }
      } catch (e) {
        console.error('Failed to refresh season after pack open:', e);
      }
    }

    // Pack-opens counters are server-authoritative. OpenPack bumps
    // players.packs_opened + packs_opened_by_set in the same transaction
    // as the card grant. Refetch first so processAchievements evaluates
    // Pack Rat / Hoarder against the post-bump value rather than a
    // stale local snapshot.
    const updatedProfile = { ...arenaProfile, collection: serverCollection };
    const token = await getStoredToken();
    const fresh = token ? await loadArenaProfile(token).catch(() => null) : null;
    const refreshedProfile = fresh
      ? this.profileFromServer(fresh, token)
      : updatedProfile;

    const withAchievements = openFailed
      ? refreshedProfile
      : await this.processAchievements(refreshedProfile);

    // Mark the designated pack as opened so a later back-out finalizes
    // it (cards were granted) instead of restoring it to the queue. On
    // failure leave it un-opened so the pack survives and stays openable.
    if (!openFailed) this._currentPackOpened = true;

    this.setState({ arenaProfile: withAchievements });
  };

  handlePackDone = () => {
    // Entering the opening screen designates a pack as arenaOpenedPack
    // and pulls it out of the pending queue, but doesn't open it. The
    // picker is shown first. If the player backs out without opening
    // (_currentPackOpened never flipped), put the pack back in the
    // queue so it doesn't vanish from the store. The server keeps the
    // row alive (finalize only deletes opened packs), so this is purely
    // restoring the local view to match the authoritative state.
    this.setState((s) => {
      const pack = s.arenaOpenedPack;
      const restore = Boolean(pack?.id) && !this._currentPackOpened;
      if (!restore) {
        return { arenaView: 'store', arenaOpenedPack: null };
      }
      const existing = s.arenaPendingPacks || [];
      const already = existing.some((p) => p.id === pack.id);
      return {
        arenaView: 'store',
        arenaOpenedPack: null,
        arenaPendingPacks: already ? existing : [pack, ...existing],
      };
    });
  };

  // ArenaPackOpening calls this after a successful reroll/transmute
  // on an already-opened pack, where the server has swapped a card in
  // the player's collection. We merge the authoritative collection
  // into arenaProfile so the ownedMap in the opening UI (and the
  // collection screen, deck builder, etc.) all stay in sync without
  // a separate profile refetch.
  handlePackCollectionUpdate = (collection) => {
    if (!Array.isArray(collection)) return;
    this.setState((s) => s.arenaProfile
      ? { arenaProfile: { ...s.arenaProfile, collection } }
      : null);
  };

  handleOpenAnother = () => {
    const { arenaPendingPacks } = this.state;
    if (!arenaPendingPacks || arenaPendingPacks.length === 0) {
      // Nothing left to open. Fall back to the store.
      this.handlePackDone();
      return;
    }
    // Stay in the pack-opening view but remount ArenaPackOpening into
    // its phase='sealed' picker so the player explicitly chooses the
    // next pack. The key in render() folds in arenaAutoOpenPack, so
    // flipping it to false guarantees a fresh mount in picker mode.
    const nextPack = arenaPendingPacks[0];
    const remaining = arenaPendingPacks.slice(1);
    // Fresh picker for the next pack. Reset so a back-out restores it.
    this._currentPackOpened = false;
    this.setState({
      arenaOpenedPack: nextPack,
      arenaPendingPacks: remaining,
      arenaAutoOpenPack: false,
    });
  };

  openArenaDeckBuilder = () => {
    playUI('snd-open-deckbuilder.wav', { volume: 0.5 });
    playMusic('arena-deckbuilder', { fadeInDuration: 3000 });
    this.refreshSavedDecks();
    this.setState({
      arenaView: 'deck-gallery',
      editingDeckData: null,
    });
  };

  // Resolves to whether the deck opened, so a notification deep link can
  // fall back to the library copy when the player no longer owns it.
  handleOpenDeckInEditor = async (deckId) => {
    playUI(UI.OPEN);
    if (!deckId || !Array.isArray(this.state.sorceryCards)) return false;
    try {
      const savedDeck = await loadSavedDeckById(deckId, 'sorcery');
      if (!savedDeck) return false;

      const activeCards = this.getActiveCards();
      const restoredCards = await restoreSavedDeckCards({
        savedEntries: savedDeck.cards || [],
        cardIndex: this.getSavedDeckCardIndex(activeCards),
        resolvePreferredPrinting: this.resolvePreferredPrinting,
      });
      // Auto-migrate zones against the deck's avatar. Decks saved
      // before an avatar rule existed (e.g. Magician's
      // spellbookAllowsSites) get their zones corrected transparently
      // on open. User sees a valid deck, not an inconsistent one.
      const migratedCards = applyAvatarZoneRouting(restoredCards);
      this.setState({
        arenaView: 'deck-editor',
        editingDeckData: {
          id: savedDeck.id,
          name: savedDeck.name,
          cards: migratedCards,
          shared: savedDeck.shared || false,
          gameMode: savedDeck.gameMode || 'constructed',
          tags: Array.isArray(savedDeck.tags) ? savedDeck.tags : [],
          strategyNotes: savedDeck.strategyNotes || '',
          libraryView: false,
          updatedAt: savedDeck.updatedAt,
        },
      });
      return true;
    } catch (error) {
      console.error('Failed to open deck:', error);
      return false;
    }
  };

  // Config comes from CreateDeckModal: name / tags / format /
  // visibility and an optional pre-picked avatar.
  handleCreateNewDeck = (config = {}) => {
    playUI(UI.CONFIRM);
    const cards = [];
    if (config.avatar?.cardId) {
      const avatarCard = (this.state.sorceryCards || []).find((c) => c.unique_id === config.avatar.cardId);
      // The modal picks a concrete variant (art group + foil); the
      // printing is the only channel that identity travels through.
      const printing = avatarCard?.printings?.find((p) => p.unique_id === config.avatar.printingId)
        || avatarCard?.printings?.[0];
      if (printing) cards.push({ card: avatarCard, printing, zone: 'avatar' });
    }
    this.setState({
      arenaView: 'deck-editor',
      editingDeckData: {
        id: '',
        name: config.name || '',
        cards: applyAvatarZoneRouting(cards),
        gameMode: config.gameMode || 'constructed',
        tags: config.tags || [],
        // A "Public" deck is published on its first save; the flag is
        // consumed (and cleared) in handleSaveDeckFromEditor.
        pendingShare: config.visibility === 'public',
      },
    });
  };

  handleOpenLibraryDeckInEditor = async (libraryDeck) => {
    playUI(UI.OPEN);
    if (!libraryDeck || !Array.isArray(this.state.sorceryCards)) return;

    const activeCards = this.getActiveCards();
    const restoredCards = await restoreSavedDeckCards({
      savedEntries: libraryDeck.cards || [],
      cardIndex: this.getSavedDeckCardIndex(activeCards),
      resolvePreferredPrinting: this.resolvePreferredPrinting,
    });
    const migratedCards = applyAvatarZoneRouting(restoredCards);

    // The library tab is always a preview: cards are sandbox-editable,
    // strategy notes are read-only, and Save creates a fresh owned
    // copy. This holds even if the deck happens to belong to the
    // current player. Notes are only editable through the My Decks
    // entry point, never from the library view.
    this.setState({
      arenaView: 'deck-editor',
      editingDeckData: {
        id: '',
        name: libraryDeck.name,
        cards: migratedCards,
        shared: false,
        gameMode: 'sandbox',
        strategyNotes: libraryDeck.strategyNotes || '',
        libraryView: true,
        sourceLibraryId: libraryDeck.id,
        author: {
          playerId: libraryDeck.playerId,
          playerName: libraryDeck.playerName,
          playerAvatar: libraryDeck.playerAvatar || null,
          playerTier: libraryDeck.playerTier || null,
        },
        likes: libraryDeck.likes || 0,
        liked: !!libraryDeck.liked,
        views: libraryDeck.views || 0,
        updatedAt: libraryDeck.updatedAt,
      },
    });
  };

  // Resolve saved-deck-style entries ({ cardId, cardName, printingId,
  // zone }) into fully hydrated { card, printing, zone } editor entries
  // against the sorceryCards catalog. Shared by the import flow and the
  // create-deck modal's bulk add.
  resolveImportEntries = (entries) => {
    const sorceryCards = this.state.sorceryCards || [];
    const cardIndex = new Map();
    const cardByNormalizedName = new Map();
    for (const c of sorceryCards) {
      cardIndex.set(c.unique_id, c);
      const normalizedName = normalizeSavedDeckText(c.name);
      if (normalizedName && !cardByNormalizedName.has(normalizedName)) {
        cardByNormalizedName.set(normalizedName, c);
      }
    }

    const cards = [];
    for (const entry of entries || []) {
      // Fall back to the entry's name when the cardId slug misses. An
      // imported "King's Council" can carry a punctuation-variant slug
      // that won't match the catalog's unique_id. Same fix as spawnDeck.
      const card =
        cardIndex.get(entry.cardId) ||
        cardByNormalizedName.get(normalizeSavedDeckText(entry.cardName));
      if (!card) continue;
      const printing = card.printings?.find((p) => p.unique_id === entry.printingId) || card.printings?.[0];
      if (!printing) continue;
      cards.push({ card, printing, zone: entry.zone || 'spellbook' });
    }
    return cards;
  };

  handleImportDeck = (data) => {
    playUI(UI.CONFIRM);
    const cards = this.resolveImportEntries(data.cards);

    // Apply avatar-aware zone routing: the import source may use
    // generic zone labels that don't account for the chosen avatar
    // (e.g. a Magician import lists its sites as `zone: 'atlas'`).
    // Same helper used at load time. One source of truth.
    const migrated = applyAvatarZoneRouting(cards);

    if (data.warnings?.length > 0) {
      toast.info(t('Imported with {n} warning(s): {warnings}', { n: data.warnings.length, warnings: data.warnings.slice(0, 3).join(', ') }));
    }

    this.setState({
      arenaView: 'deck-editor',
      editingDeckData: { id: '', name: data.name || t('Imported Deck'), cards: migrated },
    });
  };

  handleSaveDeckFromEditor = async (payload) => {
    playUI(UI.CONFIRM);
    // Consume the create-modal's "Public" choice on the first save.
    // The deck has to exist server-side before it can be shared. On a
    // share failure the flag survives so the next save retries.
    const pendingShare = !!this.state.editingDeckData?.pendingShare;
    const savedSummary = await saveSavedDeck(payload, 'sorcery');
    let sharedNow = false;
    if (pendingShare && savedSummary?.id) {
      try {
        await shareDeck(savedSummary.id);
        sharedNow = true;
        toast.success(t('Deck shared to library'));
      } catch (error) {
        console.error('Failed to share deck after save:', error);
        toast.error(t('Failed to share deck'));
      }
    }

    // After saving, the deck is owned by the current player. So a deck
    // that was opened in library view becomes a regular owned deck.
    // Clear library-only context (libraryView, author, source id) so
    // subsequent edits behave like any other owned deck.
    this.setState({
      editingDeckData: {
        ...this.state.editingDeckData,
        id: savedSummary.id,
        name: savedSummary.name,
        strategyNotes: savedSummary.strategyNotes ?? this.state.editingDeckData?.strategyNotes ?? '',
        updatedAt: savedSummary.updatedAt ?? this.state.editingDeckData?.updatedAt,
        shared: sharedNow || this.state.editingDeckData?.shared || false,
        pendingShare: pendingShare && !sharedNow,
        libraryView: false,
        sourceLibraryId: null,
        author: null,
      },
    });

    await this.refreshSavedDecks();

    // Building a deck unlocks `first_deck`. The only place this rule
    // can trigger, since processAchievements isn't on the match/pack
    // path for deck-only changes. Runs after refreshSavedDecks so the
    // count reflects the just-saved row.
    const { arenaProfile } = this.state;
    if (arenaProfile) {
      const withAchievements = await this.processAchievements(arenaProfile);
      if (withAchievements !== arenaProfile) {
        this.setState({ arenaProfile: withAchievements });
      }
    }

    return savedSummary;
  };


  handleBackToGallery = () => {
    this.refreshSavedDecks();
    // A notification deep link seeds this for one open; DeckEditor reads it
    // on mount, so it must not survive into the next visit.
    if (this.state.deckHighlightCommentId) this.setState({ deckHighlightCommentId: null });
    // When the editor was opened from another player's profile (the
    // "open published deck" flow on the profile page), Back should
    // return to that profile, not dump the user into their own deck
    // gallery. `deckEditorReturnToProfile` is set at open time and
    // cleared here so subsequent edits from My Decks behave normally.
    const returnProfileId = this.state.deckEditorReturnToProfile;
    if (returnProfileId) {
      this.setState({
        arenaView: 'hub',
        editingDeckData: null,
        deckEditorReturnToProfile: null,
        viewingFriendProfile: returnProfileId,
      });
      return;
    }
    this.setState({ arenaView: 'deck-gallery', editingDeckData: null });
  };

  handleBackToHubFromGallery = () => {
    playMusic('arena-hub', { fadeInDuration: 3000 });
    this.setState({ galleryInitialTab: null,  arenaView: 'hub' });
  };

  handleDeleteDeckFromGallery = async (deckId) => {
    playUI(UI.DELETE);
    if (!deckId) return;
    try {
      await deleteSavedDeckById(deckId, 'sorcery');
      await this.refreshSavedDecks();
    } catch (error) {
      console.error('Failed to delete deck:', error);
    }
  };

  handleSaveDeckShowcase = async (deckId, showcase) => {
    if (!deckId) return;
    try {
      await saveDeckShowcase(deckId, showcase);
      await this.refreshSavedDecks();
    } catch (error) {
      console.error('Failed to save deck showcase:', error);
      toast.error(t('Failed to save deck artwork.'));
    }
  };

  handleRenameDeckFromGallery = async (deckId, name) => {
    const trimmed = (name || '').trim();
    if (!deckId || !trimmed) return;
    try {
      await renameSavedDeck(deckId, trimmed);
      await this.refreshSavedDecks();
    } catch (error) {
      console.error('Failed to rename deck:', error);
      toast.error(t('Failed to rename deck.'));
    }
  };

  handleToggleShareFromGallery = async (deck) => {
    if (!deck?.id) return;
    try {
      if (deck.shared) {
        await unshareDeck(deck.id);
        toast.success(t('Deck removed from library'));
      } else {
        await shareDeck(deck.id);
        toast.success(t('Deck shared to library'));
      }
      await this.refreshSavedDecks();
    } catch (error) {
      console.error('Failed to update deck sharing:', error);
      toast.error(t('Failed to update sharing'));
    }
  };

  handleSetDeckTagsFromGallery = async (deckId, tags) => {
    if (!deckId) return;
    try {
      await setDeckTags(deckId, tags);
      await this.refreshSavedDecks();
    } catch (error) {
      console.error('Failed to update deck tags:', error);
      toast.error(t('Failed to update tags'));
    }
  };

  handleDeleteTagFromGallery = async (tag) => {
    if (!tag) return;
    try {
      await deleteDeckTag(tag);
      await this.refreshSavedDecks();
      toast.success(t('Deleted tag "{tag}"', { tag }));
    } catch (error) {
      console.error('Failed to delete tag:', error);
      toast.error(t('Failed to delete tag'));
    }
  };

  handleToggleFavoriteDeckFromGallery = async (deck) => {
    if (!deck?.id) return;
    try {
      await setDeckFavorite(deck.id, !deck.favorite);
      await this.refreshSavedDecks();
    } catch (error) {
      console.error('Failed to update deck favorite:', error);
      toast.error(t('Failed to update favorite'));
    }
  };

  handleDuplicateDeckFromGallery = async (deckId) => {
    if (!deckId) return;
    playUI(UI.CONFIRM);
    try {
      const original = await loadSavedDeckById(deckId, 'sorcery');
      if (!original) {
        toast.error(t('Could not load deck to duplicate.'));
        return;
      }
      const payload = {
        id: `deck-${Date.now()}`,
        name: t('{name} (Copy)', { name: original.name || t('Untitled Deck') }),
        format: original.format || 'constructed',
        gameMode: original.gameMode || 'constructed',
        tags: original.tags || [],
        cards: original.cards || [],
        previewCards: original.previewCards || [],
      };
      await saveSavedDeck(payload, 'sorcery');
      // Deck saves don't carry the showcase, so copy the tile artwork
      // over separately. A duplicate should look like its original.
      if (original.showcase) {
        await saveDeckShowcase(payload.id, original.showcase).catch((e) => {
          console.warn('Failed to copy deck showcase to duplicate:', e);
        });
      }
      await this.refreshSavedDecks();
      toast.success(t('Duplicated "{name}"', { name: original.name || t('deck') }));
    } catch (error) {
      console.error('Failed to duplicate deck:', error);
      toast.error(t('Failed to duplicate deck.'));
    }
  };

  getActiveCards = () => {
    return this.state.sorceryCards;
  };


  resolvePreferredPrinting = async (card) => {
    if (!card) {
      return null;
    }

    return resolvePreferredPrintingStatic(card);
  };

  getSavedDeckCardIndex = (cards = this.getActiveCards()) => {
    if (!Array.isArray(cards)) {
      return null;
    }

    if (this.savedDeckCardIndexSource !== cards) {
      this.savedDeckCardIndex = createSavedDeckCardIndex(cards);
      this.savedDeckCardIndexSource = cards;
    }

    return this.savedDeckCardIndex;
  };

  handleDocumentKeyUp = (event) => {
    if (!this.state.isGameBoardOpen) return;
    // Map keyup events back to a canonical pan direction via the
    // user's current keybindings so remapped pan keys release cleanly.
    const panBindings = {
      'camera.panUp': 'w',
      'camera.panDown': 's',
      'camera.panLeft': 'a',
      'camera.panRight': 'd',
    };
    for (const actionId in panBindings) {
      const binding = gameKeybindingGetBinding(actionId);
      if (!binding) continue;
      const codeMatch = binding.code && binding.code === event.code;
      const keyMatch = binding.key && event.key && binding.key.toLowerCase() === event.key.toLowerCase();
      const modMatch = Boolean(binding.shift) === event.shiftKey
        && Boolean(binding.ctrl) === event.ctrlKey
        && Boolean(binding.alt) === event.altKey
        && Boolean(binding.meta) === event.metaKey;
      if ((codeMatch || keyMatch) && modMatch) {
        this.gameBoardRef?.scene?.setKeyHeld(panBindings[actionId], false);
        return;
      }
    }
  };

  handleToggleFriendsSidebar = () => {
    this.setState((s) => {
      const next = !s.friendsSidebarOpen;
      // Snap to fresh data the moment the user looks at the sidebar. Covers
      // the gap between background poll cycles so opening it never shows a
      // stale avatar from a friend who just changed it.
      if (next) refreshFriendList();
      return { friendsSidebarOpen: next, mailboxOpen: false }; // close mailbox when opening friends
    });
  };

  handleGameMenuResume = () => this.setState({ gameMenuOpen: false });

  handleGameMenuQuit = () => {
    // In a browser there is no process to quit; without this guard the
    // POST 404s and the user is stuck on the closing overlay forever.
    if (isWeb()) return;
    // Electrobun's quit waits up to 5s for CEF to shut down cleanly, plus
    // the OS tears down CEF helpers via the launcher's job object. Total
    // 5-10s from click to process exit. Show a closing overlay immediately
    // so the user has feedback instead of assuming the button is broken.
    this.setState({ isQuitting: true, gameMenuOpen: false });
    fetch('/api/app/quit', { method: 'POST' }).catch(() => {});
  };

  handleGameMenuMainMenu = async () => {
    // If in an active draft, warn the player before leaving
    if (this.state.draftEventId && this.state.draftPhase && this.state.draftPhase !== 'lobby') {
      const ok = await this._askConfirm({
        title: t('Leave active draft?'),
        message: t('Leaving will disconnect you. If you don’t reconnect within 2 minutes, the draft will be cancelled and your entry refunded.'),
        confirmLabel: t('Leave'),
        danger: true,
      });
      if (!ok) {
        this.setState({ gameMenuOpen: false });
        return;
      }
      // Clear draft state on client. Server will handle the disconnect grace period
      this.setState({ draftEventId: null, draftPhase: null });
    }

    // If the player is matchmaking, leave the queue first (fire-and-forget.
    // we don't want network errors to block returning to the menu).
    if (this.state.arenaMatchmaking) {
      try { leaveQueue(); } catch {}
      clearQueueState();
    }

    // If the game board is mounted, dropping `isGameBoardOpen` will unmount
    // GameBoard, which triggers its componentWillUnmount → autoSave →
    // disconnectSocket → scene.dispose. That's the same graceful shutdown
    // path used by the in-game "Leave game" button.
    if (this.state.isGameBoardOpen) {
      this.gameBoardRef = null;
      stopMusic(2000);
    }

    this._launchingInstanceId = null;
    this.setState({
      gameMenuOpen: false,
      settingsOpen: false,
      mailboxOpen: false,
      isGameBoardOpen: false,
      sessionMode: null,
      sessionId: null,
      roomCode: null,
      isArenaMatch: false,
      isRankedMatch: false,
      isCasualSandbox: false,
      arenaMatchmaking: false,
      arenaMatchmakingOpponent: null,
      arenaMatchmakingMode: null,
      arenaMatchId: null,
      arenaOpenedPack: null,
      editingDeckData: null,
      arenaView: 'hub',
    });
  };

  handleGlobalContextMenu = (event) => {
    // Allow context menu on input fields for cut/copy/paste
    if (isEditableTarget(event.target)) return;
    event.preventDefault();
  };

  // Escape priority chain. The app-level "escape menu" (gameMenuOpen)
  // should ONLY open when we're on the game board and nothing else is
  // overlaid on top. Anywhere else, Escape backs out of whatever is open.
  // Order is: in-flight handlers → self-managed dialogs → DOM popovers →
  // app overlays in z-order → game-board overlays → off-board bail → toggle.
  handleAppEscape = (event) => {
    // NB: do not gate on event.defaultPrevented. Main.jsx preventDefaults
    // every Escape in the capture phase (to block the browser's fullscreen
    // exit), so defaultPrevented is always true by the time this bubble-phase
    // handler runs. Coordination with GameBoard's window-level handler is via
    // stopPropagation instead: document bubbles before window, so when this
    // handler claims the press it calls stopPropagation and GameBoard's
    // handler never fires; when it defers, it lets the event reach window.
    const s = this.state;

    // The pause menu sits above everything. Escape closes it.
    if (s.gameMenuOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.setState({ gameMenuOpen: false });
      return;
    }

    // A ready check is a must-answer prompt. Escape neither dismisses it nor
    // opens the pause menu behind it. Defer (no stopPropagation) so nothing
    // downstream mistakes this for an unhandled press.
    if (s.activeReadyCheck) return;

    // Topmost-first: any dismissable surface that registered with the Esc
    // stack (card inspectors, popovers, HUD sub-panels, themed modals) owns
    // this press. dismissTop closes exactly the one on top, so nested layers
    // peel one Escape at a time and the pause menu never opens over them.
    if (dismissTop()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Fallback for surfaces that own Escape elsewhere rather than via the
    // stack: shadcn dropdown/select/multi-select/context-menu self-close
    // (role="menu"/"listbox"), and GameBoard's own window handler cancels its
    // targeting/effect dialogs (role="dialog", e.g. AttackTargetModal) which
    // aren't on the stack. When any is present, defer (no stopPropagation) so
    // its owner handles the press and the pause menu stays shut.
    if (typeof document !== 'undefined' &&
        document.querySelector('[role="menu"], [role="listbox"], [role="dialog"]')) {
      return;
    }

    // App-owned overlays in z-order (topmost first).
    const APP_OVERLAY_CLOSERS = [
      ['abandonedMatchModal', { abandonedMatchModal: null }],
      ['draftCancelledModal', { draftCancelledModal: null }],
      ['miniProfileTarget', { miniProfileTarget: null, miniProfileAnchor: null }],
      ['friendInvitePickerOpen', { friendInvitePickerOpen: false }],
      ['skillTreeOpen', { skillTreeOpen: false }],
      ['hallOfFameOpen', { hallOfFameOpen: false }],
      ['seasonRankingOpen', { seasonRankingOpen: false }],
      ['settingsOpen', { settingsOpen: false }],
      ['mailboxOpen', { mailboxOpen: false }],
      ['friendsSidebarOpen', { friendsSidebarOpen: false }],
    ];
    for (const [key, patch] of APP_OVERLAY_CLOSERS) {
      if (s[key]) {
        event.preventDefault();
        event.stopPropagation();
        this.setState(patch);
        return;
      }
    }

    // GameBoard's own window-level handler closes its internal overlays; defer
    // (no stopPropagation) so the event reaches window and that handler fires.
    if (s.isGameBoardOpen && this.gameBoardRef?.hasOpenOverlay?.()) return;

    if (!s.isGameBoardOpen) return;

    // Nothing else is open. The pause menu is the fallback, but it's gated
    // off behind PAUSE_MENU_ENABLED. When disabled, Escape is a no-op here.
    if (!PAUSE_MENU_ENABLED) return;

    event.preventDefault();
    event.stopPropagation();
    this.setState({ gameMenuOpen: true });
  };

  handleDocumentKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (isEditableTarget(event.target)) return;
      this.handleAppEscape(event);
      return;
    }

    if (this.state.gameMenuOpen) {
      return;
    }

    // O toggles the social modal (friends, chat, profile). F is taken
    // by card-flip in the game board, and O isn't bound anywhere else
    // in the app. Blocked while typing into a form. Otherwise hitting
    // O in a chat message or deck name would close the thing the user
    // is writing in.
    if (
      (event.key === 'o' || event.key === 'O')
      && !event.metaKey && !event.ctrlKey && !event.altKey
      && !isEditableTarget(event.target)
      && this.state.loggedIn
      && this.state.arenaProfile
    ) {
      event.preventDefault();
      this.handleToggleMailbox();
      return;
    }

    if (this.state.isGameBoardOpen) {
      this.gameBoardRef?.handleGameHotkey(event);
    }
  };

  handleThemeMediaQueryChange = () => {
    if (this.state.themePreference === 'system') {
      applyThemePreference('system');
    }
  };

  setThemePreference = () => {
    // Dark mode is forced. No-op
  };

  performLoadSavedDeck = async (deckId) => {
    if (!deckId || !Array.isArray(this.state.sorceryCards)) {
      return;
    }

    try {
      const savedDeck = await loadSavedDeckById(deckId, 'sorcery');

      if (!savedDeck) {
        return;
      }

      const activeCards = this.getActiveCards();
      const restoredCards = await restoreSavedDeckCards({
        savedEntries: savedDeck.cards || [],
        cardIndex: this.getSavedDeckCardIndex(activeCards),
        resolvePreferredPrinting: this.resolvePreferredPrinting,
      });

      this.setState({
        sorceryCards: (activeCards || []).map((card) => {
          const restoredCard = restoredCards.find((entry) => entry?.card?.unique_id === card.unique_id)?.card;
          return restoredCard || card;
        }),
      });
    } catch (error) {
      console.error('Failed to load saved deck:', error);
      this.setState({
        savedDecksError: error?.message || 'Failed to load saved deck',
      });
    }
  };

  loadSavedDeck = async (deckId) => {
    if (!deckId || !Array.isArray(this.getActiveCards())) {
      return;
    }

    await this.performLoadSavedDeck(deckId);
  };

  deleteSavedDeck = async (deckId) => {
    if (!deckId) {
      return;
    }

    try {
      await deleteSavedDeckById(deckId, 'sorcery');
      this.setState((state) => ({
        savedDecks: state.savedDecks.filter((deck) => deck.id !== deckId),
      }));
    } catch (error) {
      console.error('Failed to delete saved deck:', error);
      this.setState({
        savedDecksError: error?.message || 'Failed to delete saved deck',
      });
    }
  };

  renderAuthOverlay() {
    return (
      <AnimatePresence>
        {this.state.authChecking ? (
          <motion.div
            key="auth-overlay"
            className="fixed inset-0 bg-black flex items-center justify-center z-[200]"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          >
            <RuneSpinner size={120} useViewportUnits />
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  }

  handleIntroComplete = () => {
    try { introSeenStore().setItem(INTRO_SEEN_KEY, '1'); } catch {}
    this.setState({ introComplete: true });
  };

  // The live board plus the replay chrome. Also rendered for a public replay
  // link before login, so it must not assume a profile.
  renderMatchBoard() {
    // Once per render: both identity props come from the same computation.
    const replaySeats = this.state.replaySession
      ? replaySeatIdentities(this.state.replaySession, this.state.sorceryCards)
      : null;
    const replayHistoryRow = this.state.replaySession
      ? (this.state.arenaProfile?.matchHistory || []).find((m) => m.roomCode === this.state.replaySession.roomCode) || null
      : null;
    return (
      <>
        {this.state.isGameBoardOpen && this.state.sessionMode ? (
          <MatchAssetGate
            // Not keyed on the replay viewpoint: switching sides flips the
            // seat in place (replayViewpoint controller), it does not reload
            // the match (QA#151).
            key={(this.state.sessionId || this.state.roomCode || 'match')
              + (this.state.replaySession ? ':replay' : '')}
            deck={this._getPreloadDeck()}
            opponentDeck={this.state.opponentDeckForPreload}
            needsOpponentPreload={this._matchNeedsOpponentPreload()}
            sorceryCards={this.state.sorceryCards}
            playmatUrl={getPlaymatUrl()}
            matchMusicUrl={this._getMatchMusicUrl()}
            onReady={() => this.setState({ matchAssetsReady: true })}
            onAbandon={this.handleAbandonStuckLoad}
          >
          <GameBoard
            ref={(ref) => { this.gameBoardRef = ref; }}
            assetsReady={this.state.matchAssetsReady}
            onOpponentDeckResolved={(deck) => this.setState({ opponentDeckForPreload: deck })}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            savedDecks={this.state.isArenaMatch ? this.getArenaDecksForGameBoard() : this.state.savedDecks}
            onOpenSettings={this.handleOpenSettings}
            sessionMode={this.state.sessionMode}
            sessionId={this.state.sessionId}
            joinRoomCode={this.state.roomCode}
            isArenaMatch={this.state.isArenaMatch}
            isRankedMatch={this.state.isRankedMatch}
            isCasualSandbox={this.state.isCasualSandbox}
            isSandboxMatch={this.state.isSandboxMatch}
            instanceId={this.state.currentInstanceId || null}
            instanceIsHost={!!this.state.currentInstanceIsHost}
            instanceVisibility={this.state.currentInstanceVisibility || null}
            instanceMode={this.state.currentInstanceMode || null}
            startingHp={this.state.currentStartingHp || 20}
            automations={this.state.currentAutomations !== false}
            isResumeJoin={!!this.state.isResumeJoin}
            draftMatchId={this.state.currentDraftMatchId || null}
            arenaSelectedDeckId={this.state.arenaSelectedDeckId}
            // A replay is watched, not played: the seats belong to the two
            // people who sat there, never to the viewer (QA#150).
            arenaPlayerInfo={replaySeats
              ? replaySeats.local
              : this.state.isArenaMatch && this.state.arenaProfile ? {
                id: this.state.arenaProfile.id,
                name: this.state.arenaProfile.name,
                avatarUrl: this.getArenaAvatarUrl(this.state.arenaProfile.profileAvatar),
                discordHandle: this.state.arenaProfile.discordHandle || null,
                displayedCrownAvatarId: this.state.arenaProfile.displayedCrownAvatarId || null,
                isFounder: !!this.state.arenaProfile.isFounder,
                isQaTester: !!this.state.arenaProfile.isQaTester,
              } : null}
            arenaOpponentInfo={replaySeats
              ? replaySeats.remote
              : this.state.arenaMatchmakingOpponent || null}
            isSpectating={this.state.isSpectating}
            replayDriver={this.state.replaySession?.driver || null}
            replayViewpoint={this.state.replaySession?.viewpoint || 'p1'}
            onMatchReward={this.handleMatchReward}
            onViewProfile={this.handleViewFriendProfile}
            onExit={this.handleGameBoardExit}
            onWatchReplay={this.handleWatchReplayFromResult}
          />
          </MatchAssetGate>
        ) : null}
        {this.state.isSpectating && !this.state.replaySession ? <SpectatorBanner onLeave={this.handleLeaveSpectate} /> : null}
        {this.state.replaySession && this.state.isGameBoardOpen ? (
          <ReplayOverlay
            // No viewpoint key: the sidebar reads the viewpoint at render
            // time only, and a remount here would drop the feed's scroll
            // position and follow state on every side swap. The board
            // below flips its seat in place off the same prop.
            driver={this.state.replaySession.driver}
            view={this.state.replaySession.view}
            names={this.state.replaySession.names}
            sorceryCards={this.state.sorceryCards}
            comments={this.state.replaySession.comments}
            selfId={this.state.arenaProfile?.id}
            onPostComment={this.handlePostReplayComment}
            onDeleteComment={this.handleDeleteReplayComment}
            onToggleCommentLike={this.handleToggleReplayCommentLike}
            onOpenProfile={this.handleViewFriendProfile}
            viewpoint={this.state.replaySession.viewpoint}
            onSetViewpoint={this.handleSetReplayViewpoint}
            initialNoteKey={this.state.replaySession.noteKey}
            highlightCommentId={this.state.replaySession.commentId}
            onExit={this.handleLeaveReplay}
            // The viewer's own row for this match: participants hold one and
            // name the match from here as from the lists.
            matchName={replayHistoryRow?.customName || null}
            onRenameMatch={replayHistoryRow ? (name) => this.handleUpdateMatchMeta(replayHistoryRow.roomCode, { name }) : null}
            canShare={!!this.state.arenaProfile}
            canComment={!!this.state.arenaProfile}
            friends={this.state.friendListData?.friends || []}
            onShareToFriend={this.handleShareReplayToFriend}
            onShareToTavern={this.handleShareReplayToTavern}
            onCopyLink={this.handleCopyReplayLink}
            onRequestLogin={this.handleReplayRequestLogin}
            onOpenCard={(cardId) => this.gameBoardRef?.handleOpenLogbookMention?.(cardId)}
            onCardHover={(cardId, element) => this.gameBoardRef?.handleLogbookMentionHover?.(cardId, element)}
            onCardHoverEnd={() => this.gameBoardRef?.handleLogbookMentionHoverEnd?.()}
            onRowHover={(cardIds) => this.gameBoardRef?.setMentionCellHighlights?.(cardIds)}
            onRowHoverEnd={() => this.gameBoardRef?.setMentionCellHighlights?.(null)}
          />
        ) : null}
      </>
    );
  }

  render() {
    // Dev-only foil white-zone editor (?dev=1&foilzones=1). Short-circuits
    // the app so it needs no login; the editor loads the catalog itself.
    if (isDevBuild()) {
      try {
        const q = new URLSearchParams(window.location.search);
        if (q.get('foilzones') === '1') return <FoilZoneEditor />;
        if (q.get('matchresult') === '1') return <MatchResultGallery />;
      } catch (_) { /* no-op */ }
    }
    if (!this.state.introComplete) {
      return <IntroSplash onComplete={this.handleIntroComplete} />;
    }
    if (this.state.isQuitting) {
      return <LoadingIndicator message={t('Closing Valkenhall…')} />;
    }
    if (!this.state.loggedIn && !this.state.authChecking) {
      if (this.state.replaySession && this.state.isGameBoardOpen) {
        return <>{this.renderMatchBoard()}<Toaster /></>;
      }
      if (this.state.replayLinkPending) {
        return <><LoadingIndicator message={t('Loading replay')} detail={t('Opening the shared match...')} /><Toaster /></>;
      }
      return (
        <>
          <LoginScreen onLogin={this.handleLogin} serverUnreachable={this.state.serverUnreachable} />
          <Toaster />
          {this.state.gameMenuOpen ? <GameMenu onResume={this.handleGameMenuResume} onQuit={this.handleGameMenuQuit} onOpenSettings={this.handleOpenSettings} appVersion={this.state.updateStatus?.currentVersion} /> : null}
          {this.renderAuthOverlay()}
        </>
      );
    }

    if (!this.state.loggedIn) {
      return this.renderAuthOverlay();
    }

    if (this.state.needsAssetDownload) {
      return (
        <FirstRunDownload
          missingSlugs={this.state.missingAssetSlugs}
          onComplete={this.handleAssetDownloadComplete}
        />
      );
    }

    if (this.state.pendingTokenUpdates?.length > 0) {
      return (
        <TokenUpdateGate
          items={this.state.pendingTokenUpdates}
          onComplete={this.handleTokenUpdatesComplete}
        />
      );
    }

    const showDeckGallery = this.state.arenaView === 'deck-gallery' && !this.state.isGameBoardOpen;
    const showDeckEditor = this.state.arenaView === 'deck-editor' && !this.state.isGameBoardOpen && this.state.editingDeckData;
    const showArena = !this.state.isGameBoardOpen && this.state.arenaView !== 'deck-gallery' && this.state.arenaView !== 'deck-editor';

    return (
      // framer-motion defaults to `reducedMotion: "never"`, so every
      // motion.div in the app ignored the OS setting. "user" keeps the
      // opacity crossfades (they aid comprehension) and drops the
      // positional part of each entrance for players who ask for it.
      <MotionConfig reducedMotion="user">
      <>
        <AnimatePresence>
        {showDeckGallery ? (
          <motion.div key="deck-gallery" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <DeckGallery
            initialTab={this.state.galleryInitialTab}
            savedDecks={this.getArenaSavedDecks()}
            sorceryCards={this.state.sorceryCards}
            profile={this.state.arenaProfile}
            onCreateDeck={this.handleCreateNewDeck}
            onOpenDeck={this.handleOpenDeckInEditor}
            onOpenLibraryDeck={this.handleOpenLibraryDeckInEditor}
            onDeleteDeck={this.handleDeleteDeckFromGallery}
            onDuplicateDeck={this.handleDuplicateDeckFromGallery}
            onToggleShare={this.handleToggleShareFromGallery}
            onToggleFavorite={this.handleToggleFavoriteDeckFromGallery}
            onSetDeckTags={this.handleSetDeckTagsFromGallery}
            onDeleteTag={this.handleDeleteTagFromGallery}
            onSaveShowcase={this.handleSaveDeckShowcase}
            onRenameDeck={this.handleRenameDeckFromGallery}
            onImportDeck={this.handleImportDeck}
            onBack={this.handleBackToHubFromGallery}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
            onToggleFriends={this.handleToggleFriendsSidebar}
            friendListData={this.state.friendListData}
          />
          </motion.div>
        ) : null}
        {showDeckEditor ? (
          <motion.div key="deck-editor" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <DeckEditor
            deck={this.state.editingDeckData}
            highlightCommentId={this.state.deckHighlightCommentId}
            sorceryCards={this.state.sorceryCards}
            arenaProfile={this.state.arenaProfile}
            savedDecks={this.state.savedDecks}
            onSave={this.handleSaveDeckFromEditor}
            onBack={this.handleBackToGallery}
            onViewProfile={this.handleViewFriendProfile}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
            onToggleFriends={this.handleToggleFriendsSidebar}
            friendListData={this.state.friendListData}
          />
          </motion.div>
        ) : null}
        {this.renderMatchBoard()}
        {showArena && this.state.arenaLoading ? (
          <LoadingIndicator message={t('Loading Arena')} detail={t('Fetching your profile...')} />
        ) : null}
        {showArena && this.state.arenaProfile && !this.hasValidUsername() ? (
          <ArenaUsernamePrompt
            currentName={this.state.arenaProfile.name}
            onRegister={this.registerArenaUsername}
          />
        ) : null}
        {/* L1 first-run flow. Opens the deck picker DIRECTLY. We
            deliberately skip the Progression screen here because L1
            isn't a level-up; it's the starting state. The first
            interaction should be "choose your starter", not a
            "Level 1 reached" celebration. The picker's confirm path
            routes through handleProgressionDeckChosen which stamps
            starter_deck server-side, satisfying this condition
            permanently for subsequent loads. */}
        {showArena && this.state.arenaProfile && this.hasValidUsername() && !this.state.arenaProfile.starterDeck && !this.state.pickerOpen ? (
          (() => {
            // Self-trigger on next paint. SetState during render is
            // a no-go but a microtask after the component returns is
            // fine. Guarded with the same condition so a transient
            // profile-load gap doesn't double-fire.
            queueMicrotask(() => {
              if (!this.state.arenaProfile?.starterDeck && !this.state.pickerOpen) {
                this.setState({ pickerOpen: true });
              }
            });
            return null;
          })()
        ) : null}

        {/* Unified Progression screen. Replaces the legacy
            LevelUpOverlay + queue/picker-mode dance with one click-
            to-claim surface. Mounted whenever progressionScreenOpen
            is true. The deck picker mounts on top via its own
            condition below when a deck cell is clicked. */}
        {showArena && this.state.arenaProfile && this.hasValidUsername() && this.state.progressionScreenOpen ? (
          <ProgressionScreen
            progression={this.state.arenaProfile.progression}
            progressionRefreshTag={this.state.progressionRefreshTag}
            sorceryCards={this.state.sorceryCards}
            ownedDeckIds={this.state.arenaProfile.starterDecksOwned || []}
            hiddenForPicker={this.state.pickerOpen}
            onClaimDeck={this.handleProgressionDeckCellClicked}
            onRewardClaimed={this.handleProgressionRewardClaimed}
            onClose={() => this.setState({ progressionScreenOpen: false })}
          />
        ) : null}

        {/* Deck picker. Opens on top of the Progression screen when
            the player clicks a deck cell. Confirming routes through
            handleProgressionDeckChosen which closes the picker and
            bumps progressionRefreshTag so the screen advances focus. */}
        {showArena && this.state.arenaProfile && this.hasValidUsername() && this.state.pickerOpen ? (
          <ArenaStarterPicker
            sorceryCards={this.state.sorceryCards}
            decks={PROGRESSION_DECKS}
            ownedIds={this.state.arenaProfile.starterDecksOwned || []}
            title={(this.state.arenaProfile.starterDecksOwned?.length || 0) === 0 ? t('Choose Your Path') : t('New Deck Unlocked')}
            subtitle={(this.state.arenaProfile.starterDecksOwned?.length || 0) === 0
              ? t('Select a starter deck to begin your journey.')
              : t("Choose your next precon. Any decks you already own are greyed out, and you'll earn another pick at each milestone.")}
            confirmLabel={t('Claim this deck')}
            onChoose={this.handleProgressionDeckChosen}
          />
        ) : null}
        {/* First-playmat pick. Slots between the starter deck and the
            Discord prompt. Shows once the player has a starter deck but
            hasn't claimed their free playmat yet (and the deck picker
            isn't open). */}
        {showArena && this.state.arenaProfile && this.hasValidUsername()
          && this.state.arenaProfile.starterDeck
          && !this.state.arenaProfile.starterPlaymatClaimed
          && !this.state.pickerOpen ? (
          <ArenaFirstPlaymatPicker
            sorceryCards={this.state.sorceryCards}
            profile={this.state.arenaProfile}
            onChosen={this.handleStarterPlaymatChosen}
          />
        ) : null}
        {showArena && this.state.arenaProfile?.starterDeck && this.state.arenaProfile.starterPlaymatClaimed && this.hasValidUsername() && this.shouldShowDiscordPrompt() ? (
          <ArenaDiscordPrompt
            onSave={this.saveArenaDiscordHandle}
            onSkip={this.skipArenaDiscordPrompt}
          />
        ) : null}
        {showArena && this.state.arenaProfile?.starterDeck && this.state.arenaProfile.starterPlaymatClaimed && this.hasValidUsername() && !this.shouldShowDiscordPrompt() && this.isHubVisible() && isPhoneLayout() ? (
          <MobileHub
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            version={this.state.updateStatus?.currentVersion}
            onOpenPlay={this.openPlay}
            onOpenDeckBuilder={this.openArenaDeckBuilder}
            onLogout={this.handleLogout}
            // Remaining navigation handlers are passed ONLY once the
            // destination screen has a mobile layout; missing ones toast
            // "coming soon" instead of dumping the player into a
            // desktop-sized screen with no way back. Play routes into the
            // instance-first flow (SessionBrowser's phone branch);
            // Settings, Leaderboard and Profile are mobile-native inside
            // MobileHub.
          />
        ) : null}
        {showArena && this.state.arenaProfile?.starterDeck && this.state.arenaProfile.starterPlaymatClaimed && this.hasValidUsername() && !this.shouldShowDiscordPrompt() && this.isHubVisible() && !isPhoneLayout() ? (
          <ArenaHub
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            rank={this.state.arenaProfile.rank}
            isAdmin={hasStaffAccess(this.state.arenaProfile)}
            onOpenPlay={this.openPlay}
            onOpenLfg={() => this.setState({ showLfg: true })}
            onChallenge={this.handleChallenge}
            onOpenChat={this.handleSendMailFromProfile}
            getPlayableDecks={this.getPlayableDecks}
            onDevQuickMatch={this.startDevQuickMatch}
            onDevMatchFound={this.simulateLfgMatch}
            onOpenStore={this.openArenaStore}
            onOpenDeckBuilder={this.openArenaDeckBuilder}
            onOpenAuctionHouse={this.openAuctionHouse}
            onOpenArcaneTrials={this.openArcaneTrials}
            onQuit={isWeb() ? null : this.handleGameMenuQuit}
            onUpdateName={this.updateArenaName}
            onUpdateProfile={(profile) => this.setState({ arenaProfile: profile })}
            friendListData={this.state.friendListData}
            onToggleFriends={this.handleToggleFriendsSidebar}
            onOpenSettings={this.handleOpenSettings}
            onOpenSkillTree={this.handleOpenSkillTree}
            onOpenProgression={() => this.setState({ progressionScreenOpen: true })}
            onOpenHallOfFame={this.handleOpenHallOfFame}
            onOpenSeasonRanking={this.handleOpenSeasonRanking}
            canModerateChat={this.canModerateChat()}
            onWatchReplay={this.handleWatchReplay}
            onViewProfile={this.handleViewFriendProfile}
            updateStatus={this.state.updateStatus}
            replayHubTutorial={this.state.replayHubTutorialRequested}
            onHubTutorialDismissed={() => this.setState({ replayHubTutorialRequested: false })}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
          />
        ) : null}
        {this.state.arenaView === 'arcane-trials' && !this.state.isGameBoardOpen ? (
          <motion.div
            key="arcane-trials"
            className="fixed inset-0 z-[45]"
            {...PAGE_TRANSITION_PROPS}
          >
          <ArcaneTrials
            season={this.state.currentSeason}
            progress={this.state.arenaProfile?.seasonProgress}
            sorceryCards={this.state.sorceryCards}
            profile={this.state.arenaProfile}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
            onToggleFriends={this.handleToggleFriendsSidebar}
            friendListData={this.state.friendListData}
            onClaimReward={this.handleClaimSeasonReward}
            onOpenSeasonRanking={this.handleOpenSeasonRanking}
            onBack={() => { playUI(UI.CLOSE); this.setState({ arenaView: 'hub' }); }}
          />
          </motion.div>
        ) : null}
        {this.state.settingsOpen ? (
          <motion.div key="settings" className="fixed inset-0 z-[100]" {...PAGE_TRANSITION_PROPS}>
          <SettingsScreen
            profile={this.state.arenaProfile}
            updateStatus={this.state.updateStatus}
            updateManager={this.updateManager}
            onApply={this.handleApplyUpdate}
            onBack={this.handleCloseSettings}
            onLogout={this.handleLogout}
            onQuit={this.handleGameMenuQuit}
            onUpdateProfile={(profile) => this.setState({ arenaProfile: profile }, () => saveArenaProfile(profile))}
            onReplayHubTutorial={() => {
              // Close settings, return to the hub, and raise the
              // replay flag so ArenaHub.componentDidUpdate re-mounts
              // the overlay the moment it becomes visible again.
              this.setState({
                settingsOpen: false,
                arenaView: 'hub',
                replayHubTutorialRequested: true,
              });
            }}
          />
          </motion.div>
        ) : null}
        {this.state.skillTreeOpen ? (
          <motion.div key="skill-tree" className="fixed inset-0 z-[100]" {...PAGE_TRANSITION_PROPS}>
            <SkillTreeScreen
              profile={this.state.arenaProfile}
              isAdmin={hasCap(this.state.arenaProfile, 'dev.skills')}
              onClose={this.handleCloseSkillTree}
            />
          </motion.div>
        ) : null}
        {this.state.viewingSkillTreeProfile ? (
          <motion.div key="skill-tree-readonly" className="fixed inset-0 z-[100]" {...PAGE_TRANSITION_PROPS}>
            <SkillTreeScreen
              profile={this.state.arenaProfile}
              viewProfile={this.state.viewingSkillTreeProfile}
              isAdmin={false}
              onClose={() => {
                const returnId = this.state.skillTreeReturnToProfile;
                this.setState({
                  viewingSkillTreeProfile: null,
                  skillTreeReturnToProfile: null,
                  // Restore the profile overlay we came from.
                  viewingFriendProfile: returnId,
                });
              }}
            />
          </motion.div>
        ) : null}
        {this.state.hallOfFameOpen ? (
          <motion.div key="hall-of-fame" className="fixed inset-0 z-[100]" {...PAGE_TRANSITION_PROPS}>
            <HallOfFameScreen
              sorceryCards={this.state.sorceryCards}
              onViewProfile={this.handleViewFriendProfileFromHallOfFame}
              onClose={this.handleCloseHallOfFame}
            />
          </motion.div>
        ) : null}
        {this.state.seasonRankingOpen ? (
          <motion.div key="season-ranking" className="fixed inset-0 z-[100]" {...PAGE_TRANSITION_PROPS}>
            <SeasonRankingScreen
              sorceryCards={this.state.sorceryCards}
              onViewProfile={this.handleViewFriendProfile}
              onClose={this.handleCloseSeasonRanking}
            />
          </motion.div>
        ) : null}
        {this.state.seasonChampionCelebration ? (
          <SeasonChampionCelebrationOverlay
            champion={this.state.seasonChampionCelebration}
            onOpenMailbox={() => {
              // Close the overlay and surface the mailbox so the
              // player can claim immediately. The Mail tab is the
              // default and the new champion mails land there.
              this.setState({
                seasonChampionCelebration: null,
                mailboxOpen: true,
              });
            }}
            onDismiss={() => this.setState({ seasonChampionCelebration: null })}
          />
        ) : null}
        {showArena && this.state.arenaView === 'deck-select' ? (
          <motion.div key="deck-select" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <ArenaDeckSelect
            decks={(() => {
              const all = this.getArenaSavedDecks();
              const ownedMap = buildOwnedMap(this.state.arenaProfile?.collection || []);
              const cardCatalog = buildCardCatalog(this.state.sorceryCards);
              // Sandbox queue accepts both deck modes; other queues hide
              // sandbox-marked decks and enforce ownership.
              const isSandboxQueue = this.state.pendingQueueMode === 'sandbox';
              const format = this.state.pendingQueueFormat;
              return all.filter((d) => {
                const deckMode = d.gameMode || 'constructed';
                if (!isSandboxQueue && deckMode === 'sandbox') return false;
                // Format pick gates the deck list to matching decks:
                // a peasant match shows only peasant decks; a
                // constructed match hides them. Sandbox queues
                // ignore format entirely.
                if (!isSandboxQueue && format === 'peasant' && deckMode !== 'peasant') return false;
                if (!isSandboxQueue && format === 'constructed' && deckMode === 'peasant') return false;
                return canPlayDeck(d.cards || [], ownedMap, {
                  cardCatalog,
                  skipOwnership: isSandboxQueue,
                  gameMode: deckMode,
                }).canPlay;
              });
            })()}
            pickerMode={this.state.pendingQueueMode || 'ranked'}
            format={this.state.pendingQueueFormat}
            {...(this.state.pendingInstanceCreate?.visibility === 'solo' ? {
              // Solo two-slot pick: first pass = your deck, second pass =
              // the opponent slot's deck (you pilot both).
              titleOverride: this.state.pendingInstanceCreate.creatorDeckId
                ? t('Choose the Opponent Deck')
                : t('Choose Your Deck'),
              subtitleOverride: this.state.pendingInstanceCreate.creatorDeckId
                ? t('You pilot this side too. Any deck. No rewards.')
                : t('Solo sandbox. Any deck. No rewards.'),
            } : {})}
            {...(this.state.pendingInstanceCreate?.visibility === 'bot' ? {
              // Bot two-slot pick: your deck, then the bot's deck.
              titleOverride: this.state.pendingInstanceCreate.creatorDeckId
                ? t("Choose the Bot's Deck")
                : t('Choose Your Deck'),
              subtitleOverride: this.state.pendingInstanceCreate.creatorDeckId
                ? t('The practice bot pilots this deck. Any deck. No rewards.')
                : t('Practice match. Any deck. No rewards.'),
            } : {})}
            ownedMap={buildOwnedMap(this.state.arenaProfile?.collection || [])}
            sorceryCards={this.state.sorceryCards}
            profile={this.state.arenaProfile}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            onConfirm={this.confirmDeckAndQueue}
            onCancel={() => this.setState((s) => ({
              // Return to wherever the user entered the mode from.
              // 'play' when they came off the mode-picker, 'hub'
              // otherwise (chat invite accept, match-invite, etc).
              arenaView: s.arenaBackTarget || 'hub',
              arenaBackTarget: 'hub',
              pendingInstanceCreate: null,
              pendingInviteFriendId: null,
              pendingInviteAcceptSenderId: null,
              pendingQueueFormat: null,
              hubQuickHost: false,
              challengeTarget: null,
              lfgHostInstanceId: null,
              joiningInstanceId: null,
            }))}
          />
          </motion.div>
        ) : null}
        {showArena && this.state.arenaView === 'matchmaking' ? (
          <motion.div key="matchmaking" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <ArenaMatchmaking
            opponent={this.state.arenaMatchmakingOpponent}
            mode={this.state.arenaMatchmakingMode || 'ranked'}
            onCancel={this.cancelMatchmaking}
          />
          </motion.div>
        ) : null}
        {showArena && this.state.arenaView === 'store' ? (
          <motion.div key="store" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <ArenaStore
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            savedDecks={this.state.savedDecks}
            pendingPacks={this.state.arenaPendingPacks}
            onBuyPack={this.buyArenaPack}
            onOpenPacks={this.openNextPack}
            onProfileUpdate={this.handleShardPurchaseUpdate}
            onBack={() => this.setState({ arenaView: 'hub' })}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
            onToggleFriends={this.handleToggleFriendsSidebar}
            friendListData={this.state.friendListData}
          />
          </motion.div>
        ) : null}
        {showArena && this.state.arenaView === 'auction-house' ? (
          <motion.div key="auction-house" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <AuctionHouse
            profile={this.state.arenaProfile}
            initialTab={this.state.auctionInitialTab}
            focusListingId={this.state.auctionFocusListingId}
            savedDecks={this.state.savedDecks}
            sorceryCards={this.state.sorceryCards}
            onRefreshDecks={this.refreshSavedDecks}
            onUpdateProfile={(profile) => this.setState({ arenaProfile: profile }, () => saveArenaProfile(profile))}
            onBack={() => this.setState({ arenaView: 'hub', auctionInitialTab: null, auctionFocusListingId: null })}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
            onToggleFriends={this.handleToggleFriendsSidebar}
            friendListData={this.state.friendListData}
          />
          </motion.div>
        ) : null}
        {showArena && this.state.preconCollected ? (
          <PreconCollectedModal
            deck={this.state.preconCollected.deck}
            quantity={this.state.preconCollected.quantity}
            sorceryCards={this.state.sorceryCards}
            onAccept={() => this.setState({ preconCollected: null })}
          />
        ) : null}
        {showArena && this.state.arenaView === 'pack-opening' && this.state.arenaOpenedPack ? (
          <ArenaPackOpening
            key={this.state.arenaOpenedPack?.setKey + '-' + (this.state.arenaPendingPacks?.length || 0) + '-' + (this.state.arenaAutoOpenPack ? 'auto' : '')}
            pack={this.state.arenaOpenedPack}
            allPendingPacks={this.state.arenaPendingPacks}
            sorceryCards={this.state.sorceryCards}
            profile={this.state.arenaProfile}
            autoOpen={this.state.arenaAutoOpenPack || false}
            onDone={() => { this.setState({ arenaAutoOpenPack: false }); this.handlePackDone(); }}
            onOpenAnother={this.handleOpenAnother}
            onOpenFromSet={this.openFromSet}
            onPackOpened={this.handlePackOpened}
            onCollectionUpdate={this.handlePackCollectionUpdate}
            canAffordAnother={this.state.arenaProfile?.coins >= CURRENCY.PACK_PRICE}
            remainingPacks={this.state.arenaPendingPacks?.length || 0}
          />
        ) : null}
        {showArena && this.state.arenaView === 'play' ? (
          <motion.div key="play" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
            {/* Instance-first flow: SessionBrowser replaces the old
                PlayScreen tile grid. ModePickerScreen and
                ModeSetupDialog stack on top when the user is
                mid-create. The old PlayScreen import is kept above
                only for the legacy deck-select back-target logic;
                its render path no longer fires. */}
            {this.state.pickerVisibility && !this.state.setupForMode ? (
              <ModePickerScreen
                visibility={this.state.pickerVisibility}
                profile={this.state.arenaProfile}
                sorceryCards={this.state.sorceryCards}
                onBack={this.handleCancelPicker}
                onPickMode={this.handlePickMode}
                    friendListData={this.state.friendListData}
                onToggleMailbox={this.handleToggleMailbox}
                mailboxUnreadCount={this.mailboxBadgeCount()}
                mailboxOpen={this.state.mailboxOpen}
                onToggleFriends={this.handleToggleFriendsSidebar}
              />
            ) : this.state.hubQuickHost ? null : (
              // Hub-initiated host / challenge: nothing mounts under the
              // setup dialog, so the Play screen's first-run tutorial can't
              // stack on top of the dialog's own.
              <SessionBrowser
                profile={this.state.arenaProfile}
                sorceryCards={this.state.sorceryCards}
                matchHistory={this.state.arenaProfile?.matchHistory || []}
                onWatchReplay={this.handleWatchReplay}
                onUpdateMatchMeta={this.handleUpdateMatchMeta}
                    friendListData={this.state.friendListData}
                onBack={() => { playUI(UI.CLOSE); this.setState({ arenaView: 'hub' }); }}
                onResumeTournament={(eventId) => this.openDraftLobby(eventId)}
                onToggleMailbox={this.handleToggleMailbox}
                mailboxUnreadCount={this.mailboxBadgeCount()}
                mailboxDropdown={
                  <Mailbox
                        onViewProfile={this.handleViewFriendProfile}
                    open={this.state.mailboxOpen}
                    onClose={this.handleCloseMailbox}
                    lastChatMessage={this.state.lastChatMessage}
                    lastChatClaimed={this.state.lastChatClaimed}
                    onProfileReload={this.reloadProfile}
                    onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                    onRejoinMatch={this.handleRejoinMatch}
                    onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                    profile={this.state.arenaProfile}
                    friendListData={this.state.friendListData}
                    sorceryCards={this.state.sorceryCards}
                    onProfileUpdate={this.handleMailProfileUpdate}
                    selectedMailId={this.state.mailboxSelectedMailId}
                    initialView={this.state.mailboxView}
                    composeRecipientId={this.state.mailboxComposeRecipientId}
                    onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                    activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                    onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                    unreadByTab={this.mailboxUnreadTabs()}
                    canModerateChat={this.canModerateChat()}
                    onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
                  />
                }
                onToggleFriends={this.handleToggleFriendsSidebar}
                onPickVisibility={this.handlePickVisibility}
                onInstanceAction={this.handleInstanceAction}
                onJoinPublic={this.handleJoinPublic}
              />
            )}
            {this.state.setupForMode ? (
              <ModeSetupDialog
                modeKey={this.state.setupForMode.modeKey}
                visibility={this.state.setupForMode.visibility}
                profile={this.state.arenaProfile}
                onCancel={this.handleCancelSetup}
                onConfirm={this.handleSetupConfirm}
              />
            ) : null}
            {this.state.draftCreateSetup ? (
              <CreateDraftModal
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                modeKey={this.state.draftCreateSetup.modeKey}
                title={this.state.draftCreateSetup.modeKey === 'sealed' ? t('Create Sealed Event') : t('Create Draft Event')}
                initialVisibility={this.state.draftCreateSetup.visibility === 'public' ? 'public' : 'private'}
                presetInvitees={this.state.challengeTarget ? [{ id: this.state.challengeTarget.id, name: this.state.challengeTarget.name }] : undefined}
                presetSetKey={this.state.challengeSetKey || undefined}
                lockVisibility
                onCreate={this.handleDraftCreateFromSession}
                onClose={this.handleCancelDraftCreate}
              />
            ) : null}
          </motion.div>
        ) : null}
        {/* draft-lobby view is intentionally gone. Pending drafts
            live in the queue indicator + SessionBrowser; phase
            transitions are driven by the app-level
            _startDraftEventWatcher which calls handleDraftStarted
            / handleDraftBuildingPhase / handleDraftTournamentStart
            / handleDraftComplete directly, routing the user into
            the matching screen without an intermediate lobby. */}
        {showArena && this.state.arenaView === 'draft' ? (
          <motion.div key="draft" className="fixed inset-0 z-[45]" {...PAGE_TRANSITION_PROPS}>
          <DraftBrowser
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            friendListData={this.state.friendListData}
            activeDraftEventId={this.state.draftEventId}
            onDraftJoined={(eventId) => this.setState({ draftEventId: eventId, draftPhase: 'lobby' })}
            onDraftLeft={() => this.setState({ draftEventId: null, draftPhase: null })}
            onDraftCancelled={this.handleDraftCancelled}
            onDraftStarted={this.handleDraftStarted}
            onBack={() => {
              playUI(UI.CLOSE);
              this.setState((s) => ({
                arenaView: s.arenaBackTarget || 'hub',
                arenaBackTarget: 'hub',
              }));
            }}
            onToggleMailbox={this.handleToggleMailbox}
            mailboxUnreadCount={this.mailboxBadgeCount()}
            mailboxOpen={this.state.mailboxOpen}
            mailboxDropdown={
              <Mailbox
                onViewProfile={this.handleViewFriendProfile}
                open={this.state.mailboxOpen}
                onClose={this.handleCloseMailbox}
                lastChatMessage={this.state.lastChatMessage}
                lastChatClaimed={this.state.lastChatClaimed}
                onProfileReload={this.reloadProfile}
                onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
                onRejoinMatch={this.handleRejoinMatch}
                onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
                profile={this.state.arenaProfile}
                friendListData={this.state.friendListData}
                sorceryCards={this.state.sorceryCards}
                onProfileUpdate={this.handleMailProfileUpdate}
                selectedMailId={this.state.mailboxSelectedMailId}
                initialView={this.state.mailboxView}
                composeRecipientId={this.state.mailboxComposeRecipientId}
                onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
                activeChatFriendId={this.state.mailboxChatFriend?.friendId}
                onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
                unreadByTab={this.mailboxUnreadTabs()}
                canModerateChat={this.canModerateChat()}
                onWatchReplay={this.handleWatchReplayFromMailbox}
                focusGlobalMessageId={this.state.focusGlobalMessageId}
                onTavernFocusDone={this.handleTavernFocusDone}
                focusNotifications={this.state.mailboxFocusNotifications}
              />
            }
            onToggleFriends={this.handleToggleFriendsSidebar}
          />
          </motion.div>
        ) : null}
        {showArena && this.state.arenaView === 'draft-picking' && this.state.draftEventId ? (
          <DraftPicker
            eventId={this.state.draftEventId}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            onBuildingPhase={this.handleDraftBuildingPhase}
          />
        ) : null}
        {showArena && this.state.arenaView === 'sealed-reveal' && this.state.draftEventId ? (
          <SealedPackOpener
            draftedCards={this.state.draftedCards}
            sorceryCards={this.state.sorceryCards}
            setKey={this.state.draftEventSetKey || 'beta'}
            profile={this.state.arenaProfile}
            onComplete={this.handleSealedRevealComplete}
          />
        ) : null}
        {showArena && this.state.arenaView === 'draft-building' && this.state.draftEventId ? (
          <DraftDeckBuilder
            eventId={this.state.draftEventId}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            draftedCards={this.state.draftedCards}
            onTournamentStart={this.handleDraftTournamentStart}
            onDeckSubmitted={this.handleDeckSubmitted}
            onBack={this.handleDraftBack}
          />
        ) : null}
        {showArena && this.state.arenaView === 'draft-tournament' && this.state.draftEventId ? (
          <DraftTournament
            eventId={this.state.draftEventId}
            mode={this.state.draftEventMode}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            onPlayMatch={this.handleDraftPlayMatch}
            onSpectate={this.handleDraftSpectate}
            onDraftComplete={this.handleDraftComplete}
            onBack={this.handleDraftBack}
          />
        ) : null}
        {showArena && this.state.arenaView === 'draft-results' ? (
          <DraftResults
            eventId={this.state.draftEventId}
            mode={this.state.draftEventMode}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            draftedCards={this.state.draftedCards}
            finalStandings={this.state.draftFinalStandings}
            prizes={this.state.draftPrizes}
            onBack={this.handleDraftBack}
          />
        ) : null}
        {this.state.tradeActive ? (
          <TradeWindow
            collection={this.state.arenaProfile?.collection}
            sorceryCards={this.state.sorceryCards}
            partnerName={this.state.tradePartnerName}
            onOfferChanged={this.handleTradeOfferChanged}
            onLockIn={this.handleTradeLockIn}
            onConfirm={this.handleTradeConfirm}
            onCancel={this.handleTradeCancel}
          />
        ) : null}
        {this.state.friendInvitePickerOpen ? (
          <FriendInvitePicker
            friendListData={this.state.friendListData}
            sorceryCards={this.state.sorceryCards}
            onInvite={this.handleFriendInvite}
            onClose={() => this.setState({ friendInvitePickerOpen: false })}
          />
        ) : null}
        <AchievementUnlockToast
          onClaimRequest={this.handleOpenClaimOverlay}
          paused={!!this.state.claimingAchievementId}
        />
        {/* AnimatePresence so the overlay's exit animation actually
            runs on dismiss. Without it the panel just unmounts. */}
        <AnimatePresence>
          {this.state.claimingAchievementId ? (() => {
            const achievement = getAchievement(this.state.claimingAchievementId);
            if (!achievement) return null;
            return (
              <AchievementClaimOverlay
                key={this.state.claimingAchievementId}
                achievement={achievement}
                onClaim={() => this.handleClaimAchievement(this.state.claimingAchievementId)}
                onDismiss={this.handleDismissClaimOverlay}
              />
            );
          })() : null}
        </AnimatePresence>
        {this.state.viewingFriendProfile ? (
          <FriendProfileOverlay
            profileId={this.state.viewingFriendProfile}
            currentUserId={this.state.arenaProfile?.id}
            selfProfile={this.state.arenaProfile}
            customDeckCount={this.getCustomDeckCount()}
            maxDeckLikes={this.getMaxDeckLikes()}
            unclaimedAchievements={this.state.arenaProfile?.unclaimedAchievements}
            onClaimAchievement={this.handleOpenClaimOverlay}
            lockNavigation={this.state.isGameBoardOpen}
            isFriend={this.state.friendListData?.friends?.some((f) => f.id === this.state.viewingFriendProfile) ?? false}
            friendPresence={this.state.friendListData?.friends?.find((f) => f.id === this.state.viewingFriendProfile)}
            sorceryCards={this.state.sorceryCards}
            onClose={this.handleCloseFriendProfile}
            onSendMail={this.handleSendMailFromProfile}
            onChallenge={this.handleChallenge}
            onWatchReplay={this.handleWatchReplay}
            onUpdateMatchMeta={this.handleUpdateMatchMeta}
            onRemoveFriend={async (id) => {
              // The overlay calls onClose() right after onRemoveFriend
              // (see FriendProfileOverlay's Remove Friend confirm click),
              // so the close path is already handled. We just fire the
              // API call here and let onClose route through the
              // profile-close handler.
              await friendsApi.removeFriend(id).catch(() => {});
            }}
            onOpenLibraryDeck={(deck) => {
              // Remember the profile we came from so Back from the
              // editor returns to that profile rather than the deck
              // gallery. The flag is consumed by handleBackToGallery.
              const fromProfileId = this.state.viewingFriendProfile;
              this.setState({
                viewingFriendProfile: null,
                deckEditorReturnToProfile: fromProfileId,
              });
              this.handleOpenLibraryDeckInEditor(deck);
            }}
            onSaveShowcase={this.updateShowcaseCards}
            onUpdateAvatar={this.updateArenaAvatar}
            onUpdateBackground={this.updateProfileBackground}
            onSaveDisplayedCrown={this.updateDisplayedCrown}
            onSavePinnedBadges={this.updatePinnedBadges}
            onViewSkillTree={(p) => {
              if (!p) return;
              // Remember the profile we came from so closing the
              // read-only skill tree returns to it instead of dumping
              // the visitor onto the hub.
              this.setState({
                viewingFriendProfile: null,
                viewingSkillTreeProfile: p,
                skillTreeReturnToProfile: p.id,
              });
            }}
          />
        ) : null}
        </AnimatePresence>
        {/* Blurred backdrop for the social overlay. Driven solely by
            mailboxOpen. ChatPanel's visibility is now gated on the
            same flag, so tying the backdrop to mailboxChatFriend too
            kept the blur stuck on after close (mailboxChatFriend
            is intentionally preserved so the chat can re-open with
            state intact). */}
        {this.state.loggedIn && this.state.arenaProfile && this.state.mailboxOpen ? (
          <motion.div
            className="fixed inset-0 z-[58]"
            data-screenshot-ignore="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            // Backdrop just hides the overlay. It does NOT clear
            // mailboxChatFriend or Mailbox's selected mail. Those
            // stay so the next reopen comes back to the same chat,
            // the same mail, the same tab. Performance: Mailbox and
            // ChatPanel stay mounted with pointerEvents: none when
            // hidden, so no state is thrown away.
            onClick={() => this.setState({ mailboxOpen: false })}
            style={{ background: 'rgba(0,0,0,0.28)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
          />
        ) : null}
        {this.state.draftCancelledModal ? (() => {
          const { wasSealed, reason } = this.state.draftCancelledModal;
          const heading = wasSealed ? t('Sealed match cancelled') : t('Draft cancelled');
          const body = reason === 'player_timeout'
            ? t('Your opponent disconnected and did not return in time.')
            : reason === 'all_players_disconnected'
              ? t('All players disconnected.')
              : reason === 'cancelled'
                ? t('The event was cancelled.')
                : t('The event was aborted before the match started.');
          return (
            <div className="fixed inset-0 z-[1400] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)' }}>
              <div
                className="relative w-[27.5cqw] p-6"
                style={{
                  background: 'linear-gradient(180deg, rgba(30,22,12,0.98) 0%, rgba(18,12,8,0.98) 100%)',
                  border: '1px solid rgba(180,140,60,0.35)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: '0 0 60px rgba(0,0,0,0.5), 0 0 30px rgba(180,140,60,0.08)',
                }}
              >
                <h2 className="arena-heading text-xl font-semibold mb-2" style={{ color: '#e8d5a0' }}>
                  {heading}
                </h2>
                <p className="text-sm mb-1" style={{ color: '#c5b98e' }}>
                  {body}
                </p>
                <p className="text-xs mb-5" style={{ color: '#9c9080' }}>
                  {t('Your entry cost has been refunded. No cards were added to your collection.')}
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-semibold rounded-lg cursor-pointer transition-transform hover:scale-[1.03]"
                    style={{
                      background: 'linear-gradient(180deg, rgba(212,168,67,0.95), rgba(158,118,46,0.95))',
                      color: '#1a1208',
                      border: '1px solid rgba(255,220,140,0.5)',
                    }}
                    onClick={() => this.setState({ draftCancelledModal: null })}
                  >
                    {t('Back to Hub')}
                  </button>
                </div>
              </div>
            </div>
          );
        })() : null}
        {this.state.abandonedMatchModal ? (
          <div className="fixed inset-0 z-[1400] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)' }}>
            <div
              className="relative w-[27.5cqw] p-6"
              style={{
                background: 'linear-gradient(180deg, rgba(30,22,12,0.98) 0%, rgba(18,12,8,0.98) 100%)',
                border: '1px solid rgba(180,140,60,0.35)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: '0 0 60px rgba(0,0,0,0.5), 0 0 30px rgba(180,140,60,0.08)',
              }}
            >
              <h2 className="arena-heading text-xl font-semibold mb-2" style={{ color: '#e8d5a0' }}>
                {t('Match abandoned')}
              </h2>
              <p className="text-sm mb-5" style={{ color: '#c5b98e' }}>
                {t("Both players disconnected and didn't return in time. No winner. No coins, XP, shards, or season progress changed.")}
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="px-5 py-2 text-sm font-semibold rounded-lg cursor-pointer transition-transform hover:scale-[1.03]"
                  style={{
                    background: 'linear-gradient(180deg, rgba(212,168,67,0.95), rgba(158,118,46,0.95))',
                    color: '#1a1208',
                    border: '1px solid rgba(255,220,140,0.5)',
                  }}
                  onClick={() => this.setState({ abandonedMatchModal: null })}
                >
                  {t('Back to Hub')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {/* Mini profile popover. Opened either by clicking the self
            avatar on the dock (target=self) or by hovering a friend
            row in the friends list (target=friend + profile data). */}
        {this.state.loggedIn && this.state.arenaProfile ? (
          <MiniProfile
            open={!!this.state.miniProfileTarget}
            anchor={this.state.miniProfileAnchor}
            isSelf={this.state.miniProfileTarget?.kind === 'self'}
            profile={
              this.state.miniProfileTarget?.kind === 'self'
                ? this.state.arenaProfile
                : this.state.miniProfileTarget?.profile
            }
            sorceryCards={this.state.sorceryCards}
            onClose={() => this.setState({ miniProfileTarget: null, miniProfileAnchor: null })}
            onEditProfile={() => this.handleOpenSettings?.()}
            onSavePersonality={this.updateArenaPersonality}
            onSavePresence={this.updateArenaPresence}
            onSaveDisplayedCrown={this.updateDisplayedCrown}
            onViewFullProfile={() => {
              const t = this.state.miniProfileTarget;
              const selfId = this.state.arenaProfile?.id;
              if (t?.kind === 'self' && selfId) {
                this.handleViewFriendProfile(selfId);
              } else if (t?.profile?.id) {
                this.handleViewFriendProfile(t.profile.id);
              }
              this.setState({ miniProfileTarget: null, miniProfileAnchor: null });
            }}
            onSendMessage={() => {
              const t = this.state.miniProfileTarget;
              if (!t || t.kind === 'self') return;
              this.setState({
                mailboxChatFriend: {
                  friendId: t.profile.id,
                  friendName: t.profile.name,
                  friendAvatar: t.profile.profileAvatar,
                  friendDisplayedCrownAvatarId: t.profile.displayedCrownAvatarId,
                  friendIsFounder: !!t.profile.isFounder,
                  friendIsQaTester: !!t.profile.isQaTester,
                  online: t.profile.online,
                },
                mailboxOpen: true,
                miniProfileTarget: null,
                miniProfileAnchor: null,
              });
            }}
          />
        ) : null}
        {/* Moments. Ephemeral social-feed panel docked on the left
            edge of the O dashboard. Tied to mailboxOpen so it appears
            together with the rest of the social overlay. */}
        {this.state.loggedIn && this.state.arenaProfile && !isWeb() ? (
          <MomentsPanel
            open={this.state.mailboxOpen}
            profile={this.state.arenaProfile}
            friendListData={this.state.friendListData}
          />
        ) : null}
        {/* App-level Mailbox. Rendered as a centered modal. Lives at
            app level so it's openable from any screen (including
            GameBoard), via the AppHeader friends icon or the "F"
            keyboard shortcut. */}
        {this.state.loggedIn && this.state.arenaProfile ? (
          <Mailbox
              onViewProfile={this.handleViewFriendProfile}
              open={this.state.mailboxOpen}
              onClose={() => { this.handleCloseMailbox(); this.setState({ mailboxChatFriend: null }); }}
              lastChatMessage={this.state.lastChatMessage}
              lastChatClaimed={this.state.lastChatClaimed}
              onProfileReload={this.reloadProfile}
              onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
              onRejoinMatch={this.handleRejoinMatch}
              onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
              profile={this.state.arenaProfile}
              friendListData={this.state.friendListData}
              sorceryCards={this.state.sorceryCards}
              onProfileUpdate={this.handleMailProfileUpdate}
              selectedMailId={this.state.mailboxSelectedMailId}
              initialView={this.state.mailboxView}
              composeRecipientId={this.state.mailboxComposeRecipientId}
              onOpenChat={(friend) => this.setState({ mailboxChatFriend: friend })}
              activeChatFriendId={this.state.mailboxChatFriend?.friendId}
              onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
              unreadByTab={this.mailboxUnreadTabs()}
              canModerateChat={this.canModerateChat()}
              onWatchReplay={this.handleWatchReplayFromMailbox}
              focusGlobalMessageId={this.state.focusGlobalMessageId}
              onTavernFocusDone={this.handleTavernFocusDone}
              focusNotifications={this.state.mailboxFocusNotifications}
            />
        ) : null}
        {/* Floating chat panel. Opens when the user clicks a friend in
            SocialPanel. Independent from Mailbox's open state so the
            user can close the friends list and keep chatting, or open
            the friends list back up without affecting an in-progress
            chat. Docks to the left of SocialPanel when both are open,
            slides to the right edge alone when SocialPanel is closed. */}
        {this.state.loggedIn && this.state.arenaProfile ? (
          <ChatPanel
            open={this.state.mailboxOpen && !!this.state.mailboxChatFriend}
            friend={this.state.mailboxChatFriend}
            socialOpen={this.state.mailboxOpen}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            lastChatMessage={this.state.lastChatMessage}
            onClose={() => this.setState({ mailboxChatFriend: null })}
            onViewProfile={this.handleViewFriendProfile}
            onProfileReload={this.reloadProfile}
            onAcceptMatchInvite={this.handleAcceptMatchInviteFromChat}
            onDeclineMatchInvite={this.handleDeclineMatchInviteFromChat}
            onAcceptDraftInvite={(eventId, opts) => this.openDraftLobby(eventId, opts)}
            onRejoinMatch={this.handleRejoinMatch}
            onProfileUpdate={this.handleMailProfileUpdate}
            matchHistory={this.state.arenaProfile?.matchHistory || []}
            onWatchReplay={(roomCode, opts) => {
              // Close the chat surfaces before the board mounts.
              this.setState({ mailboxOpen: false, mailboxChatFriend: null });
              this.handleWatchReplay(roomCode, opts);
            }}
            onOpenTavernMessage={this.handleOpenTavernMessage}
          />
        ) : null}
        {(this.state.updateStatus?.state === 'READY_TO_INSTALL' || this.state.versionRejection) ? (
          <UpdateModal
            newVersion={this.state.updateStatus?.newVersion}
            releaseNotes={this.state.updateStatus?.releaseNotes}
            updateState={this.state.updateStatus?.state}
            downloadProgress={this.state.updateStatus?.downloadProgress}
            currentVersion={this.state.updateStatus?.currentVersion}
            versionRejection={this.state.versionRejection}
            onApply={this.handleApplyUpdate}
            onRetry={() => this.updateManager?.retry?.()}
          />
        ) : null}
        <MaintenanceBanner />
        <ConnectionLostOverlay />
        <SessionSupersededOverlay />
        <Toaster />
        {isControllerEnabled() ? <VirtualKeyboard /> : null}
        {this.state.gameMenuOpen ? (
          <GameMenu
            onResume={this.handleGameMenuResume}
            onQuit={this.handleGameMenuQuit}
            onOpenSettings={this.handleOpenSettings}
            onMainMenu={(this.state.isGameBoardOpen || this.state.arenaView !== 'hub') ? this.handleGameMenuMainMenu : null}
            inSession={this.state.isGameBoardOpen && !!this.state.sessionMode}
            appVersion={this.state.updateStatus?.currentVersion}
          />
        ) : null}
        {this.state.showLfg ? (
          <LookingForMatchDialog
            profile={this.state.arenaProfile}
            prefs={(this.state.lfgState || lfg.getState()).prefs}
            active={!!(this.state.lfgState || lfg.getState()).active}
            error={(this.state.lfgState || lfg.getState()).error}
            sorceryCards={this.state.sorceryCards}
            onClose={() => this.setState({ showLfg: false })}
            onStart={(prefs) => { lfg.start(prefs); this.setState({ showLfg: false }); }}
            onStop={() => { lfg.stop(); this.setState({ showLfg: false }); }}
          />
        ) : null}
        {this.state.lfgMatch ? (
          <MatchFoundOverlay
            match={this.state.lfgMatch}
            profile={this.state.arenaProfile}
            sorceryCards={this.state.sorceryCards}
            onAccept={this.acceptLfgMatch}
            onDismiss={this.dismissLfgMatch}
          />
        ) : null}
        {this.state.activeReadyCheck ? (
          <ReadyCheckModal
            instance={this.state.activeReadyCheck.instance}
            alreadyReady={!!(this.state.activeReadyCheck.instance.participants || []).find((p) => p.userId === this.state.arenaProfile?.id)?.readyAt}
            onReady={async () => {
              const updated = await apiMarkReady(this.state.activeReadyCheck.instance.id);
              if (updated) this.reconcileInstance(updated);
            }}
            onClose={() => this.setState({ activeReadyCheck: null })}
          />
        ) : null}
        {this.state.pendingConfirm ? (
          <ConfirmModal
            title={this.state.pendingConfirm.title}
            message={this.state.pendingConfirm.message}
            confirmLabel={this.state.pendingConfirm.confirmLabel}
            cancelLabel={this.state.pendingConfirm.cancelLabel}
            danger={this.state.pendingConfirm.danger}
            onConfirm={this.state.pendingConfirm.onConfirm}
            onCancel={this.state.pendingConfirm.onCancel}
          />
        ) : null}
        {this.state.hubMatchResult ? (
          <ArenaMatchResult
            initialEnvelope={this.state.hubMatchResult.envelope}
            autoWinner={this.state.hubMatchResult.envelope.won ? 'me' : 'opponent'}
            matchDurationMinutes={this.state.hubMatchResult.envelope.durationMinutes || 0}
            getMatchLog={() => null}
            onMatchComplete={() => this.setState({ hubMatchResult: null })}
            onClose={() => this.setState({ hubMatchResult: null })}
          />
        ) : null}
        {this.state.draftMatchReadyCheck ? (
          <ReadyCheckModal
            // Synthesize an instance-shaped object so the modal's
            // "Your {title} starts as soon as every player confirms"
            // copy reads correctly. onReady/onUnready override the
            // default instance endpoints with the per-match ones.
            instance={{
              id: this.state.draftMatchReadyCheck.matchId,
              mode: 'match',
            }}
            onReady={async () => {
              const { confirmDraftMatchReady } = await import('./utils/arena/draftApi');
              await confirmDraftMatchReady(
                this.state.draftMatchReadyCheck.eventId,
                this.state.draftMatchReadyCheck.matchId,
              );
            }}
            onUnready={async () => {
              const { unreadyDraftMatch } = await import('./utils/arena/draftApi');
              await unreadyDraftMatch(
                this.state.draftMatchReadyCheck.eventId,
                this.state.draftMatchReadyCheck.matchId,
              ).catch(() => {});
            }}
            onClose={() => this.setState({ draftMatchReadyCheck: null })}
          />
        ) : null}
        {this.renderAuthOverlay()}
      </>
      </MotionConfig>
    );
  }
}
